/**
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-05-14.
 * Copyright © Softgames 2019 - present
 */

/**
 * @class
 *
 * @description General Game Backend Client error class
 *
 * @property {ERROR_CODE} code Error code
 * @property {mixed} [parentError] Error which caused this error
 *
 * @extends Error
 * @memberof module:error
 */
class GBCError extends Error {
  /**
   * @constructor
   * @param {ERROR_CODE} code Error code
   * @param {string} message Descriptive text of the error
   * @param {mixed} [parentError=] Error that was causing this error
   */
  constructor(code, message, parentError) {
    super(message);
    this.code = code;
    this.parentError = parentError;
    this.thisFileName = this.fileName;
    this.thisLineNumber = this.lineNumber;
    this.thisColumnNumber = this.columnNumber;
  }

  getParentErrorString() {
    let parentString = null;
    if (this.parentError) {
      parentString =
        typeof this.parentError.toString === 'function'
          ? this.parentError.toString()
          : 'Parent error does not have a toString function';
    }
    let stackString = null;
    if (this.stack) {
      stackString =
        typeof this.stack.toString === 'function'
          ? this.stack.toString()
          : 'Stack does not have a toString function';
    }
    if (parentString) {
      return ` with parent error:\n${parentString}`;
    }
    return stackString
      ? ` with stack:\n${stackString || 'No stack available'}`
      : '';
  }

  toString() {
    try {
      const code = this.code || 'No error code';
      const msg = this.message || 'No error message';
      const file = this.thisFileName;
      const line = this.thisLineNumber;
      const column = this.thisColumnNumber;
      const detailParts = [
        file ? `file: ${file}` : null,
        line ? `line: ${line}` : null,
        column ? `column: ${column}` : null,
      ].filter((e) => !!e);
      const details = detailParts.length
        ? ` [[${detailParts.join(' ')}]]]`
        : '';
      const parentPart = this.getParentErrorString();
      return `GbcError: [${code}] ${msg}${details}${parentPart}`;
    } catch (error) {
      return `Failed to execute toString on error due to error: ${error}`;
    }
  }

  get fileName() {
    return this.parentError
      ? this.parentError.fileName
      : this.thisFileName || 'unknown';
  }

  get lineNumber() {
    return this.parentError
      ? this.parentError.lineNumber
      : this.thisLineNumber || 'n/a';
  }

  get columnNumber() {
    return this.parentError
      ? this.parentError.columnNumber
      : this.thisColumnNumber || 'n/a';
  }
}

/**
 * @class
 *
 * @description Extended general Game Backend Client error class
 *
 * @extends GBCError
 * @memberof module:error
 */
class GBCIdError extends GBCError {
  /**
   * @constructor
   * @param {ERROR_CODE} code Error code
   * @param {number} id Unique id, set on error creation. Expected to be EPOCH
   * @param {string} message Descriptive text of the error
   * @param {mixed} [additionalInformation=] Additional information for this error
   * @param {mixed} [parentError=] Error that was causing this error
   */
  constructor(code, id, message, additionalInformation, parentError) {
    super(code, message, parentError);
    this.id = id;
    this.additionalInformation = additionalInformation;
  }

  toString() {
    try {
      const id = this.id || 'No error id';
      const code = this.code || 'No error code';
      const msg = this.message || 'No error message';
      const addInfo = this.additionalInformation || 'No error message';

      const parentPart = this.getParentErrorString();
      return `GbcIdError: [${code}][${id}] ${msg}${parentPart}${
        addInfo ? JSON.stringify(addInfo, null, '  ') : ''
      }`;
    } catch (error) {
      return `Failed to execute toString on error due to error: ${error}`;
    }
  }
}

/**
 * @constant ERROR_CODE
 * @type {Object}
 * @property {String} UNKNOWN unknown error
 * @property {String} INTERNAL_ERROR Internal server error
 * @property {String} UNKNOWN_GRAPHQL_ERROR Identify that request has been send to the game backend but response with unknown error.
 * @property {String} BAD_RESPONSE Identify that client got bad response from server. This can means that response from server has been changed.
 * @property {String} CONNECTION_NOT_INITIALIZED Identify problem with connection do the game backend.
 * @property {String} WRONG_CREDENTIALS Given credentials are invalid, register again to get new credentials
 * @property {String} INVALID_PARAMETER Identify that validation of the input parameters has failed. We should check error message for details.
 * @property {String} ENVIRONMENT_ERROR Identify problem with the environment.
 * @property {String} TIMEOUT Identify problem with a timeout.
 * @property {String} INVALID_CALL Identifies errors that are caused by an invalid time of calling a function or an unnecessary call. Check message for details.
 * @property {String} NOT_FOUND Identify problem with request to the game backend that requested resource cannot be found. For example when we send invalid leaderboard.
 * @property {String} NOT_SUPPORTED Identify problem with request to the game backend that a used functionality is not supported for the current environment.
 * @property {String} INVALID_CONFIG Identify problem with request to the game backend that a configuration for the game in GB is not how it needs to be for the function to work. Check the documentation and ADM for the configuration of your game.
 * @property {String} FORBIDDEN Identify problem with request to the game backend that action is forbidden. For example when post score is sent to leaderboard which is disabled for user.
 * @property {String} NOT_AUTHORIZED Identify problem with not authorized request to the game backend. For example when invalid authToken will be used for login method.
 * @property {String} PURCHASE_PROVIDER_INSUFFICIENT_BALANCE We got the response from the purchase provider's api that there was an insufficient balance
 * @property {String} PURCHASE_PROVIDER_INVALID_AMOUNT We got the response from the purchase provider's api that there was an invalid amount requested to be purchased
 * @property {String} PURCHASE_PROVIDER_UNKNOWN_ITEM We got the response from the purchase provider's api that there was an unknown item requested to be purchased
 * @property {String} PURCHASE_PROVIDER_INVALID_USER_INFORMATION We got the response from the purchase provider's api that there was invalid user information
 * @property {String} PURCHASE_PROVIDER_RECEIPT_ERROR We got the response from the purchase provider's api that there was a problem with the receipt generation
 * @property {String} PURCHASE_PROVIDER_OS_ERROR We got the response from the purchase provider's api that the provided OS was invalid
 * @property {String} BOT_SERVICE_ERROR Identify problem with response from bot service
 * @property {String} WEBSOCKET_ERROR Error caused by a Websocket error event
 * @property {String} REQUEST_TIMEOUT_ERROR Error caused by a timeout on the GB game server
 * @property {String} DB_TIMEOUT_ERROR Error caused by a the GB game server and a busy database
 *
 * @memberof module:enums
 */
const ERROR_CODE = {
  UNKNOWN: 'Unknown',
  INTERNAL_ERROR: 'InternalError',
  UNKNOWN_GRAPHQL_ERROR: 'UnknownGraphqlError',
  BAD_RESPONSE: 'BadResponse',
  CONNECTION_NOT_INITIALIZED: 'ConnectionNotInitialized',
  WRONG_CREDENTIALS: 'WrongCredentials',
  INVALID_PARAMETER: 'InvalidParameter',
  ENVIRONMENT_ERROR: 'EnvironmentError',
  TIMEOUT: 'timeout',
  INVALID_CALL: 'InvalidCall',
  NOT_FOUND: 'NotFound',
  NOT_SUPPORTED: 'NotSupported',
  INVALID_CONFIG: 'InvalidConfig',
  FORBIDDEN: 'Forbidden',
  NOT_AUTHORIZED: 'NotAuthorized',
  PURCHASE_PROVIDER_INSUFFICIENT_BALANCE: 'PurchaseProviderInsufficientBalance',
  PURCHASE_PROVIDER_INVALID_AMOUNT: 'PurchaseProviderInvalidAmount',
  PURCHASE_PROVIDER_UNKNOWN_ITEM: 'PurchaseProviderUnknownItem',
  PURCHASE_PROVIDER_INVALID_USER_INFORMATION:
    'PurchaseProviderInvalidUserInformation',
  PURCHASE_PROVIDER_RECEIPT_ERROR: 'PurchaseProviderReceiptError',
  PURCHASE_PROVIDER_OS_ERROR: 'PurchaseProviderOsError',
  PURCHASE_UNKNOWN_ERROR: 'PurchaseProviderUnknownError',
  PURCHASE_PROVIDER_CANCELED: 'PurchaseProviderCanceled',
  BOT_SERVICE_ERROR: 'BotServiceError',
  WEBSOCKET_ERROR: 'WebsocketError',
  REQUEST_TIMEOUT_ERROR: 'RequestTimeout',
  DB_TIMEOUT_ERROR: 'DbTimeout',
};

function isNonEmptyString$3(str) {
  return !!str && typeof str === 'string' && !!str.trim();
}

/**
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2020-04-27.
 * Copyright © Softgames 2020 - present
 */

/**
 * @class
 *
 * @description Bad response from server
 *
 * @extends GBCError
 * @memberof module:error
 */
class GBCBadResponseError extends GBCError {
  /**
   * @constructor
   * @param  {string} message Descriptive text of the error
   */
  constructor(message) {
    const msg = isNonEmptyString$3(message) ? message : 'Bad response error';
    super(ERROR_CODE.BAD_RESPONSE, msg);
  }
}

/**
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-06-24.
 * Copyright © Softgames 2019 - present
 */

/**
 * @class
 *
 * @description Bot service error. When is problem with bot service.
 *
 * @extends GBCError
 * @memberof module:error
 */
class GBCBotServiceError extends GBCError {
  /**
   * @constructor
   * @param {string} message Descriptive text of the error
   */
  constructor(message) {
    const msg = isNonEmptyString$3(message) ? message : 'Bot service error';
    super(ERROR_CODE.BOT_SERVICE_ERROR, msg);
  }
}

/**
 * @class
 *
 * @description Extended general Game Backend Client error class without stack trace
 *
 * @extends GBCIdError
 * @memberof module:error
 */
class GBCStacklessError extends GBCIdError {
  /**
   * @constructor
   * @param {ERROR_CODE} code Error code
   * @param {number} id Unique id, set on error creation. Expected to be EPOCH
   * @param {string} message Descriptive text of the error
   * @param {mixed} [additionalInformation=] Additional information for this error
   * @param {mixed} [parentError=] Error that was causing this error
   */
  constructor(code, id, message, additionalInformation, parentError) {
    super(code, id, message, additionalInformation, parentError);
    this.stack = null;
  }
}

/**
 * @class
 *
 * @description  Identifies errors that are caused by a timeout. Check message for details.
 *
 * @extends GBCStacklessError
 * @memberof module:error
 */
class GBCTimeoutError extends GBCStacklessError {}

/**
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-07-04.
 * Copyright © Softgames 2019 - present
 */

/**
 * @constant PROVIDER
 * @type {Object}
 * @property {String} FACEBOOK_INSTANT_GAME Facebook instant game
 * @property {String} ODNOKLASSNIKI Odnoklassniki
 * @property {String} TWITCH Twitch
 * @property {String} LINE Line
 *
 * @memberof module:enums
 */
const PROVIDER = {
  FACEBOOK_INSTANT_GAME: 'fbig',
  ODNOKLASSNIKI: 'ok',
  TWITCH: 'twitch',
  LINE: 'line',
};

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-05-27.
 * Copyright © Softgames 2019 - present
 */

/**
 * @constant ENVIRONMENT
 *
 * @description
 * Only used for LINE
 *
 *
 * @type {Object}
 * @property {String} SANDBOX
 * @property {String} STAGING
 * @property {String} PRODUCTION
 *
 * @memberof module:enums
 */
const ENVIRONMENT = {
  SANDBOX: 'SANDBOX',
  STAGING: 'STAGING',
  PRODUCTION: 'PRODUCTION',
};

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-05-27.
 * Copyright © Softgames 2019 - present
 */

/**
 * @constant OS
 *
 * @description
 * Only used for LINE
 *
 * @type {Object}
 * @property {String} IOS Used for Apple's iOS
 * @property {String} AOS Used for Android
 *
 * @memberof module:enums
 */
const OS = {
  IOS: 'IOS',
  AOS: 'AOS',
};

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-05-27.
 * Copyright © Softgames 2019 - present
 */

/**
 * @constant SEVERITY
 *
 * @description
 * Possible Severities for logging
 *
 * @type {Object}
 * @property {String} DEBUG
 * @property {String} INFO
 * @property {String} NOTICE
 * @property {String} WARN
 * @property {String} ERROR
 * @property {String} FATAL
 * @property {String} DISABLE
 *
 * @memberof module:enums
 */
const SEVERITY$3 = {
  DEBUG: -1,
  INFO: 0,
  NOTICE: 1,
  WARN: 2,
  ERROR: 3,
  FATAL: 4,
  DISABLE: 5,
};

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-01.
 * Created by Daniel Pochert <daniel.pochert@softgames.de> on 2019-04-01.
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-04-01.
 * Created by Benhur Davies <benhur.davies@softgames.de> on 2019-04-01.
 * Copyright © Softgames 2019 - present
 */

var enums = /*#__PURE__*/Object.freeze({
  __proto__: null,
  PROVIDER: PROVIDER,
  ENVIRONMENT: ENVIRONMENT,
  ERROR_CODE: ERROR_CODE,
  OS: OS,
  SEVERITY: SEVERITY$3
});

/**
 * @class
 *
 * @description  Identifies errors that are caused by a timeout on the client.
 *
 * @extends GBCTimeoutError
 * @memberof module:error
 */
class GBCClientTimeoutError extends GBCTimeoutError {
  /**
   * @constructor
   * @param {number} id Unique id, set on error creation. Expected to be EPOCH
   * @param {string} message Descriptive text of the error
   */
  constructor(id, message) {
    super(ERROR_CODE.CLIENT_TIMEOUT_ERROR, id, message);
  }
}

/**
 * Created by René Simon<rene.simon@softgames.de> on 2020-02-12.
 * Copyright © Softgames 2020 - present
 */

/**
 * @class
 *
 * @description  Required configuration not done on server side
 *
 * @extends GBCError
 * @memberof module:error
 */
class GBCConfigurationError extends GBCError {
  /**
   * @constructor
   * @param {string} message Descriptive text of the error
   * @param {mixed} [parentError=] Error that was causing this error
   */
  constructor(message, parentError) {
    super(ERROR_CODE.INVALID_CONFIG, message, parentError);
  }
}

/**
 * @class
 *
 * @description  Identifies errors that are caused by a timeout on the server to the DB.
 *
 * @extends GBCTimeoutError
 * @memberof module:error
 */
class GBCDbTimeoutError extends GBCTimeoutError {
  /**
   * @constructor
   * @param {number} id Unique id, set on error creation. Expected to be EPOCH
   * @param {string} message Descriptive text of the error
   */
  constructor(id, message) {
    super(ERROR_CODE.DB_TIMEOUT_ERROR, id, message);
  }
}

/**
 * @class
 *
 * @description  Identifies errors that are caused by the environment / device / device configuration
 *
 * @extends GBCStacklessError
 * @memberof module:error
 */
class GBCEnvironmentError extends GBCStacklessError {
  /**
   * @constructor
   * @param {number} id Unique id, set on error creation. Expected to be EPOCH
   * @param {string} message Descriptive text of the error
   * @param {mixed} [additionalInformation=] Additional information for this error
   * @param {mixed} [parentError=] Error that was causing this error
   */
  constructor(id, message, additionalInformation, parentError) {
    super(
      ERROR_CODE.ENVIRONMENT_ERROR,
      id,
      message,
      additionalInformation,
      parentError,
    );
  }
}

/**
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-05-14.
 * Copyright © Softgames 2019 - present
 */

/**
 * @class
 *
 * @description No connection error class. When is problem with connection to the game backend.
 *
 * @extends GBCError
 * @memberof module:error
 */
class GBCErrorNoConnection extends GBCError {
  /**
   * @constructor
   * @param  {string} message Descriptive text of the error
   */
  constructor(message) {
    const msg = isNonEmptyString$3(message)
      ? message
      : 'Connection not established';
    super(ERROR_CODE.CONNECTION_NOT_INITIALIZED, msg);
  }
}

/**
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-05-14.
 * Copyright © Softgames 2019 - present
 */

/**
 * @class
 *
 * @description  Validation error class. When function parameters validation will fail.
 *
 * @property {mixed} validExample A valid example for the property that is invalid
 *
 * @extends GBCError
 * @memberof module:error
 */
class GBCErrorValidationFailed extends GBCError {
  /**
   * @constructor
   * @param {string} message Descriptive text of the error
   * @param {mixed} validExample A valid example for the property that is invalid
   */
  constructor(message, validExample) {
    super(ERROR_CODE.INVALID_PARAMETER, message);
    this.validExample = validExample;
  }

  /**
   * @description
   * Returns the valid example given on construction
   *
   * @return {mixed}
   */
  getValidExample() {
    return this.validExample;
  }
}

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-10-30.
 * Copyright © Softgames 2019 - present
 */

/**
 * @class
 *
 * @description Internal error
 *
 * @extends GBCError
 * @memberof module:error
 */
class GBCInternalError extends GBCError {
  /**
   * @constructor
   * @param  {string} message Descriptive text of the error
   */
  constructor(message) {
    const msg = isNonEmptyString$3(message) ? message : 'Some internal error';
    super(ERROR_CODE.INTERNAL_ERROR, msg);
  }
}

/**
 * Created by René Simon<rene.simon@softgames.de> on 2019-07-19.
 * Copyright © Softgames 2019 - present
 */

/**
 * @class
 *
 * @description  Identifies errors that are caused by an invalid time of calling a function or an unnecessary call. Check message for details.
 *
 * @extends GBCError
 * @memberof module:error
 */
class GBCInvalidCallError extends GBCError {
  /**
   * @constructor
   * @param {string} message Descriptive text of the error
   * @param {mixed} [parentError=] Error that was causing this error
   */
  constructor(message, parentError) {
    super(ERROR_CODE.INVALID_CALL, message, parentError);
  }
}

/**
 * @class
 *
 * @description  General GraphQL error class.
 *
 * @property {number} messageId Id of the message send/received to/from Game Backend
 *
 * @extends GBCStacklessError
 * @memberof module:error
 */
class GBCGraphQLError extends GBCStacklessError {
  /**
   * @constructor
   * @param {ERROR_CODE} code Error code
   * @param {number} id Unique id, set on error creation. Expected to be EPOCH
   * @param {number} messageId Id of the message send/received to/from Game Backend
   * @param {string} message Descriptive text of the error
   * @param {mixed} [additionalInformation=] Additional information for this error
   * @param {mixed} [parentError=] Error that was causing this error
   */
  constructor(
    code,
    id,
    messageId,
    message,
    additionalInformation,
    parentError,
  ) {
    super(code, id, message, additionalInformation, parentError);
    this.messageId = messageId;
  }

  /**
   * @description
   * Returns the message ID provided on construction
   *
   * @return {number}
   */
  getMessageId() {
    return this.messageId;
  }
}

/**
 * @class
 *
 * @description
 * Known GraphQL error class. When graphQL request will fail and we have defined error code.
 *
 * @extends GBCGraphQLError
 * @memberof module:error
 */
class GBCKnownGraphQLError extends GBCGraphQLError {}

/**
 * Created by René Simon<rene.simon@softgames.de> on 2019-07-19.
 * Copyright © Softgames 2019 - present
 */

/**
 * @class
 *
 * @description  Identify problem with not authorized request to the game backend. For example when invalid authToken will be used for login method.
 *
 * @extends GBCError
 * @memberof module:error
 */
class GBCNotAuthorizedError extends GBCError {
  /**
   * @constructor
   * @param {string} message Descriptive text of the error
   * @param {mixed} [parentError=] Error that was causing this error
   */
  constructor(message, parentError) {
    super(ERROR_CODE.NOT_AUTHORIZED, message, parentError);
  }
}

/**
 * Created by René Simon<rene.simon@softgames.de> on 2020-02-12.
 * Copyright © Softgames 2020 - present
 */

/**
 * @class
 *
 * @description  Something went wrong in the payment process
 *
 * @property {boolean} canceled - true if the payment process was canceled by user
 * @property {boolean} insufficientBalance - true if the the user did not have enough money
 *
 * @extends GBCError
 * @memberof module:error
 */
class GBCPaymentError extends GBCError {
  /**
   * @constructor
   * @param {ERROR_CODE} code Error code
   * @param {string} message Descriptive text of the error
   * @param {mixed} [parentError=] Error that was causing this error
   */
  constructor(code, message, parentError) {
    super(code, message, parentError);
    this.canceled = code === ERROR_CODE.PURCHASE_PROVIDER_CANCELED;
    this.insufficientBalance =
      code === ERROR_CODE.PURCHASE_PROVIDER_INSUFFICIENT_BALANCE;
  }
}

/**
 * @class
 *
 * @description  Identifies errors that are caused by a timeout on the server.
 *
 * @extends GBCTimeoutError
 * @memberof module:error
 */
class GBCRequestTimeoutError extends GBCTimeoutError {
  /**
   * @constructor
   * @param {number} id Unique id, set on error creation. Expected to be EPOCH
   * @param {string} message Descriptive text of the error
   */
  constructor(id, message) {
    super(ERROR_CODE.REQUEST_TIMEOUT_ERROR, id, message);
  }
}

/**
 * Created by René Simon<rene.simon@softgames.de> on 2019-07-19.
 * Copyright © Softgames 2019 - present
 */

/**
 * @class
 *
 * @description  Unknown error
 *
 * @extends GBCError
 * @memberof module:error
 */
class GBCUnknownError extends GBCError {
  /**
   * @constructor
   * @param {string} message Descriptive text of the error
   * @param {mixed} [parentError=] Error that was causing this error
   */
  constructor(message, parentError) {
    super(ERROR_CODE.UNKNOWN, message, parentError);
  }
}

/**
 * @class
 *
 * @description
 * Unknown GraphQL error class. When graphQL request will fail and we don't have defined error code.
 *
 * @extends GBCGraphQLError
 * @memberof module:error
 */
class GBCUnknownGraphQLError extends GBCGraphQLError {
  /**
   * @constructor
   * @param {number} id Unique id, set on error creation. Expected to be EPOCH
   * @param {number} messageId Id of the message send/received to/from Game Backend
   * @param {string} message Descriptive text of the error
   * @param {mixed} [additionalInformation=] Additional information for this error
   * @param {mixed} [parentError=] Error that was causing this error
   */
  constructor(id, messageId, message, additionalInformation, parentError) {
    super(
      ERROR_CODE.UNKNOWN_GRAPHQL_ERROR,
      id,
      messageId,
      message,
      additionalInformation,
      parentError,
    );
  }
}

/**
 * Created by René Simon<rene.simon@softgames.de> on 2019-07-19.
 * Copyright © Softgames 2019 - present
 */

/**
 * @class
 *
 * @description  Error caused by a Websocket error event
 *
 * @extends GBCError
 * @memberof module:error
 */
class GBCWebsocketError extends GBCError {
  /**
   * @constructor
   * @param {string} message Descriptive text of the error
   * @param {mixed} errorEvent Error event that was causing this error
   */
  constructor(message, errorEvent) {
    super(ERROR_CODE.WEBSOCKET_ERROR, message);
    this.errorEvent = errorEvent;
  }
}

/**
 * Created by René Simon<rene.simon@softgames.de> on 2019-07-19.
 * Copyright © Softgames 2019 - present
 */

/**
 * @class
 *
 * @description  Given credentials are invalid, register again to get new credentials
 *
 * @extends GBCError
 * @memberof module:error
 */
class GBCWrongCredentialsError extends GBCError {
  /**
   * @constructor
   * @param {string} message Descriptive text of the error
   * @param {mixed} [parentError=] Error that was causing this error
   */
  constructor(message, parentError) {
    super(ERROR_CODE.WRONG_CREDENTIALS, message, parentError);
  }
}

var errors = /*#__PURE__*/Object.freeze({
  __proto__: null,
  GBCError: GBCError,
  GBCIdError: GBCIdError,
  GBCBadResponseError: GBCBadResponseError,
  GBCBotServiceError: GBCBotServiceError,
  GBCClientTimeoutError: GBCClientTimeoutError,
  GBCConfigurationError: GBCConfigurationError,
  GBCDbTimeoutError: GBCDbTimeoutError,
  GBCEnvironmentError: GBCEnvironmentError,
  GBCErrorNoConnection: GBCErrorNoConnection,
  GBCErrorValidationFailed: GBCErrorValidationFailed,
  GBCInternalError: GBCInternalError,
  GBCInvalidCallError: GBCInvalidCallError,
  GBCKnownGraphQLError: GBCKnownGraphQLError,
  GBCNotAuthorizedError: GBCNotAuthorizedError,
  GBCPaymentError: GBCPaymentError,
  GBCRequestTimeoutError: GBCRequestTimeoutError,
  GBCUnknownError: GBCUnknownError,
  GBCUnknownGraphQLError: GBCUnknownGraphQLError,
  GBCWebsocketError: GBCWebsocketError,
  GBCWrongCredentialsError: GBCWrongCredentialsError
});

function getError(error, name) {
  if (error instanceof GBCIdError) {
    // eslint-disable-next-line no-param-reassign
    error.additionalInformation = {
      ...error.additionalInformation,
      method: name,
    };
    return error;
  }
  return new GBCIdError(
    error.code || ERROR_CODE.INTERNAL_ERROR,
    1642695433,
    error.message || `Unknown error received from ${name} method`,
    { method: name },
    error,
  );
}

function createSyncFunc(func, name) {
  return (...arg) => {
    try {
      return func(...arg);
    } catch (error) {
      throw getError(error, name);
    }
  };
}

function createAsyncFunc(func, name) {
  return async (...arg) => {
    try {
      return await func(...arg);
    } catch (error) {
      throw getError(error, name);
    }
  };
}

function wrapMethod(name, func, sync = false) {
  if (sync) return createSyncFunc(func, name);
  return createAsyncFunc(func, name);
}

// Unique ID creation requires a high quality random # generator. In the browser we therefore
// require the crypto API and do not support built-in fallback to lower quality random number
// generators (like Math.random()).
var getRandomValues;
var rnds8 = new Uint8Array(16);
function rng() {
  // lazy load so that environments that need to polyfill have a chance to do so
  if (!getRandomValues) {
    // getRandomValues needs to be invoked in a context where "this" is a Crypto implementation. Also,
    // find the complete implementation of crypto (msCrypto) on IE11.
    getRandomValues = typeof crypto !== 'undefined' && crypto.getRandomValues && crypto.getRandomValues.bind(crypto) || typeof msCrypto !== 'undefined' && typeof msCrypto.getRandomValues === 'function' && msCrypto.getRandomValues.bind(msCrypto);

    if (!getRandomValues) {
      throw new Error('crypto.getRandomValues() not supported. See https://github.com/uuidjs/uuid#getrandomvalues-not-supported');
    }
  }

  return getRandomValues(rnds8);
}

var REGEX = /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}|00000000-0000-0000-0000-000000000000)$/i;

function validate(uuid) {
  return typeof uuid === 'string' && REGEX.test(uuid);
}

/**
 * Convert array of 16 byte values to UUID string format of the form:
 * XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX
 */

var byteToHex = [];

for (var i$1 = 0; i$1 < 256; ++i$1) {
  byteToHex.push((i$1 + 0x100).toString(16).substr(1));
}

function stringify(arr) {
  var offset = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  // Note: Be careful editing this code!  It's been tuned for performance
  // and works in ways you may not expect. See https://github.com/uuidjs/uuid/pull/434
  var uuid = (byteToHex[arr[offset + 0]] + byteToHex[arr[offset + 1]] + byteToHex[arr[offset + 2]] + byteToHex[arr[offset + 3]] + '-' + byteToHex[arr[offset + 4]] + byteToHex[arr[offset + 5]] + '-' + byteToHex[arr[offset + 6]] + byteToHex[arr[offset + 7]] + '-' + byteToHex[arr[offset + 8]] + byteToHex[arr[offset + 9]] + '-' + byteToHex[arr[offset + 10]] + byteToHex[arr[offset + 11]] + byteToHex[arr[offset + 12]] + byteToHex[arr[offset + 13]] + byteToHex[arr[offset + 14]] + byteToHex[arr[offset + 15]]).toLowerCase(); // Consistency check for valid UUID.  If this throws, it's likely due to one
  // of the following:
  // - One or more input array values don't map to a hex octet (leading to
  // "undefined" in the uuid)
  // - Invalid input values for the RFC `version` or `variant` fields

  if (!validate(uuid)) {
    throw TypeError('Stringified UUID is invalid');
  }

  return uuid;
}

function parse$1(uuid) {
  if (!validate(uuid)) {
    throw TypeError('Invalid UUID');
  }

  var v;
  var arr = new Uint8Array(16); // Parse ########-....-....-....-............

  arr[0] = (v = parseInt(uuid.slice(0, 8), 16)) >>> 24;
  arr[1] = v >>> 16 & 0xff;
  arr[2] = v >>> 8 & 0xff;
  arr[3] = v & 0xff; // Parse ........-####-....-....-............

  arr[4] = (v = parseInt(uuid.slice(9, 13), 16)) >>> 8;
  arr[5] = v & 0xff; // Parse ........-....-####-....-............

  arr[6] = (v = parseInt(uuid.slice(14, 18), 16)) >>> 8;
  arr[7] = v & 0xff; // Parse ........-....-....-####-............

  arr[8] = (v = parseInt(uuid.slice(19, 23), 16)) >>> 8;
  arr[9] = v & 0xff; // Parse ........-....-....-....-############
  // (Use "/" to avoid 32-bit truncation when bit-shifting high-order bytes)

  arr[10] = (v = parseInt(uuid.slice(24, 36), 16)) / 0x10000000000 & 0xff;
  arr[11] = v / 0x100000000 & 0xff;
  arr[12] = v >>> 24 & 0xff;
  arr[13] = v >>> 16 & 0xff;
  arr[14] = v >>> 8 & 0xff;
  arr[15] = v & 0xff;
  return arr;
}

function stringToBytes(str) {
  str = unescape(encodeURIComponent(str)); // UTF8 escape

  var bytes = [];

  for (var i = 0; i < str.length; ++i) {
    bytes.push(str.charCodeAt(i));
  }

  return bytes;
}

var DNS = '6ba7b810-9dad-11d1-80b4-00c04fd430c8';
var URL = '6ba7b811-9dad-11d1-80b4-00c04fd430c8';
function v35 (name, version, hashfunc) {
  function generateUUID(value, namespace, buf, offset) {
    if (typeof value === 'string') {
      value = stringToBytes(value);
    }

    if (typeof namespace === 'string') {
      namespace = parse$1(namespace);
    }

    if (namespace.length !== 16) {
      throw TypeError('Namespace must be array-like (16 iterable integer values, 0-255)');
    } // Compute hash of namespace and value, Per 4.3
    // Future: Use spread syntax when supported on all platforms, e.g. `bytes =
    // hashfunc([...namespace, ... value])`


    var bytes = new Uint8Array(16 + value.length);
    bytes.set(namespace);
    bytes.set(value, namespace.length);
    bytes = hashfunc(bytes);
    bytes[6] = bytes[6] & 0x0f | version;
    bytes[8] = bytes[8] & 0x3f | 0x80;

    if (buf) {
      offset = offset || 0;

      for (var i = 0; i < 16; ++i) {
        buf[offset + i] = bytes[i];
      }

      return buf;
    }

    return stringify(bytes);
  } // Function#name is not settable on some platforms (#270)


  try {
    generateUUID.name = name; // eslint-disable-next-line no-empty
  } catch (err) {} // For CommonJS default export support


  generateUUID.DNS = DNS;
  generateUUID.URL = URL;
  return generateUUID;
}

/*
 * Browser-compatible JavaScript MD5
 *
 * Modification of JavaScript MD5
 * https://github.com/blueimp/JavaScript-MD5
 *
 * Copyright 2011, Sebastian Tschan
 * https://blueimp.net
 *
 * Licensed under the MIT license:
 * https://opensource.org/licenses/MIT
 *
 * Based on
 * A JavaScript implementation of the RSA Data Security, Inc. MD5 Message
 * Digest Algorithm, as defined in RFC 1321.
 * Version 2.2 Copyright (C) Paul Johnston 1999 - 2009
 * Other contributors: Greg Holt, Andrew Kepert, Ydnar, Lostinet
 * Distributed under the BSD License
 * See http://pajhome.org.uk/crypt/md5 for more info.
 */
function md5(bytes) {
  if (typeof bytes === 'string') {
    var msg = unescape(encodeURIComponent(bytes)); // UTF8 escape

    bytes = new Uint8Array(msg.length);

    for (var i = 0; i < msg.length; ++i) {
      bytes[i] = msg.charCodeAt(i);
    }
  }

  return md5ToHexEncodedArray(wordsToMd5(bytesToWords(bytes), bytes.length * 8));
}
/*
 * Convert an array of little-endian words to an array of bytes
 */


function md5ToHexEncodedArray(input) {
  var output = [];
  var length32 = input.length * 32;
  var hexTab = '0123456789abcdef';

  for (var i = 0; i < length32; i += 8) {
    var x = input[i >> 5] >>> i % 32 & 0xff;
    var hex = parseInt(hexTab.charAt(x >>> 4 & 0x0f) + hexTab.charAt(x & 0x0f), 16);
    output.push(hex);
  }

  return output;
}
/**
 * Calculate output length with padding and bit length
 */


function getOutputLength(inputLength8) {
  return (inputLength8 + 64 >>> 9 << 4) + 14 + 1;
}
/*
 * Calculate the MD5 of an array of little-endian words, and a bit length.
 */


function wordsToMd5(x, len) {
  /* append padding */
  x[len >> 5] |= 0x80 << len % 32;
  x[getOutputLength(len) - 1] = len;
  var a = 1732584193;
  var b = -271733879;
  var c = -1732584194;
  var d = 271733878;

  for (var i = 0; i < x.length; i += 16) {
    var olda = a;
    var oldb = b;
    var oldc = c;
    var oldd = d;
    a = md5ff(a, b, c, d, x[i], 7, -680876936);
    d = md5ff(d, a, b, c, x[i + 1], 12, -389564586);
    c = md5ff(c, d, a, b, x[i + 2], 17, 606105819);
    b = md5ff(b, c, d, a, x[i + 3], 22, -1044525330);
    a = md5ff(a, b, c, d, x[i + 4], 7, -176418897);
    d = md5ff(d, a, b, c, x[i + 5], 12, 1200080426);
    c = md5ff(c, d, a, b, x[i + 6], 17, -1473231341);
    b = md5ff(b, c, d, a, x[i + 7], 22, -45705983);
    a = md5ff(a, b, c, d, x[i + 8], 7, 1770035416);
    d = md5ff(d, a, b, c, x[i + 9], 12, -1958414417);
    c = md5ff(c, d, a, b, x[i + 10], 17, -42063);
    b = md5ff(b, c, d, a, x[i + 11], 22, -1990404162);
    a = md5ff(a, b, c, d, x[i + 12], 7, 1804603682);
    d = md5ff(d, a, b, c, x[i + 13], 12, -40341101);
    c = md5ff(c, d, a, b, x[i + 14], 17, -1502002290);
    b = md5ff(b, c, d, a, x[i + 15], 22, 1236535329);
    a = md5gg(a, b, c, d, x[i + 1], 5, -165796510);
    d = md5gg(d, a, b, c, x[i + 6], 9, -1069501632);
    c = md5gg(c, d, a, b, x[i + 11], 14, 643717713);
    b = md5gg(b, c, d, a, x[i], 20, -373897302);
    a = md5gg(a, b, c, d, x[i + 5], 5, -701558691);
    d = md5gg(d, a, b, c, x[i + 10], 9, 38016083);
    c = md5gg(c, d, a, b, x[i + 15], 14, -660478335);
    b = md5gg(b, c, d, a, x[i + 4], 20, -405537848);
    a = md5gg(a, b, c, d, x[i + 9], 5, 568446438);
    d = md5gg(d, a, b, c, x[i + 14], 9, -1019803690);
    c = md5gg(c, d, a, b, x[i + 3], 14, -187363961);
    b = md5gg(b, c, d, a, x[i + 8], 20, 1163531501);
    a = md5gg(a, b, c, d, x[i + 13], 5, -1444681467);
    d = md5gg(d, a, b, c, x[i + 2], 9, -51403784);
    c = md5gg(c, d, a, b, x[i + 7], 14, 1735328473);
    b = md5gg(b, c, d, a, x[i + 12], 20, -1926607734);
    a = md5hh(a, b, c, d, x[i + 5], 4, -378558);
    d = md5hh(d, a, b, c, x[i + 8], 11, -2022574463);
    c = md5hh(c, d, a, b, x[i + 11], 16, 1839030562);
    b = md5hh(b, c, d, a, x[i + 14], 23, -35309556);
    a = md5hh(a, b, c, d, x[i + 1], 4, -1530992060);
    d = md5hh(d, a, b, c, x[i + 4], 11, 1272893353);
    c = md5hh(c, d, a, b, x[i + 7], 16, -155497632);
    b = md5hh(b, c, d, a, x[i + 10], 23, -1094730640);
    a = md5hh(a, b, c, d, x[i + 13], 4, 681279174);
    d = md5hh(d, a, b, c, x[i], 11, -358537222);
    c = md5hh(c, d, a, b, x[i + 3], 16, -722521979);
    b = md5hh(b, c, d, a, x[i + 6], 23, 76029189);
    a = md5hh(a, b, c, d, x[i + 9], 4, -640364487);
    d = md5hh(d, a, b, c, x[i + 12], 11, -421815835);
    c = md5hh(c, d, a, b, x[i + 15], 16, 530742520);
    b = md5hh(b, c, d, a, x[i + 2], 23, -995338651);
    a = md5ii(a, b, c, d, x[i], 6, -198630844);
    d = md5ii(d, a, b, c, x[i + 7], 10, 1126891415);
    c = md5ii(c, d, a, b, x[i + 14], 15, -1416354905);
    b = md5ii(b, c, d, a, x[i + 5], 21, -57434055);
    a = md5ii(a, b, c, d, x[i + 12], 6, 1700485571);
    d = md5ii(d, a, b, c, x[i + 3], 10, -1894986606);
    c = md5ii(c, d, a, b, x[i + 10], 15, -1051523);
    b = md5ii(b, c, d, a, x[i + 1], 21, -2054922799);
    a = md5ii(a, b, c, d, x[i + 8], 6, 1873313359);
    d = md5ii(d, a, b, c, x[i + 15], 10, -30611744);
    c = md5ii(c, d, a, b, x[i + 6], 15, -1560198380);
    b = md5ii(b, c, d, a, x[i + 13], 21, 1309151649);
    a = md5ii(a, b, c, d, x[i + 4], 6, -145523070);
    d = md5ii(d, a, b, c, x[i + 11], 10, -1120210379);
    c = md5ii(c, d, a, b, x[i + 2], 15, 718787259);
    b = md5ii(b, c, d, a, x[i + 9], 21, -343485551);
    a = safeAdd(a, olda);
    b = safeAdd(b, oldb);
    c = safeAdd(c, oldc);
    d = safeAdd(d, oldd);
  }

  return [a, b, c, d];
}
/*
 * Convert an array bytes to an array of little-endian words
 * Characters >255 have their high-byte silently ignored.
 */


function bytesToWords(input) {
  if (input.length === 0) {
    return [];
  }

  var length8 = input.length * 8;
  var output = new Uint32Array(getOutputLength(length8));

  for (var i = 0; i < length8; i += 8) {
    output[i >> 5] |= (input[i / 8] & 0xff) << i % 32;
  }

  return output;
}
/*
 * Add integers, wrapping at 2^32. This uses 16-bit operations internally
 * to work around bugs in some JS interpreters.
 */


function safeAdd(x, y) {
  var lsw = (x & 0xffff) + (y & 0xffff);
  var msw = (x >> 16) + (y >> 16) + (lsw >> 16);
  return msw << 16 | lsw & 0xffff;
}
/*
 * Bitwise rotate a 32-bit number to the left.
 */


function bitRotateLeft(num, cnt) {
  return num << cnt | num >>> 32 - cnt;
}
/*
 * These functions implement the four basic operations the algorithm uses.
 */


function md5cmn(q, a, b, x, s, t) {
  return safeAdd(bitRotateLeft(safeAdd(safeAdd(a, q), safeAdd(x, t)), s), b);
}

function md5ff(a, b, c, d, x, s, t) {
  return md5cmn(b & c | ~b & d, a, b, x, s, t);
}

function md5gg(a, b, c, d, x, s, t) {
  return md5cmn(b & d | c & ~d, a, b, x, s, t);
}

function md5hh(a, b, c, d, x, s, t) {
  return md5cmn(b ^ c ^ d, a, b, x, s, t);
}

function md5ii(a, b, c, d, x, s, t) {
  return md5cmn(c ^ (b | ~d), a, b, x, s, t);
}

v35('v3', 0x30, md5);

function v4(options, buf, offset) {
  options = options || {};
  var rnds = options.random || (options.rng || rng)(); // Per 4.4, set bits for version and `clock_seq_hi_and_reserved`

  rnds[6] = rnds[6] & 0x0f | 0x40;
  rnds[8] = rnds[8] & 0x3f | 0x80; // Copy bytes to buffer, if provided

  if (buf) {
    offset = offset || 0;

    for (var i = 0; i < 16; ++i) {
      buf[offset + i] = rnds[i];
    }

    return buf;
  }

  return stringify(rnds);
}

// Adapted from Chris Veness' SHA1 code at
// http://www.movable-type.co.uk/scripts/sha1.html
function f(s, x, y, z) {
  switch (s) {
    case 0:
      return x & y ^ ~x & z;

    case 1:
      return x ^ y ^ z;

    case 2:
      return x & y ^ x & z ^ y & z;

    case 3:
      return x ^ y ^ z;
  }
}

function ROTL(x, n) {
  return x << n | x >>> 32 - n;
}

function sha1(bytes) {
  var K = [0x5a827999, 0x6ed9eba1, 0x8f1bbcdc, 0xca62c1d6];
  var H = [0x67452301, 0xefcdab89, 0x98badcfe, 0x10325476, 0xc3d2e1f0];

  if (typeof bytes === 'string') {
    var msg = unescape(encodeURIComponent(bytes)); // UTF8 escape

    bytes = [];

    for (var i = 0; i < msg.length; ++i) {
      bytes.push(msg.charCodeAt(i));
    }
  } else if (!Array.isArray(bytes)) {
    // Convert Array-like to Array
    bytes = Array.prototype.slice.call(bytes);
  }

  bytes.push(0x80);
  var l = bytes.length / 4 + 2;
  var N = Math.ceil(l / 16);
  var M = new Array(N);

  for (var _i = 0; _i < N; ++_i) {
    var arr = new Uint32Array(16);

    for (var j = 0; j < 16; ++j) {
      arr[j] = bytes[_i * 64 + j * 4] << 24 | bytes[_i * 64 + j * 4 + 1] << 16 | bytes[_i * 64 + j * 4 + 2] << 8 | bytes[_i * 64 + j * 4 + 3];
    }

    M[_i] = arr;
  }

  M[N - 1][14] = (bytes.length - 1) * 8 / Math.pow(2, 32);
  M[N - 1][14] = Math.floor(M[N - 1][14]);
  M[N - 1][15] = (bytes.length - 1) * 8 & 0xffffffff;

  for (var _i2 = 0; _i2 < N; ++_i2) {
    var W = new Uint32Array(80);

    for (var t = 0; t < 16; ++t) {
      W[t] = M[_i2][t];
    }

    for (var _t = 16; _t < 80; ++_t) {
      W[_t] = ROTL(W[_t - 3] ^ W[_t - 8] ^ W[_t - 14] ^ W[_t - 16], 1);
    }

    var a = H[0];
    var b = H[1];
    var c = H[2];
    var d = H[3];
    var e = H[4];

    for (var _t2 = 0; _t2 < 80; ++_t2) {
      var s = Math.floor(_t2 / 20);
      var T = ROTL(a, 5) + f(s, b, c, d) + e + K[s] + W[_t2] >>> 0;
      e = d;
      d = c;
      c = ROTL(b, 30) >>> 0;
      b = a;
      a = T;
    }

    H[0] = H[0] + a >>> 0;
    H[1] = H[1] + b >>> 0;
    H[2] = H[2] + c >>> 0;
    H[3] = H[3] + d >>> 0;
    H[4] = H[4] + e >>> 0;
  }

  return [H[0] >> 24 & 0xff, H[0] >> 16 & 0xff, H[0] >> 8 & 0xff, H[0] & 0xff, H[1] >> 24 & 0xff, H[1] >> 16 & 0xff, H[1] >> 8 & 0xff, H[1] & 0xff, H[2] >> 24 & 0xff, H[2] >> 16 & 0xff, H[2] >> 8 & 0xff, H[2] & 0xff, H[3] >> 24 & 0xff, H[3] >> 16 & 0xff, H[3] >> 8 & 0xff, H[3] & 0xff, H[4] >> 24 & 0xff, H[4] >> 16 & 0xff, H[4] >> 8 & 0xff, H[4] & 0xff];
}

v35('v5', 0x50, sha1);

function isFunction$1(func) {
  return typeof func === 'function';
}

const notInitiated = Symbol('notInitiated');

function createSingleton(construct, resetAfterExecution = false) {
  if (!isFunction$1(construct)) {
    throw new Error(`Singleton construct function must be a function`);
  }
  let result = notInitiated;
  let success = false;
  let inProgress = false;
  const reset = () => {
    result = notInitiated;
    success = false;
    inProgress = false;
  };
  const singleton = () => {
    if (result === notInitiated) {
      result = construct();
      if (result instanceof Promise) {
        inProgress = true;
        result
          .then(() => {
            success = true;
          })
          .catch(() => {
            result = notInitiated;
            success = false;
          })
          .finally(() => {
            inProgress = false;
            if (resetAfterExecution) {
              reset();
            }
          });
      } else {
        success = true;
      }
    }
    return result;
  };
  singleton.reset = reset;
  singleton.isInitialized = () => result !== notInitiated;
  singleton.executionSuccessful = () => success;
  singleton.inProgress = () => inProgress;
  return singleton;
}

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-02.
 * Copyright © Softgames 2019 - present
 */

const VERSION$1 = '6.0.0';

const ENCODED_BOTS_CONFIG = 'eyJIT1NUIjoiI3tnYW1lSWR9LmJzLnNicy5zb2Z0Z2FtZXMuZGUiLCJCQVNJQ19BVVRIIjp7IlVTRVIiOiJib3Qtc2VydmljZSIsIlBBU1MiOiJhRHVEb2xsYUgifSwiRU5EUE9JTlRTIjp7IkNPTlZFUlNJT04iOiIvYXBpL3YxL2NvbnZlcnNpb25zL2dhbWUvI3tnYW1lSWR9IiwiR0VUX1NDSEVEVUxFRF9NRVNTQUdFIjoiL2FwaS92MS9zY2hlZHVsZS91c2VyLyN7cGxheWVySWR9L2dhbWUvI3tnYW1lSWR9IiwiQ1JFQVRFX1NDSEVEVUxFRF9NRVNTQUdFIjoiL2FwaS92MS9zY2hlZHVsZS9nYW1lLyN7Z2FtZUlkfT9lbnZpcm9ubWVudD0je2Vudmlyb25tZW50fSIsIkRFTEVURV9TQ0hFRFVMRURfTUVTU0FHRSI6Ii9hcGkvdjEvc2NoZWR1bGUvI3tzY2hlZHVsZUlkfSJ9fQ==';
const ENCODED_GB_CONFIG = 'eyJIT1NUIjoiI3tnYW1lSWR9LmdiLnNicy5zb2Z0Z2FtZXMuZGUiLCJFTkRQT0lOVFMiOnsiV1MiOiIvc29ja2V0L2dhbWUvI3tnYW1lSWR9IiwiSFRUUCI6Ii9ncmFwaHFsL2dhbWUvI3tnYW1lSWR9IiwiUkVTVCI6Ii9yZXN0L2dhbWUvI3tnYW1lSWR9In19';

const GAME_ID_PLACEHOLDER = '#{gameId}';
const PLAYER_ID_PLACEHOLDER = '#{playerId}';
const SCHEDULE_ID_PLACEHOLDER = '#{scheduleId}';
const ENVIRONMENT_PLACEHOLDER = '#{environment}';

const TRACK_BOT_MESSAGE_CONVERSION_REQUEST_TIMEOUT = 10000;
const GET_SCHEDULED_BOT_MESSAGE_REQUEST_TIMEOUT = 10000;
const DELETE_SCHEDULED_BOT_MESSAGE_REQUEST_TIMEOUT = 10000;
const CREATE_SCHEDULED_BOT_MESSAGE_REQUEST_TIMEOUT = 15000;

const GA_TRACKING_ID = 'UA-157820994-3';

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-06-21.
 * Copyright © Softgames 2019 - present
 */

const decodedConfigs = new Map();

function decodeConfig(config) {
  try {
    if (!isNonEmptyString$3(config)) {
      throw new Error(`Must be a not empty string`);
    }
    if (!decodedConfigs.has(config)) {
      decodedConfigs.set(config, JSON.parse(atob(config)));
    }
    return decodedConfigs.get(config);
  } catch (error) {
    const saveError = error || new Error(`Caught empty error`);
    saveError.message = `Failed to decode config: ${saveError.message}`;
    throw saveError;
  }
}

function isString$3(value) {
  return Object.prototype.toString.call(value) === '[object String]';
}

function isNumber$1(value) {
  return Object.prototype.toString.call(value) === '[object Number]';
}

function replaceAll(source, needle, replace) {
  if (!isString$3(source)) {
    throw new Error(`source must be a string for replaceAll`);
  }
  return source.split(needle).join(replace);
}

const SPANS = {
  20: '0-20ms',
  50: '21-50ms',
  100: '51-100ms',
  150: '101-150ms',
  200: '151-200ms',
  300: '201-300ms',
  400: '301-400ms',
  500: '401-500ms',
  750: '501-750ms',
  1000: '751-1000ms',
  1500: '1001-1500ms',
  2000: '1501-2000ms',
};

function castDurationToMsGroupLabel(duration) {
  if (!isNumber$1(duration)) {
    throw new Error('"duration" must be a number');
  }
  const key = Object.keys(SPANS).find((k) => duration < Number(k));
  return key ? SPANS[key] : '>2000ms';
}

let config;
let bsConfig;
let currentGameId;

function getConfig() {
  if (!config) {
    const {
      HOST,
      ENDPOINTS: { WS, HTTP, REST },
    } = decodeConfig(ENCODED_GB_CONFIG);
    config = {
      HOST,
      ENDPOINTS: {
        WS,
        HTTP,
        REST,
      },
    };
  }
  return config;
}

function getBsConfig() {
  if (!bsConfig) {
    const {
      HOST,
      BASIC_AUTH: { USER, PASS },
      ENDPOINTS: {
        CONVERSION,
        GET_SCHEDULED_MESSAGE,
        CREATE_SCHEDULED_MESSAGE,
        DELETE_SCHEDULED_MESSAGE,
      },
    } = decodeConfig(ENCODED_BOTS_CONFIG);
    bsConfig = {
      HOST,
      BASIC_AUTH: {
        USER,
        PASS,
      },
      ENDPOINTS: {
        CONVERSION,
        GET_SCHEDULED_MESSAGE,
        CREATE_SCHEDULED_MESSAGE,
        DELETE_SCHEDULED_MESSAGE,
      },
    };
  }
  return bsConfig;
}

function initWithConfig({ gameId }) {
  if (!isNonEmptyString$3(gameId)) {
    throw new GBCErrorValidationFailed(
      'gameId must be an not-empty string',
      'game-slug',
    );
  }
  if (currentGameId && currentGameId !== gameId) {
    throw new GBCErrorValidationFailed(
      `Already initialized with gameId "${currentGameId}"`,
      currentGameId,
    );
  }
  currentGameId = gameId;
}

function getConnectionUrl() {
  if (!currentGameId) {
    throw new GBCInternalError(`GameUrlManager was not initialized`);
  }
  const { HOST } = getConfig();
  return replaceAll(HOST, GAME_ID_PLACEHOLDER, currentGameId);
}

function getBotServiceAuth() {
  const {
    BASIC_AUTH: { USER: BASIC_AUTH_USER, PASS: BASIC_AUTH_PASS },
  } = getBsConfig();
  return {
    username: BASIC_AUTH_USER,
    password: BASIC_AUTH_PASS,
  };
}

function getBotServiceUrlForEndpoint(endpoint) {
  if (!currentGameId) {
    throw new GBCInternalError(`GameUrlManager was not initialized`);
  }
  const { HOST } = getBsConfig();
  return replaceAll(
    `https://${HOST}${endpoint}`,
    GAME_ID_PLACEHOLDER,
    currentGameId,
  );
}

function getBotServiceCreateScheduledMessageUrl({ environment }) {
  const {
    ENDPOINTS: { CREATE_SCHEDULED_MESSAGE },
  } = getBsConfig();
  return getBotServiceUrlForEndpoint(CREATE_SCHEDULED_MESSAGE).replace(
    ENVIRONMENT_PLACEHOLDER,
    environment,
  );
}

function getBotServiceDeleteScheduledMessageUrl({ scheduledMessageId }) {
  const {
    ENDPOINTS: { DELETE_SCHEDULED_MESSAGE },
  } = getBsConfig();
  return getBotServiceUrlForEndpoint(DELETE_SCHEDULED_MESSAGE).replace(
    SCHEDULE_ID_PLACEHOLDER,
    scheduledMessageId,
  );
}

function getBotServiceGetScheduledMessageUrl({ playerId }) {
  const {
    ENDPOINTS: { GET_SCHEDULED_MESSAGE },
  } = getBsConfig();
  return getBotServiceUrlForEndpoint(GET_SCHEDULED_MESSAGE).replace(
    PLAYER_ID_PLACEHOLDER,
    playerId,
  );
}

function getBotServiceConversionUrl() {
  const {
    ENDPOINTS: { CONVERSION },
  } = getBsConfig();
  return getBotServiceUrlForEndpoint(CONVERSION);
}

const PACKET_TYPES = Object.create(null); // no Map = no polyfill
PACKET_TYPES["open"] = "0";
PACKET_TYPES["close"] = "1";
PACKET_TYPES["ping"] = "2";
PACKET_TYPES["pong"] = "3";
PACKET_TYPES["message"] = "4";
PACKET_TYPES["upgrade"] = "5";
PACKET_TYPES["noop"] = "6";
const PACKET_TYPES_REVERSE = Object.create(null);
Object.keys(PACKET_TYPES).forEach(key => {
    PACKET_TYPES_REVERSE[PACKET_TYPES[key]] = key;
});
const ERROR_PACKET = { type: "error", data: "parser error" };

const withNativeBlob$1 = typeof Blob === "function" ||
    (typeof Blob !== "undefined" &&
        Object.prototype.toString.call(Blob) === "[object BlobConstructor]");
const withNativeArrayBuffer$2 = typeof ArrayBuffer === "function";
// ArrayBuffer.isView method is not defined in IE10
const isView$1 = obj => {
    return typeof ArrayBuffer.isView === "function"
        ? ArrayBuffer.isView(obj)
        : obj && obj.buffer instanceof ArrayBuffer;
};
const encodePacket = ({ type, data }, supportsBinary, callback) => {
    if (withNativeBlob$1 && data instanceof Blob) {
        if (supportsBinary) {
            return callback(data);
        }
        else {
            return encodeBlobAsBase64(data, callback);
        }
    }
    else if (withNativeArrayBuffer$2 &&
        (data instanceof ArrayBuffer || isView$1(data))) {
        if (supportsBinary) {
            return callback(data);
        }
        else {
            return encodeBlobAsBase64(new Blob([data]), callback);
        }
    }
    // plain string
    return callback(PACKET_TYPES[type] + (data || ""));
};
const encodeBlobAsBase64 = (data, callback) => {
    const fileReader = new FileReader();
    fileReader.onload = function () {
        const content = fileReader.result.split(",")[1];
        callback("b" + content);
    };
    return fileReader.readAsDataURL(data);
};

const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
// Use a lookup table to find the index.
const lookup$1 = typeof Uint8Array === 'undefined' ? [] : new Uint8Array(256);
for (let i = 0; i < chars.length; i++) {
    lookup$1[chars.charCodeAt(i)] = i;
}
const decode$1 = (base64) => {
    let bufferLength = base64.length * 0.75, len = base64.length, i, p = 0, encoded1, encoded2, encoded3, encoded4;
    if (base64[base64.length - 1] === '=') {
        bufferLength--;
        if (base64[base64.length - 2] === '=') {
            bufferLength--;
        }
    }
    const arraybuffer = new ArrayBuffer(bufferLength), bytes = new Uint8Array(arraybuffer);
    for (i = 0; i < len; i += 4) {
        encoded1 = lookup$1[base64.charCodeAt(i)];
        encoded2 = lookup$1[base64.charCodeAt(i + 1)];
        encoded3 = lookup$1[base64.charCodeAt(i + 2)];
        encoded4 = lookup$1[base64.charCodeAt(i + 3)];
        bytes[p++] = (encoded1 << 2) | (encoded2 >> 4);
        bytes[p++] = ((encoded2 & 15) << 4) | (encoded3 >> 2);
        bytes[p++] = ((encoded3 & 3) << 6) | (encoded4 & 63);
    }
    return arraybuffer;
};

const withNativeArrayBuffer$1 = typeof ArrayBuffer === "function";
const decodePacket = (encodedPacket, binaryType) => {
    if (typeof encodedPacket !== "string") {
        return {
            type: "message",
            data: mapBinary(encodedPacket, binaryType)
        };
    }
    const type = encodedPacket.charAt(0);
    if (type === "b") {
        return {
            type: "message",
            data: decodeBase64Packet(encodedPacket.substring(1), binaryType)
        };
    }
    const packetType = PACKET_TYPES_REVERSE[type];
    if (!packetType) {
        return ERROR_PACKET;
    }
    return encodedPacket.length > 1
        ? {
            type: PACKET_TYPES_REVERSE[type],
            data: encodedPacket.substring(1)
        }
        : {
            type: PACKET_TYPES_REVERSE[type]
        };
};
const decodeBase64Packet = (data, binaryType) => {
    if (withNativeArrayBuffer$1) {
        const decoded = decode$1(data);
        return mapBinary(decoded, binaryType);
    }
    else {
        return { base64: true, data }; // fallback for old browsers
    }
};
const mapBinary = (data, binaryType) => {
    switch (binaryType) {
        case "blob":
            return data instanceof ArrayBuffer ? new Blob([data]) : data;
        case "arraybuffer":
        default:
            return data; // assuming the data is already an ArrayBuffer
    }
};

const SEPARATOR = String.fromCharCode(30); // see https://en.wikipedia.org/wiki/Delimiter#ASCII_delimited_text
const encodePayload = (packets, callback) => {
    // some packets may be added to the array while encoding, so the initial length must be saved
    const length = packets.length;
    const encodedPackets = new Array(length);
    let count = 0;
    packets.forEach((packet, i) => {
        // force base64 encoding for binary packets
        encodePacket(packet, false, encodedPacket => {
            encodedPackets[i] = encodedPacket;
            if (++count === length) {
                callback(encodedPackets.join(SEPARATOR));
            }
        });
    });
};
const decodePayload = (encodedPayload, binaryType) => {
    const encodedPackets = encodedPayload.split(SEPARATOR);
    const packets = [];
    for (let i = 0; i < encodedPackets.length; i++) {
        const decodedPacket = decodePacket(encodedPackets[i], binaryType);
        packets.push(decodedPacket);
        if (decodedPacket.type === "error") {
            break;
        }
    }
    return packets;
};
const protocol$1 = 4;

/**
 * Initialize a new `Emitter`.
 *
 * @api public
 */

function Emitter(obj) {
  if (obj) return mixin(obj);
}

/**
 * Mixin the emitter properties.
 *
 * @param {Object} obj
 * @return {Object}
 * @api private
 */

function mixin(obj) {
  for (var key in Emitter.prototype) {
    obj[key] = Emitter.prototype[key];
  }
  return obj;
}

/**
 * Listen on the given `event` with `fn`.
 *
 * @param {String} event
 * @param {Function} fn
 * @return {Emitter}
 * @api public
 */

Emitter.prototype.on =
Emitter.prototype.addEventListener = function(event, fn){
  this._callbacks = this._callbacks || {};
  (this._callbacks['$' + event] = this._callbacks['$' + event] || [])
    .push(fn);
  return this;
};

/**
 * Adds an `event` listener that will be invoked a single
 * time then automatically removed.
 *
 * @param {String} event
 * @param {Function} fn
 * @return {Emitter}
 * @api public
 */

Emitter.prototype.once = function(event, fn){
  function on() {
    this.off(event, on);
    fn.apply(this, arguments);
  }

  on.fn = fn;
  this.on(event, on);
  return this;
};

/**
 * Remove the given callback for `event` or all
 * registered callbacks.
 *
 * @param {String} event
 * @param {Function} fn
 * @return {Emitter}
 * @api public
 */

Emitter.prototype.off =
Emitter.prototype.removeListener =
Emitter.prototype.removeAllListeners =
Emitter.prototype.removeEventListener = function(event, fn){
  this._callbacks = this._callbacks || {};

  // all
  if (0 == arguments.length) {
    this._callbacks = {};
    return this;
  }

  // specific event
  var callbacks = this._callbacks['$' + event];
  if (!callbacks) return this;

  // remove all handlers
  if (1 == arguments.length) {
    delete this._callbacks['$' + event];
    return this;
  }

  // remove specific handler
  var cb;
  for (var i = 0; i < callbacks.length; i++) {
    cb = callbacks[i];
    if (cb === fn || cb.fn === fn) {
      callbacks.splice(i, 1);
      break;
    }
  }

  // Remove event specific arrays for event types that no
  // one is subscribed for to avoid memory leak.
  if (callbacks.length === 0) {
    delete this._callbacks['$' + event];
  }

  return this;
};

/**
 * Emit `event` with the given args.
 *
 * @param {String} event
 * @param {Mixed} ...
 * @return {Emitter}
 */

Emitter.prototype.emit = function(event){
  this._callbacks = this._callbacks || {};

  var args = new Array(arguments.length - 1)
    , callbacks = this._callbacks['$' + event];

  for (var i = 1; i < arguments.length; i++) {
    args[i - 1] = arguments[i];
  }

  if (callbacks) {
    callbacks = callbacks.slice(0);
    for (var i = 0, len = callbacks.length; i < len; ++i) {
      callbacks[i].apply(this, args);
    }
  }

  return this;
};

// alias used for reserved events (protected method)
Emitter.prototype.emitReserved = Emitter.prototype.emit;

/**
 * Return array of callbacks for `event`.
 *
 * @param {String} event
 * @return {Array}
 * @api public
 */

Emitter.prototype.listeners = function(event){
  this._callbacks = this._callbacks || {};
  return this._callbacks['$' + event] || [];
};

/**
 * Check if this emitter has `event` handlers.
 *
 * @param {String} event
 * @return {Boolean}
 * @api public
 */

Emitter.prototype.hasListeners = function(event){
  return !! this.listeners(event).length;
};

const globalThisShim = (() => {
    if (typeof self !== "undefined") {
        return self;
    }
    else if (typeof window !== "undefined") {
        return window;
    }
    else {
        return Function("return this")();
    }
})();

function pick(obj, ...attr) {
    return attr.reduce((acc, k) => {
        if (obj.hasOwnProperty(k)) {
            acc[k] = obj[k];
        }
        return acc;
    }, {});
}
// Keep a reference to the real timeout functions so they can be used when overridden
const NATIVE_SET_TIMEOUT = setTimeout;
const NATIVE_CLEAR_TIMEOUT = clearTimeout;
function installTimerFunctions(obj, opts) {
    if (opts.useNativeTimers) {
        obj.setTimeoutFn = NATIVE_SET_TIMEOUT.bind(globalThisShim);
        obj.clearTimeoutFn = NATIVE_CLEAR_TIMEOUT.bind(globalThisShim);
    }
    else {
        obj.setTimeoutFn = setTimeout.bind(globalThisShim);
        obj.clearTimeoutFn = clearTimeout.bind(globalThisShim);
    }
}
// base64 encoded buffers are about 33% bigger (https://en.wikipedia.org/wiki/Base64)
const BASE64_OVERHEAD = 1.33;
// we could also have used `new Blob([obj]).size`, but it isn't supported in IE9
function byteLength(obj) {
    if (typeof obj === "string") {
        return utf8Length(obj);
    }
    // arraybuffer or blob
    return Math.ceil((obj.byteLength || obj.size) * BASE64_OVERHEAD);
}
function utf8Length(str) {
    let c = 0, length = 0;
    for (let i = 0, l = str.length; i < l; i++) {
        c = str.charCodeAt(i);
        if (c < 0x80) {
            length += 1;
        }
        else if (c < 0x800) {
            length += 2;
        }
        else if (c < 0xd800 || c >= 0xe000) {
            length += 3;
        }
        else {
            i++;
            length += 4;
        }
    }
    return length;
}

class TransportError extends Error {
    constructor(reason, description, context) {
        super(reason);
        this.description = description;
        this.context = context;
        this.type = "TransportError";
    }
}
class Transport extends Emitter {
    /**
     * Transport abstract constructor.
     *
     * @param {Object} options.
     * @api private
     */
    constructor(opts) {
        super();
        this.writable = false;
        installTimerFunctions(this, opts);
        this.opts = opts;
        this.query = opts.query;
        this.readyState = "";
        this.socket = opts.socket;
    }
    /**
     * Emits an error.
     *
     * @param {String} reason
     * @param description
     * @param context - the error context
     * @return {Transport} for chaining
     * @api protected
     */
    onError(reason, description, context) {
        super.emitReserved("error", new TransportError(reason, description, context));
        return this;
    }
    /**
     * Opens the transport.
     *
     * @api public
     */
    open() {
        if ("closed" === this.readyState || "" === this.readyState) {
            this.readyState = "opening";
            this.doOpen();
        }
        return this;
    }
    /**
     * Closes the transport.
     *
     * @api public
     */
    close() {
        if ("opening" === this.readyState || "open" === this.readyState) {
            this.doClose();
            this.onClose();
        }
        return this;
    }
    /**
     * Sends multiple packets.
     *
     * @param {Array} packets
     * @api public
     */
    send(packets) {
        if ("open" === this.readyState) {
            this.write(packets);
        }
    }
    /**
     * Called upon open
     *
     * @api protected
     */
    onOpen() {
        this.readyState = "open";
        this.writable = true;
        super.emitReserved("open");
    }
    /**
     * Called with data.
     *
     * @param {String} data
     * @api protected
     */
    onData(data) {
        const packet = decodePacket(data, this.socket.binaryType);
        this.onPacket(packet);
    }
    /**
     * Called with a decoded packet.
     *
     * @api protected
     */
    onPacket(packet) {
        super.emitReserved("packet", packet);
    }
    /**
     * Called upon close.
     *
     * @api protected
     */
    onClose(details) {
        this.readyState = "closed";
        super.emitReserved("close", details);
    }
}

// imported from https://github.com/unshiftio/yeast
const alphabet = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz-_'.split(''), length = 64, map = {};
let seed = 0, i = 0, prev;
/**
 * Return a string representing the specified number.
 *
 * @param {Number} num The number to convert.
 * @returns {String} The string representation of the number.
 * @api public
 */
function encode$2(num) {
    let encoded = '';
    do {
        encoded = alphabet[num % length] + encoded;
        num = Math.floor(num / length);
    } while (num > 0);
    return encoded;
}
/**
 * Yeast: A tiny growing id generator.
 *
 * @returns {String} A unique id.
 * @api public
 */
function yeast() {
    const now = encode$2(+new Date());
    if (now !== prev)
        return seed = 0, prev = now;
    return now + '.' + encode$2(seed++);
}
//
// Map each character to its index.
//
for (; i < length; i++)
    map[alphabet[i]] = i;

// imported from https://github.com/galkn/querystring
/**
 * Compiles a querystring
 * Returns string representation of the object
 *
 * @param {Object}
 * @api private
 */
function encode$1(obj) {
    let str = '';
    for (let i in obj) {
        if (obj.hasOwnProperty(i)) {
            if (str.length)
                str += '&';
            str += encodeURIComponent(i) + '=' + encodeURIComponent(obj[i]);
        }
    }
    return str;
}
/**
 * Parses a simple querystring into an object
 *
 * @param {String} qs
 * @api private
 */
function decode(qs) {
    let qry = {};
    let pairs = qs.split('&');
    for (let i = 0, l = pairs.length; i < l; i++) {
        let pair = pairs[i].split('=');
        qry[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1]);
    }
    return qry;
}

// imported from https://github.com/component/has-cors
let value = false;
try {
    value = typeof XMLHttpRequest !== 'undefined' &&
        'withCredentials' in new XMLHttpRequest();
}
catch (err) {
    // if XMLHttp support is disabled in IE then it will throw
    // when trying to create
}
const hasCORS = value;

// browser shim for xmlhttprequest module
function XHR(opts) {
    const xdomain = opts.xdomain;
    // XMLHttpRequest can be disabled on IE
    try {
        if ("undefined" !== typeof XMLHttpRequest && (!xdomain || hasCORS)) {
            return new XMLHttpRequest();
        }
    }
    catch (e) { }
    if (!xdomain) {
        try {
            return new globalThisShim[["Active"].concat("Object").join("X")]("Microsoft.XMLHTTP");
        }
        catch (e) { }
    }
}

function empty() { }
const hasXHR2 = (function () {
    const xhr = new XHR({
        xdomain: false
    });
    return null != xhr.responseType;
})();
class Polling extends Transport {
    /**
     * XHR Polling constructor.
     *
     * @param {Object} opts
     * @api public
     */
    constructor(opts) {
        super(opts);
        this.polling = false;
        if (typeof location !== "undefined") {
            const isSSL = "https:" === location.protocol;
            let port = location.port;
            // some user agents have empty `location.port`
            if (!port) {
                port = isSSL ? "443" : "80";
            }
            this.xd =
                (typeof location !== "undefined" &&
                    opts.hostname !== location.hostname) ||
                    port !== opts.port;
            this.xs = opts.secure !== isSSL;
        }
        /**
         * XHR supports binary
         */
        const forceBase64 = opts && opts.forceBase64;
        this.supportsBinary = hasXHR2 && !forceBase64;
    }
    /**
     * Transport name.
     */
    get name() {
        return "polling";
    }
    /**
     * Opens the socket (triggers polling). We write a PING message to determine
     * when the transport is open.
     *
     * @api private
     */
    doOpen() {
        this.poll();
    }
    /**
     * Pauses polling.
     *
     * @param {Function} callback upon buffers are flushed and transport is paused
     * @api private
     */
    pause(onPause) {
        this.readyState = "pausing";
        const pause = () => {
            this.readyState = "paused";
            onPause();
        };
        if (this.polling || !this.writable) {
            let total = 0;
            if (this.polling) {
                total++;
                this.once("pollComplete", function () {
                    --total || pause();
                });
            }
            if (!this.writable) {
                total++;
                this.once("drain", function () {
                    --total || pause();
                });
            }
        }
        else {
            pause();
        }
    }
    /**
     * Starts polling cycle.
     *
     * @api public
     */
    poll() {
        this.polling = true;
        this.doPoll();
        this.emitReserved("poll");
    }
    /**
     * Overloads onData to detect payloads.
     *
     * @api private
     */
    onData(data) {
        const callback = packet => {
            // if its the first message we consider the transport open
            if ("opening" === this.readyState && packet.type === "open") {
                this.onOpen();
            }
            // if its a close packet, we close the ongoing requests
            if ("close" === packet.type) {
                this.onClose({ description: "transport closed by the server" });
                return false;
            }
            // otherwise bypass onData and handle the message
            this.onPacket(packet);
        };
        // decode payload
        decodePayload(data, this.socket.binaryType).forEach(callback);
        // if an event did not trigger closing
        if ("closed" !== this.readyState) {
            // if we got data we're not polling
            this.polling = false;
            this.emitReserved("pollComplete");
            if ("open" === this.readyState) {
                this.poll();
            }
        }
    }
    /**
     * For polling, send a close packet.
     *
     * @api private
     */
    doClose() {
        const close = () => {
            this.write([{ type: "close" }]);
        };
        if ("open" === this.readyState) {
            close();
        }
        else {
            // in case we're trying to close while
            // handshaking is in progress (GH-164)
            this.once("open", close);
        }
    }
    /**
     * Writes a packets payload.
     *
     * @param {Array} data packets
     * @param {Function} drain callback
     * @api private
     */
    write(packets) {
        this.writable = false;
        encodePayload(packets, data => {
            this.doWrite(data, () => {
                this.writable = true;
                this.emitReserved("drain");
            });
        });
    }
    /**
     * Generates uri for connection.
     *
     * @api private
     */
    uri() {
        let query = this.query || {};
        const schema = this.opts.secure ? "https" : "http";
        let port = "";
        // cache busting is forced
        if (false !== this.opts.timestampRequests) {
            query[this.opts.timestampParam] = yeast();
        }
        if (!this.supportsBinary && !query.sid) {
            query.b64 = 1;
        }
        // avoid port if default for schema
        if (this.opts.port &&
            (("https" === schema && Number(this.opts.port) !== 443) ||
                ("http" === schema && Number(this.opts.port) !== 80))) {
            port = ":" + this.opts.port;
        }
        const encodedQuery = encode$1(query);
        const ipv6 = this.opts.hostname.indexOf(":") !== -1;
        return (schema +
            "://" +
            (ipv6 ? "[" + this.opts.hostname + "]" : this.opts.hostname) +
            port +
            this.opts.path +
            (encodedQuery.length ? "?" + encodedQuery : ""));
    }
    /**
     * Creates a request.
     *
     * @param {String} method
     * @api private
     */
    request(opts = {}) {
        Object.assign(opts, { xd: this.xd, xs: this.xs }, this.opts);
        return new Request(this.uri(), opts);
    }
    /**
     * Sends data.
     *
     * @param {String} data to send.
     * @param {Function} called upon flush.
     * @api private
     */
    doWrite(data, fn) {
        const req = this.request({
            method: "POST",
            data: data
        });
        req.on("success", fn);
        req.on("error", (xhrStatus, context) => {
            this.onError("xhr post error", xhrStatus, context);
        });
    }
    /**
     * Starts a poll cycle.
     *
     * @api private
     */
    doPoll() {
        const req = this.request();
        req.on("data", this.onData.bind(this));
        req.on("error", (xhrStatus, context) => {
            this.onError("xhr poll error", xhrStatus, context);
        });
        this.pollXhr = req;
    }
}
class Request extends Emitter {
    /**
     * Request constructor
     *
     * @param {Object} options
     * @api public
     */
    constructor(uri, opts) {
        super();
        installTimerFunctions(this, opts);
        this.opts = opts;
        this.method = opts.method || "GET";
        this.uri = uri;
        this.async = false !== opts.async;
        this.data = undefined !== opts.data ? opts.data : null;
        this.create();
    }
    /**
     * Creates the XHR object and sends the request.
     *
     * @api private
     */
    create() {
        const opts = pick(this.opts, "agent", "pfx", "key", "passphrase", "cert", "ca", "ciphers", "rejectUnauthorized", "autoUnref");
        opts.xdomain = !!this.opts.xd;
        opts.xscheme = !!this.opts.xs;
        const xhr = (this.xhr = new XHR(opts));
        try {
            xhr.open(this.method, this.uri, this.async);
            try {
                if (this.opts.extraHeaders) {
                    xhr.setDisableHeaderCheck && xhr.setDisableHeaderCheck(true);
                    for (let i in this.opts.extraHeaders) {
                        if (this.opts.extraHeaders.hasOwnProperty(i)) {
                            xhr.setRequestHeader(i, this.opts.extraHeaders[i]);
                        }
                    }
                }
            }
            catch (e) { }
            if ("POST" === this.method) {
                try {
                    xhr.setRequestHeader("Content-type", "text/plain;charset=UTF-8");
                }
                catch (e) { }
            }
            try {
                xhr.setRequestHeader("Accept", "*/*");
            }
            catch (e) { }
            // ie6 check
            if ("withCredentials" in xhr) {
                xhr.withCredentials = this.opts.withCredentials;
            }
            if (this.opts.requestTimeout) {
                xhr.timeout = this.opts.requestTimeout;
            }
            xhr.onreadystatechange = () => {
                if (4 !== xhr.readyState)
                    return;
                if (200 === xhr.status || 1223 === xhr.status) {
                    this.onLoad();
                }
                else {
                    // make sure the `error` event handler that's user-set
                    // does not throw in the same tick and gets caught here
                    this.setTimeoutFn(() => {
                        this.onError(typeof xhr.status === "number" ? xhr.status : 0);
                    }, 0);
                }
            };
            xhr.send(this.data);
        }
        catch (e) {
            // Need to defer since .create() is called directly from the constructor
            // and thus the 'error' event can only be only bound *after* this exception
            // occurs.  Therefore, also, we cannot throw here at all.
            this.setTimeoutFn(() => {
                this.onError(e);
            }, 0);
            return;
        }
        if (typeof document !== "undefined") {
            this.index = Request.requestsCount++;
            Request.requests[this.index] = this;
        }
    }
    /**
     * Called upon error.
     *
     * @api private
     */
    onError(err) {
        this.emitReserved("error", err, this.xhr);
        this.cleanup(true);
    }
    /**
     * Cleans up house.
     *
     * @api private
     */
    cleanup(fromError) {
        if ("undefined" === typeof this.xhr || null === this.xhr) {
            return;
        }
        this.xhr.onreadystatechange = empty;
        if (fromError) {
            try {
                this.xhr.abort();
            }
            catch (e) { }
        }
        if (typeof document !== "undefined") {
            delete Request.requests[this.index];
        }
        this.xhr = null;
    }
    /**
     * Called upon load.
     *
     * @api private
     */
    onLoad() {
        const data = this.xhr.responseText;
        if (data !== null) {
            this.emitReserved("data", data);
            this.emitReserved("success");
            this.cleanup();
        }
    }
    /**
     * Aborts the request.
     *
     * @api public
     */
    abort() {
        this.cleanup();
    }
}
Request.requestsCount = 0;
Request.requests = {};
/**
 * Aborts pending requests when unloading the window. This is needed to prevent
 * memory leaks (e.g. when using IE) and to ensure that no spurious error is
 * emitted.
 */
if (typeof document !== "undefined") {
    // @ts-ignore
    if (typeof attachEvent === "function") {
        // @ts-ignore
        attachEvent("onunload", unloadHandler);
    }
    else if (typeof addEventListener === "function") {
        const terminationEvent = "onpagehide" in globalThisShim ? "pagehide" : "unload";
        addEventListener(terminationEvent, unloadHandler, false);
    }
}
function unloadHandler() {
    for (let i in Request.requests) {
        if (Request.requests.hasOwnProperty(i)) {
            Request.requests[i].abort();
        }
    }
}

const nextTick = (() => {
    const isPromiseAvailable = typeof Promise === "function" && typeof Promise.resolve === "function";
    if (isPromiseAvailable) {
        return cb => Promise.resolve().then(cb);
    }
    else {
        return (cb, setTimeoutFn) => setTimeoutFn(cb, 0);
    }
})();
const WebSocket = globalThisShim.WebSocket || globalThisShim.MozWebSocket;
const usingBrowserWebSocket = true;
const defaultBinaryType = "arraybuffer";

// detect ReactNative environment
const isReactNative = typeof navigator !== "undefined" &&
    typeof navigator.product === "string" &&
    navigator.product.toLowerCase() === "reactnative";
class WS extends Transport {
    /**
     * WebSocket transport constructor.
     *
     * @api {Object} connection options
     * @api public
     */
    constructor(opts) {
        super(opts);
        this.supportsBinary = !opts.forceBase64;
    }
    /**
     * Transport name.
     *
     * @api public
     */
    get name() {
        return "websocket";
    }
    /**
     * Opens socket.
     *
     * @api private
     */
    doOpen() {
        if (!this.check()) {
            // let probe timeout
            return;
        }
        const uri = this.uri();
        const protocols = this.opts.protocols;
        // React Native only supports the 'headers' option, and will print a warning if anything else is passed
        const opts = isReactNative
            ? {}
            : pick(this.opts, "agent", "perMessageDeflate", "pfx", "key", "passphrase", "cert", "ca", "ciphers", "rejectUnauthorized", "localAddress", "protocolVersion", "origin", "maxPayload", "family", "checkServerIdentity");
        if (this.opts.extraHeaders) {
            opts.headers = this.opts.extraHeaders;
        }
        try {
            this.ws =
                usingBrowserWebSocket && !isReactNative
                    ? protocols
                        ? new WebSocket(uri, protocols)
                        : new WebSocket(uri)
                    : new WebSocket(uri, protocols, opts);
        }
        catch (err) {
            return this.emitReserved("error", err);
        }
        this.ws.binaryType = this.socket.binaryType || defaultBinaryType;
        this.addEventListeners();
    }
    /**
     * Adds event listeners to the socket
     *
     * @api private
     */
    addEventListeners() {
        this.ws.onopen = () => {
            if (this.opts.autoUnref) {
                this.ws._socket.unref();
            }
            this.onOpen();
        };
        this.ws.onclose = closeEvent => this.onClose({
            description: "websocket connection closed",
            context: closeEvent
        });
        this.ws.onmessage = ev => this.onData(ev.data);
        this.ws.onerror = e => this.onError("websocket error", e);
    }
    /**
     * Writes data to socket.
     *
     * @param {Array} array of packets.
     * @api private
     */
    write(packets) {
        this.writable = false;
        // encodePacket efficient as it uses WS framing
        // no need for encodePayload
        for (let i = 0; i < packets.length; i++) {
            const packet = packets[i];
            const lastPacket = i === packets.length - 1;
            encodePacket(packet, this.supportsBinary, data => {
                // always create a new object (GH-437)
                const opts = {};
                // Sometimes the websocket has already been closed but the browser didn't
                // have a chance of informing us about it yet, in that case send will
                // throw an error
                try {
                    if (usingBrowserWebSocket) {
                        // TypeError is thrown when passing the second argument on Safari
                        this.ws.send(data);
                    }
                }
                catch (e) {
                }
                if (lastPacket) {
                    // fake drain
                    // defer to next tick to allow Socket to clear writeBuffer
                    nextTick(() => {
                        this.writable = true;
                        this.emitReserved("drain");
                    }, this.setTimeoutFn);
                }
            });
        }
    }
    /**
     * Closes socket.
     *
     * @api private
     */
    doClose() {
        if (typeof this.ws !== "undefined") {
            this.ws.close();
            this.ws = null;
        }
    }
    /**
     * Generates uri for connection.
     *
     * @api private
     */
    uri() {
        let query = this.query || {};
        const schema = this.opts.secure ? "wss" : "ws";
        let port = "";
        // avoid port if default for schema
        if (this.opts.port &&
            (("wss" === schema && Number(this.opts.port) !== 443) ||
                ("ws" === schema && Number(this.opts.port) !== 80))) {
            port = ":" + this.opts.port;
        }
        // append timestamp to URI
        if (this.opts.timestampRequests) {
            query[this.opts.timestampParam] = yeast();
        }
        // communicate binary support capabilities
        if (!this.supportsBinary) {
            query.b64 = 1;
        }
        const encodedQuery = encode$1(query);
        const ipv6 = this.opts.hostname.indexOf(":") !== -1;
        return (schema +
            "://" +
            (ipv6 ? "[" + this.opts.hostname + "]" : this.opts.hostname) +
            port +
            this.opts.path +
            (encodedQuery.length ? "?" + encodedQuery : ""));
    }
    /**
     * Feature detection for WebSocket.
     *
     * @return {Boolean} whether this transport is available.
     * @api public
     */
    check() {
        return !!WebSocket;
    }
}

const transports$1 = {
    websocket: WS,
    polling: Polling
};

// imported from https://github.com/galkn/parseuri
/**
 * Parses an URI
 *
 * @author Steven Levithan <stevenlevithan.com> (MIT license)
 * @api private
 */
const re = /^(?:(?![^:@]+:[^:@\/]*@)(http|https|ws|wss):\/\/)?((?:(([^:@]*)(?::([^:@]*))?)?@)?((?:[a-f0-9]{0,4}:){2,7}[a-f0-9]{0,4}|[^:\/?#]*)(?::(\d*))?)(((\/(?:[^?#](?![^?#\/]*\.[^?#\/.]+(?:[?#]|$)))*\/?)?([^?#\/]*))(?:\?([^#]*))?(?:#(.*))?)/;
const parts = [
    'source', 'protocol', 'authority', 'userInfo', 'user', 'password', 'host', 'port', 'relative', 'path', 'directory', 'file', 'query', 'anchor'
];
function parse(str) {
    const src = str, b = str.indexOf('['), e = str.indexOf(']');
    if (b != -1 && e != -1) {
        str = str.substring(0, b) + str.substring(b, e).replace(/:/g, ';') + str.substring(e, str.length);
    }
    let m = re.exec(str || ''), uri = {}, i = 14;
    while (i--) {
        uri[parts[i]] = m[i] || '';
    }
    if (b != -1 && e != -1) {
        uri.source = src;
        uri.host = uri.host.substring(1, uri.host.length - 1).replace(/;/g, ':');
        uri.authority = uri.authority.replace('[', '').replace(']', '').replace(/;/g, ':');
        uri.ipv6uri = true;
    }
    uri.pathNames = pathNames(uri, uri['path']);
    uri.queryKey = queryKey(uri, uri['query']);
    return uri;
}
function pathNames(obj, path) {
    const regx = /\/{2,9}/g, names = path.replace(regx, "/").split("/");
    if (path.substr(0, 1) == '/' || path.length === 0) {
        names.splice(0, 1);
    }
    if (path.substr(path.length - 1, 1) == '/') {
        names.splice(names.length - 1, 1);
    }
    return names;
}
function queryKey(uri, query) {
    const data = {};
    query.replace(/(?:^|&)([^&=]*)=?([^&]*)/g, function ($0, $1, $2) {
        if ($1) {
            data[$1] = $2;
        }
    });
    return data;
}

class Socket$1 extends Emitter {
    /**
     * Socket constructor.
     *
     * @param {String|Object} uri or options
     * @param {Object} opts - options
     * @api public
     */
    constructor(uri, opts = {}) {
        super();
        if (uri && "object" === typeof uri) {
            opts = uri;
            uri = null;
        }
        if (uri) {
            uri = parse(uri);
            opts.hostname = uri.host;
            opts.secure = uri.protocol === "https" || uri.protocol === "wss";
            opts.port = uri.port;
            if (uri.query)
                opts.query = uri.query;
        }
        else if (opts.host) {
            opts.hostname = parse(opts.host).host;
        }
        installTimerFunctions(this, opts);
        this.secure =
            null != opts.secure
                ? opts.secure
                : typeof location !== "undefined" && "https:" === location.protocol;
        if (opts.hostname && !opts.port) {
            // if no port is specified manually, use the protocol default
            opts.port = this.secure ? "443" : "80";
        }
        this.hostname =
            opts.hostname ||
                (typeof location !== "undefined" ? location.hostname : "localhost");
        this.port =
            opts.port ||
                (typeof location !== "undefined" && location.port
                    ? location.port
                    : this.secure
                        ? "443"
                        : "80");
        this.transports = opts.transports || ["polling", "websocket"];
        this.readyState = "";
        this.writeBuffer = [];
        this.prevBufferLen = 0;
        this.opts = Object.assign({
            path: "/engine.io",
            agent: false,
            withCredentials: false,
            upgrade: true,
            timestampParam: "t",
            rememberUpgrade: false,
            rejectUnauthorized: true,
            perMessageDeflate: {
                threshold: 1024
            },
            transportOptions: {},
            closeOnBeforeunload: true
        }, opts);
        this.opts.path = this.opts.path.replace(/\/$/, "") + "/";
        if (typeof this.opts.query === "string") {
            this.opts.query = decode(this.opts.query);
        }
        // set on handshake
        this.id = null;
        this.upgrades = null;
        this.pingInterval = null;
        this.pingTimeout = null;
        // set on heartbeat
        this.pingTimeoutTimer = null;
        if (typeof addEventListener === "function") {
            if (this.opts.closeOnBeforeunload) {
                // Firefox closes the connection when the "beforeunload" event is emitted but not Chrome. This event listener
                // ensures every browser behaves the same (no "disconnect" event at the Socket.IO level when the page is
                // closed/reloaded)
                addEventListener("beforeunload", () => {
                    if (this.transport) {
                        // silently close the transport
                        this.transport.removeAllListeners();
                        this.transport.close();
                    }
                }, false);
            }
            if (this.hostname !== "localhost") {
                this.offlineEventListener = () => {
                    this.onClose("transport close", {
                        description: "network connection lost"
                    });
                };
                addEventListener("offline", this.offlineEventListener, false);
            }
        }
        this.open();
    }
    /**
     * Creates transport of the given type.
     *
     * @param {String} transport name
     * @return {Transport}
     * @api private
     */
    createTransport(name) {
        const query = Object.assign({}, this.opts.query);
        // append engine.io protocol identifier
        query.EIO = protocol$1;
        // transport name
        query.transport = name;
        // session id if we already have one
        if (this.id)
            query.sid = this.id;
        const opts = Object.assign({}, this.opts.transportOptions[name], this.opts, {
            query,
            socket: this,
            hostname: this.hostname,
            secure: this.secure,
            port: this.port
        });
        return new transports$1[name](opts);
    }
    /**
     * Initializes transport to use and starts probe.
     *
     * @api private
     */
    open() {
        let transport;
        if (this.opts.rememberUpgrade &&
            Socket$1.priorWebsocketSuccess &&
            this.transports.indexOf("websocket") !== -1) {
            transport = "websocket";
        }
        else if (0 === this.transports.length) {
            // Emit error on next tick so it can be listened to
            this.setTimeoutFn(() => {
                this.emitReserved("error", "No transports available");
            }, 0);
            return;
        }
        else {
            transport = this.transports[0];
        }
        this.readyState = "opening";
        // Retry with the next transport if the transport is disabled (jsonp: false)
        try {
            transport = this.createTransport(transport);
        }
        catch (e) {
            this.transports.shift();
            this.open();
            return;
        }
        transport.open();
        this.setTransport(transport);
    }
    /**
     * Sets the current transport. Disables the existing one (if any).
     *
     * @api private
     */
    setTransport(transport) {
        if (this.transport) {
            this.transport.removeAllListeners();
        }
        // set up transport
        this.transport = transport;
        // set up transport listeners
        transport
            .on("drain", this.onDrain.bind(this))
            .on("packet", this.onPacket.bind(this))
            .on("error", this.onError.bind(this))
            .on("close", reason => this.onClose("transport close", reason));
    }
    /**
     * Probes a transport.
     *
     * @param {String} transport name
     * @api private
     */
    probe(name) {
        let transport = this.createTransport(name);
        let failed = false;
        Socket$1.priorWebsocketSuccess = false;
        const onTransportOpen = () => {
            if (failed)
                return;
            transport.send([{ type: "ping", data: "probe" }]);
            transport.once("packet", msg => {
                if (failed)
                    return;
                if ("pong" === msg.type && "probe" === msg.data) {
                    this.upgrading = true;
                    this.emitReserved("upgrading", transport);
                    if (!transport)
                        return;
                    Socket$1.priorWebsocketSuccess = "websocket" === transport.name;
                    this.transport.pause(() => {
                        if (failed)
                            return;
                        if ("closed" === this.readyState)
                            return;
                        cleanup();
                        this.setTransport(transport);
                        transport.send([{ type: "upgrade" }]);
                        this.emitReserved("upgrade", transport);
                        transport = null;
                        this.upgrading = false;
                        this.flush();
                    });
                }
                else {
                    const err = new Error("probe error");
                    // @ts-ignore
                    err.transport = transport.name;
                    this.emitReserved("upgradeError", err);
                }
            });
        };
        function freezeTransport() {
            if (failed)
                return;
            // Any callback called by transport should be ignored since now
            failed = true;
            cleanup();
            transport.close();
            transport = null;
        }
        // Handle any error that happens while probing
        const onerror = err => {
            const error = new Error("probe error: " + err);
            // @ts-ignore
            error.transport = transport.name;
            freezeTransport();
            this.emitReserved("upgradeError", error);
        };
        function onTransportClose() {
            onerror("transport closed");
        }
        // When the socket is closed while we're probing
        function onclose() {
            onerror("socket closed");
        }
        // When the socket is upgraded while we're probing
        function onupgrade(to) {
            if (transport && to.name !== transport.name) {
                freezeTransport();
            }
        }
        // Remove all listeners on the transport and on self
        const cleanup = () => {
            transport.removeListener("open", onTransportOpen);
            transport.removeListener("error", onerror);
            transport.removeListener("close", onTransportClose);
            this.off("close", onclose);
            this.off("upgrading", onupgrade);
        };
        transport.once("open", onTransportOpen);
        transport.once("error", onerror);
        transport.once("close", onTransportClose);
        this.once("close", onclose);
        this.once("upgrading", onupgrade);
        transport.open();
    }
    /**
     * Called when connection is deemed open.
     *
     * @api private
     */
    onOpen() {
        this.readyState = "open";
        Socket$1.priorWebsocketSuccess = "websocket" === this.transport.name;
        this.emitReserved("open");
        this.flush();
        // we check for `readyState` in case an `open`
        // listener already closed the socket
        if ("open" === this.readyState &&
            this.opts.upgrade &&
            this.transport.pause) {
            let i = 0;
            const l = this.upgrades.length;
            for (; i < l; i++) {
                this.probe(this.upgrades[i]);
            }
        }
    }
    /**
     * Handles a packet.
     *
     * @api private
     */
    onPacket(packet) {
        if ("opening" === this.readyState ||
            "open" === this.readyState ||
            "closing" === this.readyState) {
            this.emitReserved("packet", packet);
            // Socket is live - any packet counts
            this.emitReserved("heartbeat");
            switch (packet.type) {
                case "open":
                    this.onHandshake(JSON.parse(packet.data));
                    break;
                case "ping":
                    this.resetPingTimeout();
                    this.sendPacket("pong");
                    this.emitReserved("ping");
                    this.emitReserved("pong");
                    break;
                case "error":
                    const err = new Error("server error");
                    // @ts-ignore
                    err.code = packet.data;
                    this.onError(err);
                    break;
                case "message":
                    this.emitReserved("data", packet.data);
                    this.emitReserved("message", packet.data);
                    break;
            }
        }
    }
    /**
     * Called upon handshake completion.
     *
     * @param {Object} data - handshake obj
     * @api private
     */
    onHandshake(data) {
        this.emitReserved("handshake", data);
        this.id = data.sid;
        this.transport.query.sid = data.sid;
        this.upgrades = this.filterUpgrades(data.upgrades);
        this.pingInterval = data.pingInterval;
        this.pingTimeout = data.pingTimeout;
        this.maxPayload = data.maxPayload;
        this.onOpen();
        // In case open handler closes socket
        if ("closed" === this.readyState)
            return;
        this.resetPingTimeout();
    }
    /**
     * Sets and resets ping timeout timer based on server pings.
     *
     * @api private
     */
    resetPingTimeout() {
        this.clearTimeoutFn(this.pingTimeoutTimer);
        this.pingTimeoutTimer = this.setTimeoutFn(() => {
            this.onClose("ping timeout");
        }, this.pingInterval + this.pingTimeout);
        if (this.opts.autoUnref) {
            this.pingTimeoutTimer.unref();
        }
    }
    /**
     * Called on `drain` event
     *
     * @api private
     */
    onDrain() {
        this.writeBuffer.splice(0, this.prevBufferLen);
        // setting prevBufferLen = 0 is very important
        // for example, when upgrading, upgrade packet is sent over,
        // and a nonzero prevBufferLen could cause problems on `drain`
        this.prevBufferLen = 0;
        if (0 === this.writeBuffer.length) {
            this.emitReserved("drain");
        }
        else {
            this.flush();
        }
    }
    /**
     * Flush write buffers.
     *
     * @api private
     */
    flush() {
        if ("closed" !== this.readyState &&
            this.transport.writable &&
            !this.upgrading &&
            this.writeBuffer.length) {
            const packets = this.getWritablePackets();
            this.transport.send(packets);
            // keep track of current length of writeBuffer
            // splice writeBuffer and callbackBuffer on `drain`
            this.prevBufferLen = packets.length;
            this.emitReserved("flush");
        }
    }
    /**
     * Ensure the encoded size of the writeBuffer is below the maxPayload value sent by the server (only for HTTP
     * long-polling)
     *
     * @private
     */
    getWritablePackets() {
        const shouldCheckPayloadSize = this.maxPayload &&
            this.transport.name === "polling" &&
            this.writeBuffer.length > 1;
        if (!shouldCheckPayloadSize) {
            return this.writeBuffer;
        }
        let payloadSize = 1; // first packet type
        for (let i = 0; i < this.writeBuffer.length; i++) {
            const data = this.writeBuffer[i].data;
            if (data) {
                payloadSize += byteLength(data);
            }
            if (i > 0 && payloadSize > this.maxPayload) {
                return this.writeBuffer.slice(0, i);
            }
            payloadSize += 2; // separator + packet type
        }
        return this.writeBuffer;
    }
    /**
     * Sends a message.
     *
     * @param {String} message.
     * @param {Function} callback function.
     * @param {Object} options.
     * @return {Socket} for chaining.
     * @api public
     */
    write(msg, options, fn) {
        this.sendPacket("message", msg, options, fn);
        return this;
    }
    send(msg, options, fn) {
        this.sendPacket("message", msg, options, fn);
        return this;
    }
    /**
     * Sends a packet.
     *
     * @param {String} packet type.
     * @param {String} data.
     * @param {Object} options.
     * @param {Function} callback function.
     * @api private
     */
    sendPacket(type, data, options, fn) {
        if ("function" === typeof data) {
            fn = data;
            data = undefined;
        }
        if ("function" === typeof options) {
            fn = options;
            options = null;
        }
        if ("closing" === this.readyState || "closed" === this.readyState) {
            return;
        }
        options = options || {};
        options.compress = false !== options.compress;
        const packet = {
            type: type,
            data: data,
            options: options
        };
        this.emitReserved("packetCreate", packet);
        this.writeBuffer.push(packet);
        if (fn)
            this.once("flush", fn);
        this.flush();
    }
    /**
     * Closes the connection.
     *
     * @api public
     */
    close() {
        const close = () => {
            this.onClose("forced close");
            this.transport.close();
        };
        const cleanupAndClose = () => {
            this.off("upgrade", cleanupAndClose);
            this.off("upgradeError", cleanupAndClose);
            close();
        };
        const waitForUpgrade = () => {
            // wait for upgrade to finish since we can't send packets while pausing a transport
            this.once("upgrade", cleanupAndClose);
            this.once("upgradeError", cleanupAndClose);
        };
        if ("opening" === this.readyState || "open" === this.readyState) {
            this.readyState = "closing";
            if (this.writeBuffer.length) {
                this.once("drain", () => {
                    if (this.upgrading) {
                        waitForUpgrade();
                    }
                    else {
                        close();
                    }
                });
            }
            else if (this.upgrading) {
                waitForUpgrade();
            }
            else {
                close();
            }
        }
        return this;
    }
    /**
     * Called upon transport error
     *
     * @api private
     */
    onError(err) {
        Socket$1.priorWebsocketSuccess = false;
        this.emitReserved("error", err);
        this.onClose("transport error", err);
    }
    /**
     * Called upon transport close.
     *
     * @api private
     */
    onClose(reason, description) {
        if ("opening" === this.readyState ||
            "open" === this.readyState ||
            "closing" === this.readyState) {
            // clear timers
            this.clearTimeoutFn(this.pingTimeoutTimer);
            // stop event from firing again for transport
            this.transport.removeAllListeners("close");
            // ensure transport won't stay open
            this.transport.close();
            // ignore further transport communication
            this.transport.removeAllListeners();
            if (typeof removeEventListener === "function") {
                removeEventListener("offline", this.offlineEventListener, false);
            }
            // set ready state
            this.readyState = "closed";
            // clear session id
            this.id = null;
            // emit close event
            this.emitReserved("close", reason, description);
            // clean buffers after, so users can still
            // grab the buffers on `close` event
            this.writeBuffer = [];
            this.prevBufferLen = 0;
        }
    }
    /**
     * Filters upgrades, returning only those matching client transports.
     *
     * @param {Array} server upgrades
     * @api private
     *
     */
    filterUpgrades(upgrades) {
        const filteredUpgrades = [];
        let i = 0;
        const j = upgrades.length;
        for (; i < j; i++) {
            if (~this.transports.indexOf(upgrades[i]))
                filteredUpgrades.push(upgrades[i]);
        }
        return filteredUpgrades;
    }
}
Socket$1.protocol = protocol$1;

/**
 * URL parser.
 *
 * @param uri - url
 * @param path - the request path of the connection
 * @param loc - An object meant to mimic window.location.
 *        Defaults to window.location.
 * @public
 */
function url(uri, path = "", loc) {
    let obj = uri;
    // default to window.location
    loc = loc || (typeof location !== "undefined" && location);
    if (null == uri)
        uri = loc.protocol + "//" + loc.host;
    // relative path support
    if (typeof uri === "string") {
        if ("/" === uri.charAt(0)) {
            if ("/" === uri.charAt(1)) {
                uri = loc.protocol + uri;
            }
            else {
                uri = loc.host + uri;
            }
        }
        if (!/^(https?|wss?):\/\//.test(uri)) {
            if ("undefined" !== typeof loc) {
                uri = loc.protocol + "//" + uri;
            }
            else {
                uri = "https://" + uri;
            }
        }
        // parse
        obj = parse(uri);
    }
    // make sure we treat `localhost:80` and `localhost` equally
    if (!obj.port) {
        if (/^(http|ws)$/.test(obj.protocol)) {
            obj.port = "80";
        }
        else if (/^(http|ws)s$/.test(obj.protocol)) {
            obj.port = "443";
        }
    }
    obj.path = obj.path || "/";
    const ipv6 = obj.host.indexOf(":") !== -1;
    const host = ipv6 ? "[" + obj.host + "]" : obj.host;
    // define unique id
    obj.id = obj.protocol + "://" + host + ":" + obj.port + path;
    // define href
    obj.href =
        obj.protocol +
            "://" +
            host +
            (loc && loc.port === obj.port ? "" : ":" + obj.port);
    return obj;
}

const withNativeArrayBuffer = typeof ArrayBuffer === "function";
const isView = (obj) => {
    return typeof ArrayBuffer.isView === "function"
        ? ArrayBuffer.isView(obj)
        : obj.buffer instanceof ArrayBuffer;
};
const toString$1 = Object.prototype.toString;
const withNativeBlob = typeof Blob === "function" ||
    (typeof Blob !== "undefined" &&
        toString$1.call(Blob) === "[object BlobConstructor]");
const withNativeFile = typeof File === "function" ||
    (typeof File !== "undefined" &&
        toString$1.call(File) === "[object FileConstructor]");
/**
 * Returns true if obj is a Buffer, an ArrayBuffer, a Blob or a File.
 *
 * @private
 */
function isBinary(obj) {
    return ((withNativeArrayBuffer && (obj instanceof ArrayBuffer || isView(obj))) ||
        (withNativeBlob && obj instanceof Blob) ||
        (withNativeFile && obj instanceof File));
}
function hasBinary(obj, toJSON) {
    if (!obj || typeof obj !== "object") {
        return false;
    }
    if (Array.isArray(obj)) {
        for (let i = 0, l = obj.length; i < l; i++) {
            if (hasBinary(obj[i])) {
                return true;
            }
        }
        return false;
    }
    if (isBinary(obj)) {
        return true;
    }
    if (obj.toJSON &&
        typeof obj.toJSON === "function" &&
        arguments.length === 1) {
        return hasBinary(obj.toJSON(), true);
    }
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key) && hasBinary(obj[key])) {
            return true;
        }
    }
    return false;
}

/**
 * Replaces every Buffer | ArrayBuffer | Blob | File in packet with a numbered placeholder.
 *
 * @param {Object} packet - socket.io event packet
 * @return {Object} with deconstructed packet and list of buffers
 * @public
 */
function deconstructPacket(packet) {
    const buffers = [];
    const packetData = packet.data;
    const pack = packet;
    pack.data = _deconstructPacket(packetData, buffers);
    pack.attachments = buffers.length; // number of binary 'attachments'
    return { packet: pack, buffers: buffers };
}
function _deconstructPacket(data, buffers) {
    if (!data)
        return data;
    if (isBinary(data)) {
        const placeholder = { _placeholder: true, num: buffers.length };
        buffers.push(data);
        return placeholder;
    }
    else if (Array.isArray(data)) {
        const newData = new Array(data.length);
        for (let i = 0; i < data.length; i++) {
            newData[i] = _deconstructPacket(data[i], buffers);
        }
        return newData;
    }
    else if (typeof data === "object" && !(data instanceof Date)) {
        const newData = {};
        for (const key in data) {
            if (Object.prototype.hasOwnProperty.call(data, key)) {
                newData[key] = _deconstructPacket(data[key], buffers);
            }
        }
        return newData;
    }
    return data;
}
/**
 * Reconstructs a binary packet from its placeholder packet and buffers
 *
 * @param {Object} packet - event packet with placeholders
 * @param {Array} buffers - binary buffers to put in placeholder positions
 * @return {Object} reconstructed packet
 * @public
 */
function reconstructPacket(packet, buffers) {
    packet.data = _reconstructPacket(packet.data, buffers);
    packet.attachments = undefined; // no longer useful
    return packet;
}
function _reconstructPacket(data, buffers) {
    if (!data)
        return data;
    if (data && data._placeholder) {
        return buffers[data.num]; // appropriate buffer (should be natural order anyway)
    }
    else if (Array.isArray(data)) {
        for (let i = 0; i < data.length; i++) {
            data[i] = _reconstructPacket(data[i], buffers);
        }
    }
    else if (typeof data === "object") {
        for (const key in data) {
            if (Object.prototype.hasOwnProperty.call(data, key)) {
                data[key] = _reconstructPacket(data[key], buffers);
            }
        }
    }
    return data;
}

/**
 * Protocol version.
 *
 * @public
 */
const protocol = 5;
var PacketType;
(function (PacketType) {
    PacketType[PacketType["CONNECT"] = 0] = "CONNECT";
    PacketType[PacketType["DISCONNECT"] = 1] = "DISCONNECT";
    PacketType[PacketType["EVENT"] = 2] = "EVENT";
    PacketType[PacketType["ACK"] = 3] = "ACK";
    PacketType[PacketType["CONNECT_ERROR"] = 4] = "CONNECT_ERROR";
    PacketType[PacketType["BINARY_EVENT"] = 5] = "BINARY_EVENT";
    PacketType[PacketType["BINARY_ACK"] = 6] = "BINARY_ACK";
})(PacketType || (PacketType = {}));
/**
 * A socket.io Encoder instance
 */
class Encoder {
    /**
     * Encoder constructor
     *
     * @param {function} replacer - custom replacer to pass down to JSON.parse
     */
    constructor(replacer) {
        this.replacer = replacer;
    }
    /**
     * Encode a packet as a single string if non-binary, or as a
     * buffer sequence, depending on packet type.
     *
     * @param {Object} obj - packet object
     */
    encode(obj) {
        if (obj.type === PacketType.EVENT || obj.type === PacketType.ACK) {
            if (hasBinary(obj)) {
                obj.type =
                    obj.type === PacketType.EVENT
                        ? PacketType.BINARY_EVENT
                        : PacketType.BINARY_ACK;
                return this.encodeAsBinary(obj);
            }
        }
        return [this.encodeAsString(obj)];
    }
    /**
     * Encode packet as string.
     */
    encodeAsString(obj) {
        // first is type
        let str = "" + obj.type;
        // attachments if we have them
        if (obj.type === PacketType.BINARY_EVENT ||
            obj.type === PacketType.BINARY_ACK) {
            str += obj.attachments + "-";
        }
        // if we have a namespace other than `/`
        // we append it followed by a comma `,`
        if (obj.nsp && "/" !== obj.nsp) {
            str += obj.nsp + ",";
        }
        // immediately followed by the id
        if (null != obj.id) {
            str += obj.id;
        }
        // json data
        if (null != obj.data) {
            str += JSON.stringify(obj.data, this.replacer);
        }
        return str;
    }
    /**
     * Encode packet as 'buffer sequence' by removing blobs, and
     * deconstructing packet into object with placeholders and
     * a list of buffers.
     */
    encodeAsBinary(obj) {
        const deconstruction = deconstructPacket(obj);
        const pack = this.encodeAsString(deconstruction.packet);
        const buffers = deconstruction.buffers;
        buffers.unshift(pack); // add packet info to beginning of data list
        return buffers; // write all the buffers
    }
}
/**
 * A socket.io Decoder instance
 *
 * @return {Object} decoder
 */
class Decoder extends Emitter {
    /**
     * Decoder constructor
     *
     * @param {function} reviver - custom reviver to pass down to JSON.stringify
     */
    constructor(reviver) {
        super();
        this.reviver = reviver;
    }
    /**
     * Decodes an encoded packet string into packet JSON.
     *
     * @param {String} obj - encoded packet
     */
    add(obj) {
        let packet;
        if (typeof obj === "string") {
            packet = this.decodeString(obj);
            if (packet.type === PacketType.BINARY_EVENT ||
                packet.type === PacketType.BINARY_ACK) {
                // binary packet's json
                this.reconstructor = new BinaryReconstructor(packet);
                // no attachments, labeled binary but no binary data to follow
                if (packet.attachments === 0) {
                    super.emitReserved("decoded", packet);
                }
            }
            else {
                // non-binary full packet
                super.emitReserved("decoded", packet);
            }
        }
        else if (isBinary(obj) || obj.base64) {
            // raw binary data
            if (!this.reconstructor) {
                throw new Error("got binary data when not reconstructing a packet");
            }
            else {
                packet = this.reconstructor.takeBinaryData(obj);
                if (packet) {
                    // received final buffer
                    this.reconstructor = null;
                    super.emitReserved("decoded", packet);
                }
            }
        }
        else {
            throw new Error("Unknown type: " + obj);
        }
    }
    /**
     * Decode a packet String (JSON data)
     *
     * @param {String} str
     * @return {Object} packet
     */
    decodeString(str) {
        let i = 0;
        // look up type
        const p = {
            type: Number(str.charAt(0)),
        };
        if (PacketType[p.type] === undefined) {
            throw new Error("unknown packet type " + p.type);
        }
        // look up attachments if type binary
        if (p.type === PacketType.BINARY_EVENT ||
            p.type === PacketType.BINARY_ACK) {
            const start = i + 1;
            while (str.charAt(++i) !== "-" && i != str.length) { }
            const buf = str.substring(start, i);
            if (buf != Number(buf) || str.charAt(i) !== "-") {
                throw new Error("Illegal attachments");
            }
            p.attachments = Number(buf);
        }
        // look up namespace (if any)
        if ("/" === str.charAt(i + 1)) {
            const start = i + 1;
            while (++i) {
                const c = str.charAt(i);
                if ("," === c)
                    break;
                if (i === str.length)
                    break;
            }
            p.nsp = str.substring(start, i);
        }
        else {
            p.nsp = "/";
        }
        // look up id
        const next = str.charAt(i + 1);
        if ("" !== next && Number(next) == next) {
            const start = i + 1;
            while (++i) {
                const c = str.charAt(i);
                if (null == c || Number(c) != c) {
                    --i;
                    break;
                }
                if (i === str.length)
                    break;
            }
            p.id = Number(str.substring(start, i + 1));
        }
        // look up json data
        if (str.charAt(++i)) {
            const payload = this.tryParse(str.substr(i));
            if (Decoder.isPayloadValid(p.type, payload)) {
                p.data = payload;
            }
            else {
                throw new Error("invalid payload");
            }
        }
        return p;
    }
    tryParse(str) {
        try {
            return JSON.parse(str, this.reviver);
        }
        catch (e) {
            return false;
        }
    }
    static isPayloadValid(type, payload) {
        switch (type) {
            case PacketType.CONNECT:
                return typeof payload === "object";
            case PacketType.DISCONNECT:
                return payload === undefined;
            case PacketType.CONNECT_ERROR:
                return typeof payload === "string" || typeof payload === "object";
            case PacketType.EVENT:
            case PacketType.BINARY_EVENT:
                return Array.isArray(payload) && payload.length > 0;
            case PacketType.ACK:
            case PacketType.BINARY_ACK:
                return Array.isArray(payload);
        }
    }
    /**
     * Deallocates a parser's resources
     */
    destroy() {
        if (this.reconstructor) {
            this.reconstructor.finishedReconstruction();
        }
    }
}
/**
 * A manager of a binary event's 'buffer sequence'. Should
 * be constructed whenever a packet of type BINARY_EVENT is
 * decoded.
 *
 * @param {Object} packet
 * @return {BinaryReconstructor} initialized reconstructor
 */
class BinaryReconstructor {
    constructor(packet) {
        this.packet = packet;
        this.buffers = [];
        this.reconPack = packet;
    }
    /**
     * Method to be called when binary data received from connection
     * after a BINARY_EVENT packet.
     *
     * @param {Buffer | ArrayBuffer} binData - the raw binary data received
     * @return {null | Object} returns null if more binary data is expected or
     *   a reconstructed packet object if all buffers have been received.
     */
    takeBinaryData(binData) {
        this.buffers.push(binData);
        if (this.buffers.length === this.reconPack.attachments) {
            // done with buffer list
            const packet = reconstructPacket(this.reconPack, this.buffers);
            this.finishedReconstruction();
            return packet;
        }
        return null;
    }
    /**
     * Cleans up binary packet reconstruction variables.
     */
    finishedReconstruction() {
        this.reconPack = null;
        this.buffers = [];
    }
}

var parser = /*#__PURE__*/Object.freeze({
  __proto__: null,
  protocol: protocol,
  get PacketType () { return PacketType; },
  Encoder: Encoder,
  Decoder: Decoder
});

function on(obj, ev, fn) {
    obj.on(ev, fn);
    return function subDestroy() {
        obj.off(ev, fn);
    };
}

/**
 * Internal events.
 * These events can't be emitted by the user.
 */
const RESERVED_EVENTS = Object.freeze({
    connect: 1,
    connect_error: 1,
    disconnect: 1,
    disconnecting: 1,
    // EventEmitter reserved events: https://nodejs.org/api/events.html#events_event_newlistener
    newListener: 1,
    removeListener: 1,
});
class Socket extends Emitter {
    /**
     * `Socket` constructor.
     *
     * @public
     */
    constructor(io, nsp, opts) {
        super();
        this.connected = false;
        this.receiveBuffer = [];
        this.sendBuffer = [];
        this.ids = 0;
        this.acks = {};
        this.flags = {};
        this.io = io;
        this.nsp = nsp;
        if (opts && opts.auth) {
            this.auth = opts.auth;
        }
        if (this.io._autoConnect)
            this.open();
    }
    /**
     * Whether the socket is currently disconnected
     */
    get disconnected() {
        return !this.connected;
    }
    /**
     * Subscribe to open, close and packet events
     *
     * @private
     */
    subEvents() {
        if (this.subs)
            return;
        const io = this.io;
        this.subs = [
            on(io, "open", this.onopen.bind(this)),
            on(io, "packet", this.onpacket.bind(this)),
            on(io, "error", this.onerror.bind(this)),
            on(io, "close", this.onclose.bind(this)),
        ];
    }
    /**
     * Whether the Socket will try to reconnect when its Manager connects or reconnects
     */
    get active() {
        return !!this.subs;
    }
    /**
     * "Opens" the socket.
     *
     * @public
     */
    connect() {
        if (this.connected)
            return this;
        this.subEvents();
        if (!this.io["_reconnecting"])
            this.io.open(); // ensure open
        if ("open" === this.io._readyState)
            this.onopen();
        return this;
    }
    /**
     * Alias for connect()
     */
    open() {
        return this.connect();
    }
    /**
     * Sends a `message` event.
     *
     * @return self
     * @public
     */
    send(...args) {
        args.unshift("message");
        this.emit.apply(this, args);
        return this;
    }
    /**
     * Override `emit`.
     * If the event is in `events`, it's emitted normally.
     *
     * @return self
     * @public
     */
    emit(ev, ...args) {
        if (RESERVED_EVENTS.hasOwnProperty(ev)) {
            throw new Error('"' + ev + '" is a reserved event name');
        }
        args.unshift(ev);
        const packet = {
            type: PacketType.EVENT,
            data: args,
        };
        packet.options = {};
        packet.options.compress = this.flags.compress !== false;
        // event ack callback
        if ("function" === typeof args[args.length - 1]) {
            const id = this.ids++;
            const ack = args.pop();
            this._registerAckCallback(id, ack);
            packet.id = id;
        }
        const isTransportWritable = this.io.engine &&
            this.io.engine.transport &&
            this.io.engine.transport.writable;
        const discardPacket = this.flags.volatile && (!isTransportWritable || !this.connected);
        if (discardPacket) ;
        else if (this.connected) {
            this.notifyOutgoingListeners(packet);
            this.packet(packet);
        }
        else {
            this.sendBuffer.push(packet);
        }
        this.flags = {};
        return this;
    }
    /**
     * @private
     */
    _registerAckCallback(id, ack) {
        const timeout = this.flags.timeout;
        if (timeout === undefined) {
            this.acks[id] = ack;
            return;
        }
        // @ts-ignore
        const timer = this.io.setTimeoutFn(() => {
            delete this.acks[id];
            for (let i = 0; i < this.sendBuffer.length; i++) {
                if (this.sendBuffer[i].id === id) {
                    this.sendBuffer.splice(i, 1);
                }
            }
            ack.call(this, new Error("operation has timed out"));
        }, timeout);
        this.acks[id] = (...args) => {
            // @ts-ignore
            this.io.clearTimeoutFn(timer);
            ack.apply(this, [null, ...args]);
        };
    }
    /**
     * Sends a packet.
     *
     * @param packet
     * @private
     */
    packet(packet) {
        packet.nsp = this.nsp;
        this.io._packet(packet);
    }
    /**
     * Called upon engine `open`.
     *
     * @private
     */
    onopen() {
        if (typeof this.auth == "function") {
            this.auth((data) => {
                this.packet({ type: PacketType.CONNECT, data });
            });
        }
        else {
            this.packet({ type: PacketType.CONNECT, data: this.auth });
        }
    }
    /**
     * Called upon engine or manager `error`.
     *
     * @param err
     * @private
     */
    onerror(err) {
        if (!this.connected) {
            this.emitReserved("connect_error", err);
        }
    }
    /**
     * Called upon engine `close`.
     *
     * @param reason
     * @param description
     * @private
     */
    onclose(reason, description) {
        this.connected = false;
        delete this.id;
        this.emitReserved("disconnect", reason, description);
    }
    /**
     * Called with socket packet.
     *
     * @param packet
     * @private
     */
    onpacket(packet) {
        const sameNamespace = packet.nsp === this.nsp;
        if (!sameNamespace)
            return;
        switch (packet.type) {
            case PacketType.CONNECT:
                if (packet.data && packet.data.sid) {
                    const id = packet.data.sid;
                    this.onconnect(id);
                }
                else {
                    this.emitReserved("connect_error", new Error("It seems you are trying to reach a Socket.IO server in v2.x with a v3.x client, but they are not compatible (more information here: https://socket.io/docs/v3/migrating-from-2-x-to-3-0/)"));
                }
                break;
            case PacketType.EVENT:
            case PacketType.BINARY_EVENT:
                this.onevent(packet);
                break;
            case PacketType.ACK:
            case PacketType.BINARY_ACK:
                this.onack(packet);
                break;
            case PacketType.DISCONNECT:
                this.ondisconnect();
                break;
            case PacketType.CONNECT_ERROR:
                this.destroy();
                const err = new Error(packet.data.message);
                // @ts-ignore
                err.data = packet.data.data;
                this.emitReserved("connect_error", err);
                break;
        }
    }
    /**
     * Called upon a server event.
     *
     * @param packet
     * @private
     */
    onevent(packet) {
        const args = packet.data || [];
        if (null != packet.id) {
            args.push(this.ack(packet.id));
        }
        if (this.connected) {
            this.emitEvent(args);
        }
        else {
            this.receiveBuffer.push(Object.freeze(args));
        }
    }
    emitEvent(args) {
        if (this._anyListeners && this._anyListeners.length) {
            const listeners = this._anyListeners.slice();
            for (const listener of listeners) {
                listener.apply(this, args);
            }
        }
        super.emit.apply(this, args);
    }
    /**
     * Produces an ack callback to emit with an event.
     *
     * @private
     */
    ack(id) {
        const self = this;
        let sent = false;
        return function (...args) {
            // prevent double callbacks
            if (sent)
                return;
            sent = true;
            self.packet({
                type: PacketType.ACK,
                id: id,
                data: args,
            });
        };
    }
    /**
     * Called upon a server acknowlegement.
     *
     * @param packet
     * @private
     */
    onack(packet) {
        const ack = this.acks[packet.id];
        if ("function" === typeof ack) {
            ack.apply(this, packet.data);
            delete this.acks[packet.id];
        }
    }
    /**
     * Called upon server connect.
     *
     * @private
     */
    onconnect(id) {
        this.id = id;
        this.connected = true;
        this.emitBuffered();
        this.emitReserved("connect");
    }
    /**
     * Emit buffered events (received and emitted).
     *
     * @private
     */
    emitBuffered() {
        this.receiveBuffer.forEach((args) => this.emitEvent(args));
        this.receiveBuffer = [];
        this.sendBuffer.forEach((packet) => {
            this.notifyOutgoingListeners(packet);
            this.packet(packet);
        });
        this.sendBuffer = [];
    }
    /**
     * Called upon server disconnect.
     *
     * @private
     */
    ondisconnect() {
        this.destroy();
        this.onclose("io server disconnect");
    }
    /**
     * Called upon forced client/server side disconnections,
     * this method ensures the manager stops tracking us and
     * that reconnections don't get triggered for this.
     *
     * @private
     */
    destroy() {
        if (this.subs) {
            // clean subscriptions to avoid reconnections
            this.subs.forEach((subDestroy) => subDestroy());
            this.subs = undefined;
        }
        this.io["_destroy"](this);
    }
    /**
     * Disconnects the socket manually.
     *
     * @return self
     * @public
     */
    disconnect() {
        if (this.connected) {
            this.packet({ type: PacketType.DISCONNECT });
        }
        // remove socket from pool
        this.destroy();
        if (this.connected) {
            // fire events
            this.onclose("io client disconnect");
        }
        return this;
    }
    /**
     * Alias for disconnect()
     *
     * @return self
     * @public
     */
    close() {
        return this.disconnect();
    }
    /**
     * Sets the compress flag.
     *
     * @param compress - if `true`, compresses the sending data
     * @return self
     * @public
     */
    compress(compress) {
        this.flags.compress = compress;
        return this;
    }
    /**
     * Sets a modifier for a subsequent event emission that the event message will be dropped when this socket is not
     * ready to send messages.
     *
     * @returns self
     * @public
     */
    get volatile() {
        this.flags.volatile = true;
        return this;
    }
    /**
     * Sets a modifier for a subsequent event emission that the callback will be called with an error when the
     * given number of milliseconds have elapsed without an acknowledgement from the server:
     *
     * ```
     * socket.timeout(5000).emit("my-event", (err) => {
     *   if (err) {
     *     // the server did not acknowledge the event in the given delay
     *   }
     * });
     * ```
     *
     * @returns self
     * @public
     */
    timeout(timeout) {
        this.flags.timeout = timeout;
        return this;
    }
    /**
     * Adds a listener that will be fired when any event is emitted. The event name is passed as the first argument to the
     * callback.
     *
     * @param listener
     * @public
     */
    onAny(listener) {
        this._anyListeners = this._anyListeners || [];
        this._anyListeners.push(listener);
        return this;
    }
    /**
     * Adds a listener that will be fired when any event is emitted. The event name is passed as the first argument to the
     * callback. The listener is added to the beginning of the listeners array.
     *
     * @param listener
     * @public
     */
    prependAny(listener) {
        this._anyListeners = this._anyListeners || [];
        this._anyListeners.unshift(listener);
        return this;
    }
    /**
     * Removes the listener that will be fired when any event is emitted.
     *
     * @param listener
     * @public
     */
    offAny(listener) {
        if (!this._anyListeners) {
            return this;
        }
        if (listener) {
            const listeners = this._anyListeners;
            for (let i = 0; i < listeners.length; i++) {
                if (listener === listeners[i]) {
                    listeners.splice(i, 1);
                    return this;
                }
            }
        }
        else {
            this._anyListeners = [];
        }
        return this;
    }
    /**
     * Returns an array of listeners that are listening for any event that is specified. This array can be manipulated,
     * e.g. to remove listeners.
     *
     * @public
     */
    listenersAny() {
        return this._anyListeners || [];
    }
    /**
     * Adds a listener that will be fired when any event is emitted. The event name is passed as the first argument to the
     * callback.
     *
     * @param listener
     *
     * <pre><code>
     *
     * socket.onAnyOutgoing((event, ...args) => {
     *   console.log(event);
     * });
     *
     * </pre></code>
     *
     * @public
     */
    onAnyOutgoing(listener) {
        this._anyOutgoingListeners = this._anyOutgoingListeners || [];
        this._anyOutgoingListeners.push(listener);
        return this;
    }
    /**
     * Adds a listener that will be fired when any event is emitted. The event name is passed as the first argument to the
     * callback. The listener is added to the beginning of the listeners array.
     *
     * @param listener
     *
     * <pre><code>
     *
     * socket.prependAnyOutgoing((event, ...args) => {
     *   console.log(event);
     * });
     *
     * </pre></code>
     *
     * @public
     */
    prependAnyOutgoing(listener) {
        this._anyOutgoingListeners = this._anyOutgoingListeners || [];
        this._anyOutgoingListeners.unshift(listener);
        return this;
    }
    /**
     * Removes the listener that will be fired when any event is emitted.
     *
     * @param listener
     *
     * <pre><code>
     *
     * const handler = (event, ...args) => {
     *   console.log(event);
     * }
     *
     * socket.onAnyOutgoing(handler);
     *
     * // then later
     * socket.offAnyOutgoing(handler);
     *
     * </pre></code>
     *
     * @public
     */
    offAnyOutgoing(listener) {
        if (!this._anyOutgoingListeners) {
            return this;
        }
        if (listener) {
            const listeners = this._anyOutgoingListeners;
            for (let i = 0; i < listeners.length; i++) {
                if (listener === listeners[i]) {
                    listeners.splice(i, 1);
                    return this;
                }
            }
        }
        else {
            this._anyOutgoingListeners = [];
        }
        return this;
    }
    /**
     * Returns an array of listeners that are listening for any event that is specified. This array can be manipulated,
     * e.g. to remove listeners.
     *
     * @public
     */
    listenersAnyOutgoing() {
        return this._anyOutgoingListeners || [];
    }
    /**
     * Notify the listeners for each packet sent
     *
     * @param packet
     *
     * @private
     */
    notifyOutgoingListeners(packet) {
        if (this._anyOutgoingListeners && this._anyOutgoingListeners.length) {
            const listeners = this._anyOutgoingListeners.slice();
            for (const listener of listeners) {
                listener.apply(this, packet.data);
            }
        }
    }
}

/**
 * Initialize backoff timer with `opts`.
 *
 * - `min` initial timeout in milliseconds [100]
 * - `max` max timeout [10000]
 * - `jitter` [0]
 * - `factor` [2]
 *
 * @param {Object} opts
 * @api public
 */
function Backoff(opts) {
    opts = opts || {};
    this.ms = opts.min || 100;
    this.max = opts.max || 10000;
    this.factor = opts.factor || 2;
    this.jitter = opts.jitter > 0 && opts.jitter <= 1 ? opts.jitter : 0;
    this.attempts = 0;
}
/**
 * Return the backoff duration.
 *
 * @return {Number}
 * @api public
 */
Backoff.prototype.duration = function () {
    var ms = this.ms * Math.pow(this.factor, this.attempts++);
    if (this.jitter) {
        var rand = Math.random();
        var deviation = Math.floor(rand * this.jitter * ms);
        ms = (Math.floor(rand * 10) & 1) == 0 ? ms - deviation : ms + deviation;
    }
    return Math.min(ms, this.max) | 0;
};
/**
 * Reset the number of attempts.
 *
 * @api public
 */
Backoff.prototype.reset = function () {
    this.attempts = 0;
};
/**
 * Set the minimum duration
 *
 * @api public
 */
Backoff.prototype.setMin = function (min) {
    this.ms = min;
};
/**
 * Set the maximum duration
 *
 * @api public
 */
Backoff.prototype.setMax = function (max) {
    this.max = max;
};
/**
 * Set the jitter
 *
 * @api public
 */
Backoff.prototype.setJitter = function (jitter) {
    this.jitter = jitter;
};

class Manager extends Emitter {
    constructor(uri, opts) {
        var _a;
        super();
        this.nsps = {};
        this.subs = [];
        if (uri && "object" === typeof uri) {
            opts = uri;
            uri = undefined;
        }
        opts = opts || {};
        opts.path = opts.path || "/socket.io";
        this.opts = opts;
        installTimerFunctions(this, opts);
        this.reconnection(opts.reconnection !== false);
        this.reconnectionAttempts(opts.reconnectionAttempts || Infinity);
        this.reconnectionDelay(opts.reconnectionDelay || 1000);
        this.reconnectionDelayMax(opts.reconnectionDelayMax || 5000);
        this.randomizationFactor((_a = opts.randomizationFactor) !== null && _a !== void 0 ? _a : 0.5);
        this.backoff = new Backoff({
            min: this.reconnectionDelay(),
            max: this.reconnectionDelayMax(),
            jitter: this.randomizationFactor(),
        });
        this.timeout(null == opts.timeout ? 20000 : opts.timeout);
        this._readyState = "closed";
        this.uri = uri;
        const _parser = opts.parser || parser;
        this.encoder = new _parser.Encoder();
        this.decoder = new _parser.Decoder();
        this._autoConnect = opts.autoConnect !== false;
        if (this._autoConnect)
            this.open();
    }
    reconnection(v) {
        if (!arguments.length)
            return this._reconnection;
        this._reconnection = !!v;
        return this;
    }
    reconnectionAttempts(v) {
        if (v === undefined)
            return this._reconnectionAttempts;
        this._reconnectionAttempts = v;
        return this;
    }
    reconnectionDelay(v) {
        var _a;
        if (v === undefined)
            return this._reconnectionDelay;
        this._reconnectionDelay = v;
        (_a = this.backoff) === null || _a === void 0 ? void 0 : _a.setMin(v);
        return this;
    }
    randomizationFactor(v) {
        var _a;
        if (v === undefined)
            return this._randomizationFactor;
        this._randomizationFactor = v;
        (_a = this.backoff) === null || _a === void 0 ? void 0 : _a.setJitter(v);
        return this;
    }
    reconnectionDelayMax(v) {
        var _a;
        if (v === undefined)
            return this._reconnectionDelayMax;
        this._reconnectionDelayMax = v;
        (_a = this.backoff) === null || _a === void 0 ? void 0 : _a.setMax(v);
        return this;
    }
    timeout(v) {
        if (!arguments.length)
            return this._timeout;
        this._timeout = v;
        return this;
    }
    /**
     * Starts trying to reconnect if reconnection is enabled and we have not
     * started reconnecting yet
     *
     * @private
     */
    maybeReconnectOnOpen() {
        // Only try to reconnect if it's the first time we're connecting
        if (!this._reconnecting &&
            this._reconnection &&
            this.backoff.attempts === 0) {
            // keeps reconnection from firing twice for the same reconnection loop
            this.reconnect();
        }
    }
    /**
     * Sets the current transport `socket`.
     *
     * @param {Function} fn - optional, callback
     * @return self
     * @public
     */
    open(fn) {
        if (~this._readyState.indexOf("open"))
            return this;
        this.engine = new Socket$1(this.uri, this.opts);
        const socket = this.engine;
        const self = this;
        this._readyState = "opening";
        this.skipReconnect = false;
        // emit `open`
        const openSubDestroy = on(socket, "open", function () {
            self.onopen();
            fn && fn();
        });
        // emit `error`
        const errorSub = on(socket, "error", (err) => {
            self.cleanup();
            self._readyState = "closed";
            this.emitReserved("error", err);
            if (fn) {
                fn(err);
            }
            else {
                // Only do this if there is no fn to handle the error
                self.maybeReconnectOnOpen();
            }
        });
        if (false !== this._timeout) {
            const timeout = this._timeout;
            if (timeout === 0) {
                openSubDestroy(); // prevents a race condition with the 'open' event
            }
            // set timer
            const timer = this.setTimeoutFn(() => {
                openSubDestroy();
                socket.close();
                // @ts-ignore
                socket.emit("error", new Error("timeout"));
            }, timeout);
            if (this.opts.autoUnref) {
                timer.unref();
            }
            this.subs.push(function subDestroy() {
                clearTimeout(timer);
            });
        }
        this.subs.push(openSubDestroy);
        this.subs.push(errorSub);
        return this;
    }
    /**
     * Alias for open()
     *
     * @return self
     * @public
     */
    connect(fn) {
        return this.open(fn);
    }
    /**
     * Called upon transport open.
     *
     * @private
     */
    onopen() {
        // clear old subs
        this.cleanup();
        // mark as open
        this._readyState = "open";
        this.emitReserved("open");
        // add new subs
        const socket = this.engine;
        this.subs.push(on(socket, "ping", this.onping.bind(this)), on(socket, "data", this.ondata.bind(this)), on(socket, "error", this.onerror.bind(this)), on(socket, "close", this.onclose.bind(this)), on(this.decoder, "decoded", this.ondecoded.bind(this)));
    }
    /**
     * Called upon a ping.
     *
     * @private
     */
    onping() {
        this.emitReserved("ping");
    }
    /**
     * Called with data.
     *
     * @private
     */
    ondata(data) {
        this.decoder.add(data);
    }
    /**
     * Called when parser fully decodes a packet.
     *
     * @private
     */
    ondecoded(packet) {
        this.emitReserved("packet", packet);
    }
    /**
     * Called upon socket error.
     *
     * @private
     */
    onerror(err) {
        this.emitReserved("error", err);
    }
    /**
     * Creates a new socket for the given `nsp`.
     *
     * @return {Socket}
     * @public
     */
    socket(nsp, opts) {
        let socket = this.nsps[nsp];
        if (!socket) {
            socket = new Socket(this, nsp, opts);
            this.nsps[nsp] = socket;
        }
        return socket;
    }
    /**
     * Called upon a socket close.
     *
     * @param socket
     * @private
     */
    _destroy(socket) {
        const nsps = Object.keys(this.nsps);
        for (const nsp of nsps) {
            const socket = this.nsps[nsp];
            if (socket.active) {
                return;
            }
        }
        this._close();
    }
    /**
     * Writes a packet.
     *
     * @param packet
     * @private
     */
    _packet(packet) {
        const encodedPackets = this.encoder.encode(packet);
        for (let i = 0; i < encodedPackets.length; i++) {
            this.engine.write(encodedPackets[i], packet.options);
        }
    }
    /**
     * Clean up transport subscriptions and packet buffer.
     *
     * @private
     */
    cleanup() {
        this.subs.forEach((subDestroy) => subDestroy());
        this.subs.length = 0;
        this.decoder.destroy();
    }
    /**
     * Close the current socket.
     *
     * @private
     */
    _close() {
        this.skipReconnect = true;
        this._reconnecting = false;
        this.onclose("forced close");
        if (this.engine)
            this.engine.close();
    }
    /**
     * Alias for close()
     *
     * @private
     */
    disconnect() {
        return this._close();
    }
    /**
     * Called upon engine close.
     *
     * @private
     */
    onclose(reason, description) {
        this.cleanup();
        this.backoff.reset();
        this._readyState = "closed";
        this.emitReserved("close", reason, description);
        if (this._reconnection && !this.skipReconnect) {
            this.reconnect();
        }
    }
    /**
     * Attempt a reconnection.
     *
     * @private
     */
    reconnect() {
        if (this._reconnecting || this.skipReconnect)
            return this;
        const self = this;
        if (this.backoff.attempts >= this._reconnectionAttempts) {
            this.backoff.reset();
            this.emitReserved("reconnect_failed");
            this._reconnecting = false;
        }
        else {
            const delay = this.backoff.duration();
            this._reconnecting = true;
            const timer = this.setTimeoutFn(() => {
                if (self.skipReconnect)
                    return;
                this.emitReserved("reconnect_attempt", self.backoff.attempts);
                // check again for the case socket closed in above events
                if (self.skipReconnect)
                    return;
                self.open((err) => {
                    if (err) {
                        self._reconnecting = false;
                        self.reconnect();
                        this.emitReserved("reconnect_error", err);
                    }
                    else {
                        self.onreconnect();
                    }
                });
            }, delay);
            if (this.opts.autoUnref) {
                timer.unref();
            }
            this.subs.push(function subDestroy() {
                clearTimeout(timer);
            });
        }
    }
    /**
     * Called upon successful reconnect.
     *
     * @private
     */
    onreconnect() {
        const attempt = this.backoff.attempts;
        this._reconnecting = false;
        this.backoff.reset();
        this.emitReserved("reconnect", attempt);
    }
}

/**
 * Managers cache.
 */
const cache = {};
function lookup(uri, opts) {
    if (typeof uri === "object") {
        opts = uri;
        uri = undefined;
    }
    opts = opts || {};
    const parsed = url(uri, opts.path || "/socket.io");
    const source = parsed.source;
    const id = parsed.id;
    const path = parsed.path;
    const sameNamespace = cache[id] && path in cache[id]["nsps"];
    const newConnection = opts.forceNew ||
        opts["force new connection"] ||
        false === opts.multiplex ||
        sameNamespace;
    let io;
    if (newConnection) {
        io = new Manager(source, opts);
    }
    else {
        if (!cache[id]) {
            cache[id] = new Manager(source, opts);
        }
        io = cache[id];
    }
    if (parsed.query && !opts.query) {
        opts.query = parsed.queryKey;
    }
    return io.socket(parsed.path, opts);
}
// so that "lookup" can be used both as a function (e.g. `io(...)`) and as a
// namespace (e.g. `io.connect(...)`), for backward compatibility
Object.assign(lookup, {
    Manager,
    Socket,
    io: lookup,
    connect: lookup,
});

/**
 * Created by René Simon <rene.simon@softgames.de> on 2020-01-17.
 * Copyright © Softgames 2020 - present
 */

/**
 * @memberof module:base
 * @constant SEVERITY
 * @type {Object}
 * @property {Number} DEBUG - -1
 * @property {Number} INFO - 0
 * @property {Number} NOTICE - 1
 * @property {Number} WARN - 2
 * @property {Number} ERROR - 3
 * @property {Number} FATAL - 4
 * @property {Number} DISABLE - 5
 */
const SEVERITY$1$1 = {
  DEBUG: -1,
  INFO: 0,
  NOTICE: 1,
  WARN: 2,
  ERROR: 3,
  FATAL: 4,
  DISABLE: 5,
};

const allowedSeverities$2 = Object.values(SEVERITY$1$1);

function isValidSeverity$1(severity) {
  return allowedSeverities$2.includes(severity);
}

function isString$2(str) {
  return typeof str === 'string';
}

function isNonEmptyString$2(str) {
  return !!str && isString$2(str) && !!str.trim();
}

/**
 * Created by René Simon <rene.simon@softgames.de> on 2020-01-17.
 * Copyright © Softgames 2020 - present
 */

const SEVERITY$2 = SEVERITY$1$1;

let NAME$2 = null;

const consoleExists$1 =
  typeof console !== 'undefined' &&
  !!console &&
  typeof console.log === 'function';

function getLogFunction$1(target, ...alternatives) {
  if (typeof console[target] === 'function') {
    return console[target].bind(console);
  }
  if (!alternatives || !alternatives.length) {
    return console.log.bind(console);
  }
  return getLogFunction$1(...alternatives);
}

function getLog$3(logLevel) {
  return {
    log:
      !consoleExists$1 || logLevel > SEVERITY$2.INFO
        ? () => {}
        : getLogFunction$1('info'),
    debug:
      !consoleExists$1 || logLevel > SEVERITY$2.DEBUG
        ? () => {}
        : getLogFunction$1('debug'),
    info:
      !consoleExists$1 || logLevel > SEVERITY$2.INFO
        ? () => {}
        : getLogFunction$1('info'),
    notice:
      !consoleExists$1 || logLevel > SEVERITY$2.NOTICE
        ? () => {}
        : getLogFunction$1('notice', 'info'),
    warn:
      !consoleExists$1 || logLevel > SEVERITY$2.WARN
        ? () => {}
        : getLogFunction$1('warn', 'notice', 'info'),
    error:
      !consoleExists$1 || logLevel > SEVERITY$2.ERROR
        ? () => {}
        : getLogFunction$1('error'),
    fatal:
      !consoleExists$1 || logLevel > SEVERITY$2.FATAL
        ? () => {}
        : getLogFunction$1('fatal', 'error'),
  };
}

let currentLogger$1 = getLog$3(SEVERITY$2.DISABLE);

/**
 * @memberof module:base
 * @function setLogSeverity
 * @description
 * Sets the  project's severity to log.
 * The given severity and all higher severities are logged.
 *
 * @param {Object} options
 * @param {Number} options.severity - Severity of type SEVERITY
 * @example
 * import { setLogSeverity, SEVERITY } from '@sgorg/fe-logger';
 *
 * setLogSeverity({ severity: SEVERITY.WARN });
 * // After this all warn, error and fatal logs are printed in the console.
 *
 * @returns {void}
 */
function setLogSeverity$1({ severity } = {}) {
  if (!isValidSeverity$1(severity)) {
    throw new Error(
      `Severity must be on of ${allowedSeverities$2.join(
        ', ',
      )}. Suggest to use SEVERITY enum.`,
    );
  }
  currentLogger$1 = getLog$3(severity);
}

/**
 * @memberof module:base
 * @function init
 * @description
 * Initializes all loggers for the project.
 * Sets the project name and the project's severity to log (see also setLogSeverity).
 *
 * @param {Object} options
 * @param {String} options.name - Name of the logger
 * @param {Number} options.severity - Severity of type SEVERITY
 * @example
 * import { init, SEVERITY } from '@sgorg/fe-logger';
 *
 * init({ name: 'client', severity: SEVERITY.DEBUG });
 *
 * @returns {void}
 */
function init$1({ name, severity = SEVERITY$2.DISABLE } = {}) {
  if (!isNonEmptyString$2(name)) {
    throw new Error(`Name must be a non empty string`);
  }
  setLogSeverity$1({ severity });
  NAME$2 = name;
}

function getLogHandler$1(severity, name) {
  if (typeof currentLogger$1[severity] !== 'function') {
    throw new Error(
      `There is no log method with name "${severity}" for generating logger "${name}"`,
    );
  }
  return (...args) => {
    try {
      currentLogger$1[severity](`[${NAME$2}][${severity}]${name}`, ...args);
    } catch (error) {
      // [RS] Executing a log never generates an error
    }
  };
}

/**
 * @memberof module:base
 * @function getLogger
 * @description
 * Creates a new logger instance. By default all logs are disabled. Check init or setLogSeverity to change that.
 *
 * @param {Object} options
 * @param {String} options.name - Name of the logger
 * @example
 * import { getLogger } from '@sgorg/fe-logger';
 *
 * const log = getLogger({ name: 'utils' });
 *
 * @returns {module:base.Logger}
 */
function getLogger$1({ name } = {}) {
  if (!isNonEmptyString$2(name)) {
    throw new Error(`Name must be non-empty string`);
  }
  return Object.keys(currentLogger$1).reduce(
    (m, severity) => ({
      ...m,
      [severity]: getLogHandler$1(severity, name),
    }),
    {},
  );
}

const NAME$1 = 'GBC';

init$1({ name: NAME$1, severity: SEVERITY$2.DISABLE });

function setLogLevel$2(logLevel) {
  setLogSeverity$1({ severity: logLevel });
}

function getLog$2(name) {
  return getLogger$1({ name });
}

function Event(eventName) {
  const log = getLog$2(`${eventName}Event`);
  let handlers = {};
  let nextId = 0;

  function on(handler) {
    const id = nextId;
    nextId += 1;
    handlers[id] = handler;
    return () => delete handlers[id];
  }

  function emit(...args) {
    Object.values(handlers).forEach((f) => {
      try {
        const p = f(...args);
        if (p instanceof Promise) {
          p.catch((error) => {
            log.error(
              `Uncaught promise rejection while executing event listener provided by the library user.`,
              error,
            );
          });
        }
      } catch (error) {
        log.error(
          `Error while executing event listener provided by the library user.`,
          error,
        );
      }
    });
  }

  function once(handler) {
    const off = on((...args) => {
      handler(...args);
      off();
    });
    return off;
  }

  function clear() {
    handlers = {};
  }

  return {
    on,
    emit,
    once,
    clear,
    get listeners() {
      return Object.values(handlers);
    },
  };
}

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-09.
 * Copyright © Softgames 2019 - present
 */

const onLogin = new Event('onLogin');
const onOpen = new Event('onOpen');
const onConnectionError = new Event('onConnectionError');
const onClose = new Event('onClose');
const onError = new Event('onError');
const onMessage = new Event('onMessage');

var commonjsGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};

function getDefaultExportFromCjs (x) {
	return x && x.__esModule && Object.prototype.hasOwnProperty.call(x, 'default') ? x['default'] : x;
}

/** Used to match a single whitespace character. */

var reWhitespace = /\s/;

/**
 * Used by `_.trim` and `_.trimEnd` to get the index of the last non-whitespace
 * character of `string`.
 *
 * @private
 * @param {string} string The string to inspect.
 * @returns {number} Returns the index of the last non-whitespace character.
 */
function trimmedEndIndex$1(string) {
  var index = string.length;

  while (index-- && reWhitespace.test(string.charAt(index))) {}
  return index;
}

var _trimmedEndIndex = trimmedEndIndex$1;

var trimmedEndIndex = _trimmedEndIndex;

/** Used to match leading whitespace. */
var reTrimStart = /^\s+/;

/**
 * The base implementation of `_.trim`.
 *
 * @private
 * @param {string} string The string to trim.
 * @returns {string} Returns the trimmed string.
 */
function baseTrim$1(string) {
  return string
    ? string.slice(0, trimmedEndIndex(string) + 1).replace(reTrimStart, '')
    : string;
}

var _baseTrim = baseTrim$1;

/**
 * Checks if `value` is the
 * [language type](http://www.ecma-international.org/ecma-262/7.0/#sec-ecmascript-language-types)
 * of `Object`. (e.g. arrays, functions, objects, regexes, `new Number(0)`, and `new String('')`)
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an object, else `false`.
 * @example
 *
 * _.isObject({});
 * // => true
 *
 * _.isObject([1, 2, 3]);
 * // => true
 *
 * _.isObject(_.noop);
 * // => true
 *
 * _.isObject(null);
 * // => false
 */

function isObject$2(value) {
  var type = typeof value;
  return value != null && (type == 'object' || type == 'function');
}

var isObject_1 = isObject$2;

/** Detect free variable `global` from Node.js. */

var freeGlobal$1 = typeof commonjsGlobal == 'object' && commonjsGlobal && commonjsGlobal.Object === Object && commonjsGlobal;

var _freeGlobal = freeGlobal$1;

var freeGlobal = _freeGlobal;

/** Detect free variable `self`. */
var freeSelf = typeof self == 'object' && self && self.Object === Object && self;

/** Used as a reference to the global object. */
var root$1 = freeGlobal || freeSelf || Function('return this')();

var _root = root$1;

var root = _root;

/** Built-in value references. */
var Symbol$3 = root.Symbol;

var _Symbol = Symbol$3;

var Symbol$2 = _Symbol;

/** Used for built-in method references. */
var objectProto$2 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$1 = objectProto$2.hasOwnProperty;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var nativeObjectToString$1 = objectProto$2.toString;

/** Built-in value references. */
var symToStringTag$1 = Symbol$2 ? Symbol$2.toStringTag : undefined;

/**
 * A specialized version of `baseGetTag` which ignores `Symbol.toStringTag` values.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the raw `toStringTag`.
 */
function getRawTag$1(value) {
  var isOwn = hasOwnProperty$1.call(value, symToStringTag$1),
      tag = value[symToStringTag$1];

  try {
    value[symToStringTag$1] = undefined;
    var unmasked = true;
  } catch (e) {}

  var result = nativeObjectToString$1.call(value);
  if (unmasked) {
    if (isOwn) {
      value[symToStringTag$1] = tag;
    } else {
      delete value[symToStringTag$1];
    }
  }
  return result;
}

var _getRawTag = getRawTag$1;

/** Used for built-in method references. */

var objectProto$1 = Object.prototype;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var nativeObjectToString = objectProto$1.toString;

/**
 * Converts `value` to a string using `Object.prototype.toString`.
 *
 * @private
 * @param {*} value The value to convert.
 * @returns {string} Returns the converted string.
 */
function objectToString$1(value) {
  return nativeObjectToString.call(value);
}

var _objectToString = objectToString$1;

var Symbol$1 = _Symbol,
    getRawTag = _getRawTag,
    objectToString = _objectToString;

/** `Object#toString` result references. */
var nullTag = '[object Null]',
    undefinedTag = '[object Undefined]';

/** Built-in value references. */
var symToStringTag = Symbol$1 ? Symbol$1.toStringTag : undefined;

/**
 * The base implementation of `getTag` without fallbacks for buggy environments.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the `toStringTag`.
 */
function baseGetTag$2(value) {
  if (value == null) {
    return value === undefined ? undefinedTag : nullTag;
  }
  return (symToStringTag && symToStringTag in Object(value))
    ? getRawTag(value)
    : objectToString(value);
}

var _baseGetTag = baseGetTag$2;

/**
 * Checks if `value` is object-like. A value is object-like if it's not `null`
 * and has a `typeof` result of "object".
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is object-like, else `false`.
 * @example
 *
 * _.isObjectLike({});
 * // => true
 *
 * _.isObjectLike([1, 2, 3]);
 * // => true
 *
 * _.isObjectLike(_.noop);
 * // => false
 *
 * _.isObjectLike(null);
 * // => false
 */

function isObjectLike$2(value) {
  return value != null && typeof value == 'object';
}

var isObjectLike_1 = isObjectLike$2;

var baseGetTag$1 = _baseGetTag,
    isObjectLike$1 = isObjectLike_1;

/** `Object#toString` result references. */
var symbolTag = '[object Symbol]';

/**
 * Checks if `value` is classified as a `Symbol` primitive or object.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a symbol, else `false`.
 * @example
 *
 * _.isSymbol(Symbol.iterator);
 * // => true
 *
 * _.isSymbol('abc');
 * // => false
 */
function isSymbol$1(value) {
  return typeof value == 'symbol' ||
    (isObjectLike$1(value) && baseGetTag$1(value) == symbolTag);
}

var isSymbol_1 = isSymbol$1;

var baseTrim = _baseTrim,
    isObject$1 = isObject_1,
    isSymbol = isSymbol_1;

/** Used as references for various `Number` constants. */
var NAN = 0 / 0;

/** Used to detect bad signed hexadecimal string values. */
var reIsBadHex = /^[-+]0x[0-9a-f]+$/i;

/** Used to detect binary string values. */
var reIsBinary = /^0b[01]+$/i;

/** Used to detect octal string values. */
var reIsOctal = /^0o[0-7]+$/i;

/** Built-in method references without a dependency on `root`. */
var freeParseInt = parseInt;

/**
 * Converts `value` to a number.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to process.
 * @returns {number} Returns the number.
 * @example
 *
 * _.toNumber(3.2);
 * // => 3.2
 *
 * _.toNumber(Number.MIN_VALUE);
 * // => 5e-324
 *
 * _.toNumber(Infinity);
 * // => Infinity
 *
 * _.toNumber('3.2');
 * // => 3.2
 */
function toNumber$1(value) {
  if (typeof value == 'number') {
    return value;
  }
  if (isSymbol(value)) {
    return NAN;
  }
  if (isObject$1(value)) {
    var other = typeof value.valueOf == 'function' ? value.valueOf() : value;
    value = isObject$1(other) ? (other + '') : other;
  }
  if (typeof value != 'string') {
    return value === 0 ? value : +value;
  }
  value = baseTrim(value);
  var isBinary = reIsBinary.test(value);
  return (isBinary || reIsOctal.test(value))
    ? freeParseInt(value.slice(2), isBinary ? 2 : 8)
    : (reIsBadHex.test(value) ? NAN : +value);
}

var toNumber_1 = toNumber$1;

var toNumber = toNumber_1;

/** Used as references for various `Number` constants. */
var INFINITY = 1 / 0,
    MAX_INTEGER = 1.7976931348623157e+308;

/**
 * Converts `value` to a finite number.
 *
 * @static
 * @memberOf _
 * @since 4.12.0
 * @category Lang
 * @param {*} value The value to convert.
 * @returns {number} Returns the converted number.
 * @example
 *
 * _.toFinite(3.2);
 * // => 3.2
 *
 * _.toFinite(Number.MIN_VALUE);
 * // => 5e-324
 *
 * _.toFinite(Infinity);
 * // => 1.7976931348623157e+308
 *
 * _.toFinite('3.2');
 * // => 3.2
 */
function toFinite$1(value) {
  if (!value) {
    return value === 0 ? value : 0;
  }
  value = toNumber(value);
  if (value === INFINITY || value === -INFINITY) {
    var sign = (value < 0 ? -1 : 1);
    return sign * MAX_INTEGER;
  }
  return value === value ? value : 0;
}

var toFinite_1 = toFinite$1;

var toFinite = toFinite_1;

/**
 * Converts `value` to an integer.
 *
 * **Note:** This method is loosely based on
 * [`ToInteger`](http://www.ecma-international.org/ecma-262/7.0/#sec-tointeger).
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to convert.
 * @returns {number} Returns the converted integer.
 * @example
 *
 * _.toInteger(3.2);
 * // => 3
 *
 * _.toInteger(Number.MIN_VALUE);
 * // => 0
 *
 * _.toInteger(Infinity);
 * // => 1.7976931348623157e+308
 *
 * _.toInteger('3.2');
 * // => 3
 */
function toInteger$1(value) {
  var result = toFinite(value),
      remainder = result % 1;

  return result === result ? (remainder ? result - remainder : result) : 0;
}

var toInteger_1 = toInteger$1;

var toInteger = toInteger_1;

/**
 * Checks if `value` is an integer.
 *
 * **Note:** This method is based on
 * [`Number.isInteger`](https://mdn.io/Number/isInteger).
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an integer, else `false`.
 * @example
 *
 * _.isInteger(3);
 * // => true
 *
 * _.isInteger(Number.MIN_VALUE);
 * // => false
 *
 * _.isInteger(Infinity);
 * // => false
 *
 * _.isInteger('3');
 * // => false
 */
function isInteger$1(value) {
  return typeof value == 'number' && value == toInteger(value);
}

var isInteger_1 = isInteger$1;

function isPositiveInteger(val) {
  return isInteger_1(val) && val > 0;
}

function timeoutCallback(ms, callback) {
  if (!isPositiveInteger(ms)) {
    throw new GBCInternalError(`ms must be a positive integer`);
  }
  if (!isFunction$1(callback)) {
    throw new GBCInternalError(`callback must be a function`);
  }
  let called = false;
  const wrappedCallback = (error, ...args) => {
    if (called) return;
    called = true;
    callback(error, ...args);
  };
  setTimeout(() => {
    wrappedCallback(
      new GBCClientTimeoutError(1634735626, `Reached timeout of ${ms}ms`),
    );
  }, ms);
  return (...args) => {
    wrappedCallback(null, ...args);
  };
}

const ERROR_BY_CODE = {
  [ERROR_CODE.DB_TIMEOUT_ERROR]: (id, message) =>
    new GBCDbTimeoutError(id, message),
  [ERROR_CODE.REQUEST_TIMEOUT_ERROR]: (id, message) =>
    new GBCRequestTimeoutError(id, message),
};

function checkForGraphQlErrors(messageId, response) {
  const { error, errors } = response;
  if (!errors && !error) {
    return;
  }
  const eArray = errors || [error];
  const [firstError] = eArray;
  const { id, code: primaryCode, message: primaryMsg } = firstError || {};
  const { extensions: { code: secondaryCode } = {}, message: secondaryMsg } =
    primaryMsg || {};
  const serverMsg = secondaryMsg || primaryMsg;
  const msg =
    typeof serverMsg === 'string'
      ? serverMsg
      : 'Received a malformed error from GB Game Server';
  const code = secondaryCode || primaryCode;
  if (ERROR_BY_CODE[code]) {
    throw new ERROR_BY_CODE[code](id || 696, msg);
  }
  throw code && !!Object.values(ERROR_CODE).find((c) => c === code)
    ? new GBCKnownGraphQLError(code, id, messageId, msg)
    : new GBCUnknownGraphQLError(id, messageId, msg, null, firstError);
}

const RESPONSE_EVENT_PREFIX = 'm_r|';
const RES_TIMEOUT = 10 * 1000;

let lastMessageId = 0;

function nextMessageId() {
  lastMessageId += 1;
  if (!Number.isSafeInteger(lastMessageId)) {
    lastMessageId = 1;
  }
  return lastMessageId;
}

function responseEventName(msgId) {
  return RESPONSE_EVENT_PREFIX + msgId;
}

function createRequest(socket) {
  return async ({ query, vars }) =>
    new Promise((resolve, reject) => {
      try {
        const messageId = nextMessageId();
        const resEventName = responseEventName(messageId);
        socket.once(
          resEventName,
          timeoutCallback(RES_TIMEOUT, (error, data) => {
            if (error) {
              socket.off(resEventName);
              reject(error);
              return;
            }
            try {
              checkForGraphQlErrors(messageId, data);
              resolve(data);
            } catch (graphQlError) {
              reject(graphQlError);
            }
          }),
        );
        socket.emit('message', { mId: resEventName, query, vars });
      } catch (error) {
        reject(error);
      }
    });
}

async function getSocketIoConnection({
  socketIoUrl,
  gameSlug,
  transports,
  forceNew,
}) {
  let connected = false;
  const socket = lookup(`wss://${socketIoUrl}`, {
    autoConnect: false,
    reconnection: false,
    withCredentials: true,
    query: { gameId: gameSlug },
    transports,
    forceNew,
  });

  socket.on('disconnect', (reason) => {
    connected = false;
    onClose.emit(reason);
  });

  const conn = new Promise((resolve, reject) => {
    socket.on('connect', () => {
      connected = true;
      onOpen.emit();
      resolve();
    });
    socket.on('connect_error', (error) => {
      onConnectionError.emit(error);
      reject(error);
    });
  });

  socket.connect();
  await conn;

  return {
    request: createRequest(socket),
    close: () => {
      socket.disconnect();
    },
    isConnected: () => connected,
  };
}

function isBoolean(value) {
  return typeof value === 'boolean';
}

const log$3 = getLog$2('connection');
const THIRD_PARTY_COOKIE_ERROR_MESSAGE = 'xhr poll error';
const LONG_POLLING = 'polling';
const WEBSOCKETS = 'websocket';
const CLIENT_ID = v4();

let transports = [WEBSOCKETS, LONG_POLLING];
let forceNew = false;
let gameSlug;
let credentials;

onOpen.on(() => {
  log$3.info('Connection opened');
  forceNew = false;
});

onLogin.on(() => {
  log$3.info('Logged in');
});

onConnectionError.on((error) => {
  const { message } = error;
  log$3.notice(`Connection error: "${message}"`, error);
  if (message === THIRD_PARTY_COOKIE_ERROR_MESSAGE) {
    transports = [WEBSOCKETS];
    log$3.notice(
      `Can't have sticky sessions due to not allowed third-party cookies on the device. Will only allow WebSocket connections from here on.`,
    );
    forceNew = true;
  }
  clearConnection();
});

onClose.on((reason) => {
  log$3.notice(`Connection lost: "${reason}"`);
  clearConnection();
});

function clearConnection() {
  resetConnection().catch((error) => {
    log$3.error('Failed to clear connection', error);
  });
}

const resetConnection = createSingleton(executeConnectionReset, true);

async function executeConnectionReset() {
  if (getConnection.executionSuccessful()) {
    const { isConnected } = await getConnection();
    if (isConnected()) {
      await saveDisconnect();
    }
  }
  if (!getConnection.inProgress()) {
    getConnection.reset();
  }
  if (!getAuthenticatedConnection.inProgress()) {
    getAuthenticatedConnection.reset();
  }
  log$3.notice(`Connection cleared.`);
}

function setGameSlug(newGameSlug) {
  if (!isNonEmptyString$3(newGameSlug)) {
    throw new GBCErrorValidationFailed(
      'Provided gameSlug (fka gameId) must be a non-empty string',
      'solitaire-tripeaks',
    );
  }
  if (gameSlug && gameSlug !== newGameSlug) {
    throw new GBCInvalidCallError(`gameSlug was already set to "${gameSlug}"`);
  }
  if (gameSlug) {
    return;
  }
  initWithConfig({ gameId: newGameSlug });
  gameSlug = newGameSlug;
}

const getConnection = createSingleton(createConnection);

async function createConnection() {
  if (!gameSlug) {
    throw new GBCInvalidCallError(
      `We can not establish a connection without gameSlug. Please execute login or register before running a query`,
    );
  }
  const socketIoUrl = getConnectionUrl();
  try {
    return await getSocketIoConnection({
      socketIoUrl,
      gameSlug,
      transports,
      forceNew,
    });
  } catch (error) {
    const { message } = error;
    if (message !== THIRD_PARTY_COOKIE_ERROR_MESSAGE) {
      throw error;
    }
    throw new GBCEnvironmentError(
      1644322468,
      "Can't have sticky sessions due to not allowed third-party cookies on the device. Will only allow WebSocket connections from here on.",
      null,
      error,
    );
  }
}

const getAuthenticatedConnection = createSingleton(
  createAuthenticatedConnection,
);
async function createAuthenticatedConnection() {
  if (!credentials) {
    throw new GBCInvalidCallError(
      `We can not establish a authenticated connection without credentials. Please execute login before running a query`,
    );
  }
  const { userId, authToken, prioritiseLogin } = credentials;
  const connection = await getConnection();
  await connection.request({
    query: `
      mutation login($authToken: String!, $userId: ID!, $prioritiseLogin: Boolean, $trackingData: TrackingData) {
        login(authToken: $authToken, userId: $userId, prioritiseLogin: $prioritiseLogin, trackingData: $trackingData)
      }
    `,
    vars: {
      userId,
      authToken,
      prioritiseLogin,
      trackingData: {
        gameBackendClientVersion: VERSION$1,
        clientId: CLIENT_ID,
      },
    },
  });
  onLogin.emit();
  return connection;
}

async function login({ gameId, userId, authToken, prioritiseLogin }) {
  try {
    log$3.debug('Login started ...');
    setGameSlug(gameId);
    if (!isNonEmptyString$3(userId)) {
      throw new GBCErrorValidationFailed(
        'Provided userId must be a non-empty string',
        '123456',
      );
    }
    if (!isNonEmptyString$3(authToken)) {
      throw new GBCErrorValidationFailed(
        'Provided authToken must be a non-empty string',
        'dsa32s-ajdWfjLo32q',
      );
    }
    if (!isBoolean(prioritiseLogin)) {
      throw new GBCErrorValidationFailed(
        'Provided prioritiseLogin must be a boolean',
        true,
      );
    }
    if (getAuthenticatedConnection.inProgress()) {
      throw new GBCInvalidCallError(
        `Login while login in progress is not allowed`,
      );
    }
    if (getAuthenticatedConnection.executionSuccessful()) {
      throw new GBCInvalidCallError(
        `Login while there is already an authenticated connection is not allowed`,
      );
    }
    credentials = {
      userId,
      authToken,
      prioritiseLogin,
    };
    await getAuthenticatedConnection();
    log$3.debug('... logged in.');
  } catch (error) {
    log$3.debug('... login failed.', error);
    throw error;
  }
}

async function registerWithFb({ gameId, signature }) {
  try {
    log$3.debug('Registration started ...');
    if (getAuthenticatedConnection.inProgress()) {
      throw new GBCInvalidCallError(
        `Register while login in progress is not allowed`,
      );
    }
    if (getAuthenticatedConnection.executionSuccessful()) {
      throw new GBCInvalidCallError(
        `Register while there is already an authenticated connection is not allowed`,
      );
    }
    setGameSlug(gameId);
    if (!isString$3(signature) || !signature.trim()) {
      throw new GBCErrorValidationFailed(
        'Invalid "signature" parameter. "signature" should be not empty string',
        '567576%ghf3hg2fh67-hj4agj',
      );
    }
    const { request } = await getConnection();
    const { data: { registerFbInstant: { userId, authToken } = {} } = {} } =
      await request({
        query: `
        mutation registerFbIg($gameId: ID!, $signature: String!, $trackingData: TrackingData) {
          registerFbInstant(gameId: $gameId, signature: $signature, trackingData: $trackingData) {
            authToken
            userId
          }
        }
      `,
        vars: {
          gameId,
          signature,
          trackingData: {
            gameBackendClientVersion: VERSION$1,
            clientId: CLIENT_ID,
          },
        },
      });
    log$3.debug('... registered.');
    return {
      userId,
      authToken,
    };
  } catch (error) {
    log$3.debug('... registration failed.', error);
    throw error;
  }
}

async function getServerTime() {
  const { request } = await getConnection();
  const { data: { now } = {} } = await request({
    query: `
      query now {now}
    `,
  });
  return now;
}

async function executeQuery({ query, vars }) {
  const { request } = await getAuthenticatedConnection();
  return request({
    query,
    vars,
  });
}

async function authenticatedConnectionEstablished() {
  return getAuthenticatedConnection.executionSuccessful();
}

async function closeConnection() {
  await disconnect();
}

async function disconnect() {
  if (getConnection.isInitialized()) {
    const { close } = await getConnection();
    await close();
  }
}

async function saveDisconnect() {
  try {
    await disconnect();
  } catch (error) {
    log$3.debug('Failed to close connection', error);
  }
}

/**
 * @async
 * @function
 * @memberof module:base
 * @description
 * Please read the authentication tutorial.
 * Use this function to close the connection to the Game Backend
 *
 * @tutorial authentication
 * @example
 * import {close} from '@sgorg/game-backend-client';
 * async function closeConnection() {
 *   await close();
 * }
 *
 * @returns {Promise}
 */
const close$1 = async () => closeConnection();

const executeTest$1 = async () => {
  const { data: { test } = {} } = await executeQuery({
    query: `
      query test {
        test
      }
    `,
  });
  return test;
};

/**
 * @async
 * @function
 * @memberof module:base
 * @description
 * Use this function to execute previously created and registered scripts on the Game Backend.
 *
 * @example
 * import {executeRuntimeScript} from '@sgorg/game-backend-client';
 * async function executeScript(scriptRoute, args) {
 *   const result = await executeRuntimeScript({route: scriptRoute, args});
 *   console.log(result);
 *   // Prints what ever the script returned
 *   return result;
 * }
 *
 * @param {Object} options
 * @param {String} options.route Route to the script is defined with the registration of the script in the backend
 * @param {Object} [options.args] Arguments that might be required by the script on execution
 * @returns {Promise<mixed>} Script result
 *
 * @throws {GBCErrorNoConnection}
 * @throws {GBCErrorValidationFailed}
 *
 */
const executeRuntimeScript$1 = async ({ route, args } = {}) => {
  if (!isString$3(route) || !route.trim()) {
    throw new GBCErrorValidationFailed(
      'Invalid "route" parameter. "route" should be not empty string',
      'exec-gb-func',
    );
  }

  const { data: { executeRuntimeScript: result } = {} } = await executeQuery({
    query: `
      mutation executeRtScript($route: String!, $args: JSON) {
        executeRuntimeScript(route: $route, args: $args)
      }
    `,
    vars: {
      route,
      args,
    },
  });
  return result;
};

/**
 * @async
 * @function
 * @memberof module:base
 * @description
 * Use this function to fetch timestamp from GB (Server timestamp).
 *
 * @example
 * import {getServerTimestamp} from '@sgorg/game-backend-client';
 * async function fetchAndLofServerTimestamp() {
 *   const time = await getServerTimestamp();
 *   console.log(time);
 *   // returns 1554470890
 *   return time;
 * }
 *
 * @returns {Promise<number>} unix timestamp
 */
const getServerTimestamp$1 = async () => getServerTime();

/**
 * @async
 * @function
 * @memberof module:base
 * @description
 * Use this function to fetch the gameConfig from the GB
 *
 * @example
 * import {getGameConfig} from '@sgorg/game-backend-client';
 * async function getConfig() {
 *   const result = await getGameConfig();
 *   console.log(result);
 *   // Prints what ever the config is
 *   return result;
 * }
 *
 * @returns {Promise<Object>} Config
 *
 * @throws {GBCErrorNoConnection}
 *
 */
const getGameConfig$1 = async () => {
  const { data: { titleData } = {} } = await executeQuery({
    query: `
      query config {
        titleData
      }
    `,
  });
  return titleData;
};

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-07-19.
 * Copyright © Softgames 2019 - present
 */

/**
 * @function
 * @memberof module:base
 * @description
 * Return the semantic version of the current GB client
 *
 * @example
 * import {getVersion} from '@sgorg/game-backend-client';
 *
 * function printGbClientVersion() {
 *   console.log(`GB Client Version: v${getVersion()}`);
 * }
 *
 * @returns {String}
 */
const getVersion$1 = () => VERSION$1;

/**
 * @async
 * @function
 * @memberof module:base
 * @description
 * Convenient method to check if the user is currently connected and logged in to the game backend
 *
 * @example
 * import {isConnected} from '@sgorg/game-backend-client';
 *
 * async function checkConnection() {
 *   return isConnected();
 * }
 *
 * @returns {Promise<Boolean>}
 */
const isConnected$1 = async () => authenticatedConnectionEstablished();

function wrapParameterCheck(f) {
  return async (...args) => {
    if (args.length === 1 && args[0] === null) {
      throw new GBCErrorValidationFailed(
        'The first parameter of a function call must not be null',
        undefined,
      );
    }
    return f(...args);
  };
}

function createUUID() {
  // http://www.ietf.org/rfc/rfc4122.txt
  const s = [];
  const hexDigits = '0123456789abcdef';
  for (let i = 0; i < 36; i += 1) {
    s[i] = hexDigits.substr(Math.floor(Math.random() * 0x10), 1);
  }
  s[14] = '4'; // bits 12-15 of the time_hi_and_version field to 0010
  s[19] = hexDigits.substr((s[19] && 0x3) || 0x8, 1); // bits 6-7 of the clock_seq_hi_and_reserved to 01
  // eslint-disable-next-line no-multi-assign
  s[8] = s[13] = s[18] = s[23] = '-';

  return s.join('');
}

function isString$1(str) {
  return typeof str === 'string';
}

function isInteger(num) {
  return typeof num === 'number' && Number.isInteger(num);
}

function isNonEmptyString(str) {
  return !!str && isString$1(str) && !!str.trim();
}

function validateParams(params) {
  const invalidParams = [];
  // [GI] Regex to match cd[0-9]+ and cm[0-9]+
  const regex = /^c([dm])(\d+)$/;
  const validParams = Object.entries(params).reduce((acc, [key, value]) => {
    const match = key.match(regex);
    // Check if the number received in the match is less than the 200 GA limit
    if (match && match[2] && parseInt(match[2], 10) <= 200) {
      // If it is a custom dimension, the value should be a string
      if (match[1] === 'd' && isNonEmptyString(value))
        return { ...acc, [key]: value };
      // If it is a custom metric, the value should be an integer
      if (match[1] === 'm' && isInteger(value)) return { ...acc, [key]: value };
    }
    invalidParams.push(key);
    return acc;
  }, {});
  return { validParams, invalidParams };
}

// [GMI] I use require instead of import for axios here because of
// https://stackoverflow.com/questions/38453307/axios-with-node-js-referenceerror-xmlhttprequest-is-not-defined
// If you find a way to use import but make the build process use 'node_modules/axios/lib/axios.js'instead of
// 'node_modules\axios\dist\axios.js', feel free to implement
const axios$4 = require('axios');

function createAxios({ baseUrl }) {
  const client = axios$4.create({ baseURL: baseUrl });
  return async ({ method, path, body, params, headers }) => {
    try {
      const { data } = await client.request({
        method,
        url: path,
        data: body,
        params,
        headers,
      });
      return data;
    } catch (error) {
      const { message, response, request } = error;
      if (response) {
        const {
          status = 666,
          data: { message: serverMessage = 'Unknown server response' } = {},
        } = response;
        const { responseURL: targetUrl = path } = request || {};
        throw new Error(
          `Server ${targetUrl} responded with ${status} - ${serverMessage}`,
        );
      } else if (request) {
        throw new Error('No server response');
      } else {
        throw new Error(`Failed to execute request: ${message} - ${error}`);
      }
    }
  };
}

/**
 * Created by René Simon <rene.simon@softgames.de> on 2020-01-17.
 * Copyright © Softgames 2020 - present
 */

/**
 * @memberof module:base
 * @constant SEVERITY
 * @type {Object}
 * @property {Number} DEBUG - -1
 * @property {Number} INFO - 0
 * @property {Number} NOTICE - 1
 * @property {Number} WARN - 2
 * @property {Number} ERROR - 3
 * @property {Number} FATAL - 4
 * @property {Number} DISABLE - 5
 */
const SEVERITY = {
  DEBUG: -1,
  INFO: 0,
  NOTICE: 1,
  WARN: 2,
  ERROR: 3,
  FATAL: 4,
  DISABLE: 5,
};

const allowedSeverities$1 = Object.values(SEVERITY);

function isValidSeverity(severity) {
  return allowedSeverities$1.includes(severity);
}

function isString$1$1(str) {
  return typeof str === 'string';
}

function isNonEmptyString$1(str) {
  return !!str && isString$1$1(str) && !!str.trim();
}

/**
 * Created by René Simon <rene.simon@softgames.de> on 2020-01-17.
 * Copyright © Softgames 2020 - present
 */

const SEVERITY$1 = SEVERITY;

let NAME = null;

const consoleExists =
  typeof console !== 'undefined' &&
  !!console &&
  typeof console.log === 'function';

function getLogFunction(target, ...alternatives) {
  if (typeof console[target] === 'function') {
    return console[target].bind(console);
  }
  if (!alternatives || !alternatives.length) {
    return console.log.bind(console);
  }
  return getLogFunction(...alternatives);
}

function getLog(logLevel) {
  return {
    log:
      !consoleExists || logLevel > SEVERITY$1.INFO
        ? () => {}
        : getLogFunction('info'),
    debug:
      !consoleExists || logLevel > SEVERITY$1.DEBUG
        ? () => {}
        : getLogFunction('debug'),
    info:
      !consoleExists || logLevel > SEVERITY$1.INFO
        ? () => {}
        : getLogFunction('info'),
    notice:
      !consoleExists || logLevel > SEVERITY$1.NOTICE
        ? () => {}
        : getLogFunction('notice', 'info'),
    warn:
      !consoleExists || logLevel > SEVERITY$1.WARN
        ? () => {}
        : getLogFunction('warn', 'notice', 'info'),
    error:
      !consoleExists || logLevel > SEVERITY$1.ERROR
        ? () => {}
        : getLogFunction('error'),
    fatal:
      !consoleExists || logLevel > SEVERITY$1.FATAL
        ? () => {}
        : getLogFunction('fatal', 'error'),
  };
}

let currentLogger = getLog(SEVERITY$1.DISABLE);

/**
 * @memberof module:base
 * @function setLogSeverity
 * @description
 * Sets the  project's severity to log.
 * The given severity and all higher severities are logged.
 *
 * @param {Object} options
 * @param {Number} options.severity - Severity of type SEVERITY
 * @example
 * import { setLogSeverity, SEVERITY } from '@sgorg/fe-logger';
 *
 * setLogSeverity({ severity: SEVERITY.WARN });
 * // After this all warn, error and fatal logs are printed in the console.
 *
 * @returns {void}
 */
function setLogSeverity({ severity } = {}) {
  if (!isValidSeverity(severity)) {
    throw new Error(
      `Severity must be on of ${allowedSeverities$1.join(
        ', ',
      )}. Suggest to use SEVERITY enum.`,
    );
  }
  currentLogger = getLog(severity);
}

/**
 * @memberof module:base
 * @function init
 * @description
 * Initializes all loggers for the project.
 * Sets the project name and the project's severity to log (see also setLogSeverity).
 *
 * @param {Object} options
 * @param {String} options.name - Name of the logger
 * @param {Number} options.severity - Severity of type SEVERITY
 * @example
 * import { init, SEVERITY } from '@sgorg/fe-logger';
 *
 * init({ name: 'client', severity: SEVERITY.DEBUG });
 *
 * @returns {void}
 */
function init({ name, severity = SEVERITY$1.DISABLE } = {}) {
  if (!isNonEmptyString$1(name)) {
    throw new Error(`Name must be a non empty string`);
  }
  setLogSeverity({ severity });
  NAME = name;
}

function getLogHandler(severity, name) {
  if (typeof currentLogger[severity] !== 'function') {
    throw new Error(
      `There is no log method with name "${severity}" for generating logger "${name}"`,
    );
  }
  return (...args) => {
    try {
      currentLogger[severity](`[${NAME}][${severity}]${name}`, ...args);
    } catch (error) {
      // [RS] Executing a log never generates an error
    }
  };
}

/**
 * @memberof module:base
 * @function getLogger
 * @description
 * Creates a new logger instance. By default all logs are disabled. Check init or setLogSeverity to change that.
 *
 * @param {Object} options
 * @param {String} options.name - Name of the logger
 * @example
 * import { getLogger } from '@sgorg/fe-logger';
 *
 * const log = getLogger({ name: 'utils' });
 *
 * @returns {module:base.Logger}
 */
function getLogger({ name } = {}) {
  if (!isNonEmptyString$1(name)) {
    throw new Error(`Name must be non-empty string`);
  }
  return Object.keys(currentLogger).reduce(
    (m, severity) => ({
      ...m,
      [severity]: getLogHandler(severity, name),
    }),
    {},
  );
}

init({
  name: 'GA-Client',
  severity: SEVERITY$1.INFO,
});

function getLog$1(name) {
  return getLogger({ name });
}

const log$2 = getLog$1('main');

/**
 * @typedef {Object} Client
 * @property {function} trackEvent
 */

/**
 * @function createClient
 * @description
 * Initialize the Google Analytics client
 *
 * @param {Object} clientOptions
 * @param {String} clientOptions.gaTrackingId - Tracking ID of the Google Analytics property.
 * @param {String} [clientOptions.clientID] - Unique identifier for the client.
 * @example
 * import { createClient } from '@sgorg/ga-client';
 *
 * const client = createClient({gaTrackingId: 'UA-XXXXX-Y'})
 * @returns {Client}
 */
const createClient = (clientOptions) => {
  try {
    const { gaTrackingId, clientID } = clientOptions || {};
    const clientId = clientID || createUUID();
    const axiosClient = createAxios({
      baseUrl: 'https://www.google-analytics.com',
    });

    const tracking = async (options) => {
      try {
        const { category, action, label, value, ...customParams } =
          options || {};
        if (!isNonEmptyString(category)) {
          throw new Error(
            'Invalid tracking data: "category" must be a non empty string',
          );
        }
        if (!isNonEmptyString(action)) {
          throw new Error(
            'Invalid tracking data: "action" must be a non empty string',
          );
        }
        if (label && !isNonEmptyString(label)) {
          throw new Error(
            'Invalid tracking data: "label" must be a non empty string',
          );
        }
        if (value && !isInteger(value)) {
          throw new Error(
            'Invalid tracking data: "value" must be an integer number',
          );
        }
        const { validParams, invalidParams } = validateParams(customParams);
        if (invalidParams.length) {
          log$2.notice(
            `The following properties are not allowed as params or have invalid value: ${invalidParams.join(
              ', ',
            )}`,
          );
        }

        const requestOptions = {
          method: 'post',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
          },
          path: '/collect',
          params: {
            // API Version.
            v: '1',
            // Tracking ID / Property ID.
            tid: gaTrackingId,
            // Anonymous Client Identifier. Ideally, this should be a UUID that
            // is associated with particular user, device, or browser instance.
            cid: clientId,
            // Event hit type.
            t: 'event',
            // Event category. Can be any string.
            ec: category,
            // Event action. Can be any string.
            ea: action,
            // Event label. Can be any string. Optional.
            ...(label && { el: label }),
            // Event value. Can be any number. Optional.
            ...(value && { ev: value }),
            // Optional and custom parameters.
            ...validParams,
          },
        };
        await axiosClient(requestOptions);
      } catch (error) {
        throw new Error(
          `Error sending your request to Google: ${error.toString()}`,
        );
      }
    };

    return {
      /**
       * @function trackEvent
       * @description
       * Send Track Event to Google Analytics.
       *
       * @param {Object} options
       * @param {String} options.category - Category of the event.
       * @param {String} options.action - Action of the event.
       * @param {String} [options.label] - Label of the event.
       * @param {Number} [options.value] - Value of the event.
       * @param {Number|String} [options.*] - Custom Metrics and Custom Dimensions, with the index e.g. cm2, cd1.
       * @example
       * const trackOptions = {
       *  category: 'testCategory',
       *  action: 'testAction',
       * }
       *
       * client.trackEvent(trackOptions);
       * @returns {Client}
       */
      trackEvent(options) {
        tracking(options)
          .then(() => log$2.debug('Event successfully tracked.'))
          .catch((error) =>
            log$2.notice(`Tracking event failed: ${error.toString()}`),
          );
      },
    };
  } catch (error) {
    log$2.warn(`Error creating client: ${error.toString()}`);
    return {
      trackEvent: () =>
        log$2.notice(
          'There was an error creating the client, therefore your event will not be tracked.',
        ),
    };
  }
};

const log$1 = getLog$2(`gaTracker`);

let trackingEnabled = false;

const getGaClient = createSingleton(() => {
  if (!isNonEmptyString$3(GA_TRACKING_ID)) {
    throw new GBCInternalError('Google Analytics tracking ID must be defined.');
  }
  return createClient({
    gaTrackingId: GA_TRACKING_ID,
  });
});

function setTrackingEnabled(enable) {
  trackingEnabled = !!enable;
}

function isTrackingEnabled() {
  return trackingEnabled;
}

function gaTracker({ category, action, label }) {
  try {
    if (!isTrackingEnabled()) {
      return;
    }
    const gaClient = getGaClient();
    const trackReturn = gaClient.trackEvent({
      category,
      action,
      label,
    });
    if (trackReturn instanceof Promise) {
      trackReturn.catch((error) => {
        log$1.error(`Failed to track event async`, error);
      });
    }
  } catch (error) {
    log$1.error(`Failed to track event`, error);
  }
}

const log = getLog$2('durationTracker');

function getDurationTacker({ category, gameId }) {
  try {
    if (!isTrackingEnabled()) {
      return () => {};
    }
    if (!isNonEmptyString$3(gameId)) {
      throw new GBCInternalError(`"gameId" must be a string`);
    }
    const trackingBeginTime = Date.now();
    return () => {
      try {
        const trackingDuration = Date.now() - trackingBeginTime;
        const trackOptions = {
          category,
          action: gameId,
          label: castDurationToMsGroupLabel(trackingDuration),
        };
        log.debug(`Try to track`, trackOptions);
        gaTracker(trackOptions);
      } catch (trackingError) {
        log.error(`Failed to track event`, trackingError);
      }
    };
  } catch (error) {
    log.error(`Failed to get tracker`, error);
    return () => {};
  }
}

function wrapDurationTracking(func, { category } = {}) {
  if (!isNonEmptyString$3(category)) {
    throw new GBCInternalError(`"category" must be a string`);
  }
  if (!isFunction$1(func)) {
    throw new GBCInternalError(`"func" must be a function`);
  }
  return async (...args) => {
    const [{ gameId } = {}] = args || [];
    const track = getDurationTacker({ category, gameId });
    const result = await func(...args);
    track();
    return result;
  };
}

const WRONG_CREDENTIALS_ERROR_CODES = [
  ERROR_CODE.NOT_FOUND,
  ERROR_CODE.NOT_AUTHORIZED,
];

/**
 * @async
 * @function
 * @memberof module:base
 * @description
 * Use this function to login into the game backend with the credentials you received via the register* functions.
 *
 * @example
 * import { loginWithGameBackendCredentials } from '@sgorg/game-backend-client';
 * async function loginWithCredentialsAndLog(){
 *   await loginWithGameBackendCredentials({gameId: 'bb', userId:'1233',authToken:'sdfnsndb2377892rhj3rhf834'})
 *   console.log('Connection established and logged in');
 * }
 *
 * @param {Object} options
 * @param {String} options.gameId game id
 * @param {String} options.userId user id
 * @param {String} options.authToken auth token
 * @param {Boolean} [options.prioritiseLogin=false] auth token
 * @returns {Promise}
 *
 * @throws {GBCErrorValidationFailed}
 * @throws {GBCErrorNoConnection}
 * @throws {GBCInvalidCallError}
 * @throws {GBCWrongCredentialsError}
 * @throws {GBCKnownGraphQLError}
 * @throws {GBCUnknownGraphQLError}
 */
const loginWithGameBackendCredentials$1 = wrapParameterCheck(
  wrapDurationTracking(
    async ({ gameId, userId, authToken, prioritiseLogin = false }) => {
      try {
        await login({ gameId, userId, authToken, prioritiseLogin });
      } catch (error) {
        if (!error.code) {
          throw new GBCUnknownError(
            `Failed to login due to unknown error`,
            error,
          );
        }
        if (!WRONG_CREDENTIALS_ERROR_CODES.includes(error.code)) {
          throw error;
        }
        throw new GBCWrongCredentialsError(
          `Please try to register again, to receive new credentials`,
          error,
        );
      }
    },
    {
      category: 'gameClientLoginDuration',
    },
  ),
);

/**
 * @async
 * @function
 * @memberof module:base
 * @description
 * Use this function to register user into GB. This function can be used only for users from Facebook platform.
 * Result of this function later on can be used to login via GB credentials.
 *
 * @example
 * import {registerFacebookInstantGameSignature} from '@sgorg/game-backend-client';
 *
 * async function register({gameId}) {
 *   const playerInfo = await window.FBInstant.player.getSignedPlayerInfoAsync();
 *   const signature = playerInfo.getSignature();
 *   const {userId, authToken} = await registerFacebookInstantGameSignature({gameId, signature});
 *   console.log({userId, authToken});
 *   //  {
 *   //    userId: '123',
 *   //    authToken: '12jfd85nas7123nfgsadu12',
 *   //  }
 *   return {userId, authToken}; // This data can be stored away and than used for direct login the next visit of the  user
 * }
 *
 * @param {Object} options
 * @param {String} options.gameId
 * @param {String} options.signature
 * @return {Promise<Credentials>}
 *
 * @throws {GBCErrorNoConnection}
 * @throws {GBCErrorValidationFailed}
 */
const registerWithFacebookInstantGameSignature$1 = wrapParameterCheck(
  wrapDurationTracking(
    async ({ gameId, signature }) => registerWithFb({ gameId, signature }),
    {
      category: 'gameClientRegisterDuration',
    },
  ),
);

/**
 * @async
 * @function
 * @memberof module:base
 * @description
 * Use this function to enable or to disable Google Analytics Tracking
 *
 * @example
 * import { setGaTracking } from '@sgorg/game-backend-client';
 * async function enableGaTracking(){
 *   setGaTracking({ enable: true });
 * }
 *
 * @param {Object} options
 * @param {Boolean} options.enable Enable Google Analytics Tracking
 * @returns {Promise}
 */
const setGaTracking$1 = ({ enable } = {}) => {
  if (!isBoolean(enable)) {
    throw new GBCErrorValidationFailed(`enable must be a boolean.`, true);
  }
  setTrackingEnabled(enable);
};

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-17.
 * Copyright © Softgames 2019 - present
 */

const allowedSeverities = Object.values(SEVERITY$3);

/**
 * @async
 * @function
 * @memberof module:base
 * @description
 * Use this function to set the logLevel of the GameBackend client
 *
 * @example
 * import { setLogLevel } from '@sgorg/game-backend-client';
 * async function changeLogLevel(severity){
 *   await setLogLevel({severity})
 *   console.log(`Set logLevel to ${severity}`);
 * }
 *
 * @param {Object} options
 * @param {Number} options.severity Integer between -1 and 5. 5 effectively disables logging.
 * @returns {Promise}
 */
const setLogLevel$1 = wrapParameterCheck(async ({ severity }) => {
  if (!allowedSeverities.includes(severity)) {
    throw new GBCErrorValidationFailed(
      `severity must be on of the following integers "${allowedSeverities.join(
        '", "',
      )}".`,
      allowedSeverities[0],
    );
  }
  setLogLevel$2(severity);
});

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-01.
 * Created by Daniel Pochert <daniel.pochert@softgames.de> on 2019-04-01.
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-04-01.
 * Created by Benhur Davies <benhur.davies@softgames.de> on 2019-04-01.
 * Copyright © Softgames 2019 - present
 */

const close = wrapMethod('close', close$1);
const executeTest = wrapMethod('executeTest', executeTest$1);
const executeRuntimeScript = wrapMethod(
  'executeRuntimeScript',
  executeRuntimeScript$1,
);
const getServerTimestamp = wrapMethod(
  'getServerTimestamp',
  getServerTimestamp$1,
);
const getGameConfig = wrapMethod('getGameConfig', getGameConfig$1);
const getVersion = wrapMethod('getVersion', getVersion$1, true);
const isConnected = wrapMethod('isConnected', isConnected$1);
const loginWithGameBackendCredentials = wrapMethod(
  'loginWithGameBackendCredentials',
  loginWithGameBackendCredentials$1,
  true,
);
const registerWithFacebookInstantGameSignature = wrapMethod(
  'registerWithFacebookInstantGameSignature',
  registerWithFacebookInstantGameSignature$1,
  true,
);
const setGaTracking = wrapMethod('setGaTracking', setGaTracking$1, true);

const setLogLevel = wrapMethod('setLogLevel', setLogLevel$1, true);

var base = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getServerTimestamp: getServerTimestamp,
  close: close,
  executeRuntimeScript: executeRuntimeScript,
  executeTest: executeTest,
  getGameConfig: getGameConfig,
  getVersion: getVersion,
  isConnected: isConnected,
  loginWithGameBackendCredentials: loginWithGameBackendCredentials,
  registerWithFacebookInstantGameSignature: registerWithFacebookInstantGameSignature,
  setGaTracking: setGaTracking,
  setLogLevel: setLogLevel
});

function isArray$1(value) {
  return Object.prototype.toString.call(value) === '[object Array]';
}

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-06-03.
 * Copyright © Softgames 2019 - present
 */

function validateCurrencyInputDataArray(currencies, allowEmpty = false) {
  if (!isArray$1(currencies)) {
    throw new GBCErrorValidationFailed(
      'Invalid "currencies" parameter. "currencies" must be a array of CurrencyInputData',
      [{ currencySlug: 'currency1', amount: 1 }],
    );
  }
  if (!allowEmpty && !currencies.length) {
    throw new GBCErrorValidationFailed(
      'Invalid "currencies" parameter. "currencies" must be a not empty array.',
      [{ currencySlug: 'currency1', amount: 1 }],
    );
  }
  if (
    currencies.find((currency) => {
      const { currencySlug, amount } = currency || {};
      if (!currencySlug || !isString$3(currencySlug) || !currencySlug.trim()) {
        return true;
      }
      return !amount || !isNumber$1(amount);
    })
  ) {
    throw new GBCErrorValidationFailed(
      'Invalid "currencies" parameter. "currencies" must be a not empty array of CurrencyInputData. At least one item does not follow the CurrencyInputData schema.',
      [{ currencySlug: 'currency1', amount: 1 }],
    );
  }
}

/**
 * @async
 * @function
 *
 * @description
 * With this function you can add or subtract one or multiple currencies to/of the current users wallet.
 * The input is an array of CurrencyInputData. Which is a plain object containing the currencySlug and the amount.
 *
 * For TYPESCRIPT: We provide the CurrencyInputData interface.
 *
 * @example
 * import {addCurrencies} from '@sgorg/game-backend-client';
 *
 * async function handleCurrencyExchange({removeCurrencyName, removeCurrencyAmount, addCurrencyName, addCurrencyAmount}) {
 *   return addCurrencies({items: [
 *       {itemSlug: removeCurrencyName, amount: removeCurrencyAmount > 0 ? -1 * removeCurrencyAmount : removeCurrencyAmount},
 *       {itemSlug: addCurrencyName, amount: addCurrencyAmount > 0 ? addCurrencyAmount : -1 * addCurrencyAmount},
 *   ]});
 * }
 *
 * @param {Object} options
 * @param {Array<CurrencyInputData>} options.currencies List of CurrencyInputData. Consisting of unique slug of the currency to change and the amount. The CurrencyInputData must have been created in GB via ADM
 * @return {Promise}
 * @memberof module:economy
 *
 * @throws {GBCErrorNoConnection}
 * @throws {GBCErrorValidationFailed}
 */
const addCurrencies$1 = async ({ currencies } = {}) => {
  validateCurrencyInputDataArray(currencies);
  await executeQuery({
    query: `
      mutation addCurrencies($currencies: [CurrencyInputData!]!) {
          addCurrencies(currencies: $currencies)
      }
    `,
    vars: {
      currencies,
    },
  });
};

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-06-03.
 * Copyright © Softgames 2019 - present
 */

function validateItemInputDataArray(items, allowEmpty = false) {
  if (!isArray$1(items)) {
    throw new GBCErrorValidationFailed(
      'Invalid "items" parameter. "items" must be a array of ItemInputData',
      [{ itemSlug: 'item1', amount: 1 }],
    );
  }
  if (!allowEmpty && !items.length) {
    throw new GBCErrorValidationFailed(
      'Invalid "items" parameter. "items" must be a not empty array.',
      [{ itemSlug: 'item1', amount: 1 }],
    );
  }
  if (
    items.find((item) => {
      const { itemSlug, amount } = item || {};
      if (!itemSlug || !isString$3(itemSlug) || !itemSlug.trim()) {
        return true;
      }
      return !amount || !isNumber$1(amount) || amount % 1 !== 0;
    })
  ) {
    throw new GBCErrorValidationFailed(
      'Invalid "items" parameter. "items" must be a not empty array of ItemInputData. At least one item does not follow the ItemInputData schema.',
      [{ itemSlug: 'item1', amount: 1 }],
    );
  }
}

/**
 * @async
 * @function
 *
 * @description
 * With this function you can add or subtract one or multiple items to/of the item pool of the current user.
 * The input is an array of ItemInputData. Which is a plain object containing the itemSlug and the amount.
 * The amount must be an integer.
 *
 * For TYPESCRIPT: We provide the ItemInputData interface.
 *
 * @example
 * import {addItems} from '@sgorg/game-backend-client';
 *
 * async function handleOneForOneItemExchange({removeItemName, addItemName}) {
 *   return addItems({items: [
 *       {itemSlug: removeItemName, amount: -1},
 *       {itemSlug: addItemName, amount: 1},
 *   ]});
 * }
 *
 * @param {Object} options
 * @param {Array<ItemInputData>} options.items List of ItemInputData. Consisting of unique slug of the item to change and the amount. The item must have been created in GB via ADM
 * @return {Promise}
 * @memberof module:economy
 *
 * @throws {GBCErrorNoConnection}
 * @throws {GBCErrorValidationFailed}
 */
const addItems$1 = async ({ items } = {}) => {
  validateItemInputDataArray(items);
  await executeQuery({
    query: `
      mutation addItems($items: [ItemInputData!]!) {
          addItems(items: $items)
      }
    `,
    vars: {
      items,
    },
  });
};

/**
 * @async
 * @function
 *
 * @description
 * This function fetches all in GB configured currencies for the current game
 *
 * @example
 * import {getCurrencyCatalog} from '@sgorg/game-backend-client';
 *
 * async function fetchAndLogCurrencies() {
 *   const currencies = await getCurrencyCatalog();
 *   console.log(currencies);
 *   // [{
 *   //      id: '1',
 *   //      slug: 'currency1',
 *   //      displayName: 'Fancy Currency 1',
 *   //      metaData: {
 *   //        someConfigured: "dataForTheCurrency"
 *   //      },
 *   //      initialBalance: 0,
 *   //      rechargePerDay: 0,
 *   //      rechargeMax: 0,
 *   // }]
 *   return currencies;
 * }
 *
 * @returns {Promise<Array<Currency>>}
 * @memberof module:economy
 *
 * @throws {GBCErrorNoConnection}
 */
const getCurrencyCatalog$1 = async () => {
  const { data: { currencies = [] } = {} } = await executeQuery({
    query: `
      query getCurrencies {
        currencies {
          id
          slug
          displayName
          metaData
          initialBalance
          rechargePerDay
          rechargeMax
        }
      }
    `,
  });
  return currencies;
};

/**
 * @async
 * @function
 *
 * @description
 * This function fetches all in GB configured items for this game
 *
 * @example
 * import {getItemCatalog} from '@sgorg/game-backend-client';
 *
 * async function fetchAndLogItems() {
 *   const items = await getItemCatalog();
 *   console.log(items);
 *   // [{
 *   //      id: '1',
 *   //      slug: 'item1',
 *   //      displayName: 'Fancy Item 1',
 *   //      metaData: {
 *   //        someConfigured: "dataForTheItem"
 *   //      }
 *   // }]
 *   return items;
 * }
 *
 * @returns {Promise<Array<Item>>}
 * @memberof module:economy
 *
 * @throws {GBCErrorNoConnection}
 */
const getItemCatalog$1 = async () => {
  const { data: { items = [] } = {} } = await executeQuery({
    query: `
      query getItems {
        items {
          id
          slug
          displayName
          metaData
        }
      }
    `,
  });
  return items;
};

/**
 * @async
 * @function
 *
 * @description
 * With this function you can exchange one or multiple items for one or multiple currency or the other way around.
 * You should prefer this function. To adding and subtracting with addItems and addCurrency, since with this function you have one call and it is transaction save.
 * The input is an array of ItemInputData and an array of CurrencyInputData. See addCurrency and addItems.
 *
 * For TYPESCRIPT: We provide the ItemInputData interface and the CurrencyInputData interface.
 *
 * @example
 * import {transaction} from '@sgorg/game-backend-client';
 *
 * async function doFancyExchange() {
 *   return transaction({
 *     items: [
 *       {itemSlug: 'item1', amount: -1},
 *       {itemSlug: 'item2', amount: 1},
 *     ],
 *     currencies: [
 *       {itemSlug: 'currency1', amount: 1.23},
 *       {itemSlug: 'currency2, amount: -1},
 *     ],
 *   });
 * }
 *
 * @param {Object} options
 * @param {Array<ItemInputData>} options.items List of ItemInputData. Consisting of unique slug of the item to change and the amount. The item must have been created in GB via ADM
 * @param {Array<CurrencyInputData>} options.currencies List of CurrencyInputData. Consisting of unique slug of the currency to change and the amount. The CurrencyInputData must have been created in GB via ADM
 * @return {Promise<Boolean>}
 * @memberof module:economy
 *
 * @throws {GBCErrorNoConnection}
 * @throws {GBCErrorValidationFailed}
 */
const transaction$1 = async ({ items = [], currencies = [] } = {}) => {
  validateItemInputDataArray(items, true);
  validateCurrencyInputDataArray(currencies, true);
  if (!items.length && !currencies.length) {
    throw new GBCErrorValidationFailed(
      'For executing a transaction at least on of items or currencies must contain one value',
    );
  }
  const { data: { transaction: result } = {} } = await executeQuery({
    query: `
      mutation transaction($items: [ItemInputData!], $currencies: [CurrencyInputData!]) {
          transaction(items: $items, currencies: $currencies)
      }
    `,
    vars: {
      items,
      currencies,
    },
  });
  return result;
};

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-06-03.
 * Copyright © Softgames 2019 - present
 */

const addCurrencies = wrapMethod('addCurrencies', addCurrencies$1);
const addItems = wrapMethod('addItems', addItems$1);
const getCurrencyCatalog = wrapMethod(
  'getCurrencyCatalog',
  getCurrencyCatalog$1,
);
const getItemCatalog = wrapMethod('getItemCatalog', getItemCatalog$1);
const transaction = wrapMethod('transaction', transaction$1);

var economy = /*#__PURE__*/Object.freeze({
  __proto__: null,
  addCurrencies: addCurrencies,
  addItems: addItems,
  getCurrencyCatalog: getCurrencyCatalog,
  getItemCatalog: getItemCatalog,
  transaction: transaction
});

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-10.
 * Copyright © Softgames 2019 - present
 */

function exposeEvent(event) {
  return {
    on: event.on.bind(event),
    once: event.once.bind(event),
  };
}

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-09.
 * Copyright © Softgames 2019 - present
 */

/**
 * @typedef {function} CloseEventListener
 * @param {Number} [code] is optional the closing code (default value 1000). https://tools.ietf.org/html/rfc6455#section-7.4.1
 * @param {String} [reason] is the optional reason that the socket is being closed. https://tools.ietf.org/html/rfc6455#section-7.1.6
 *
 * @memberof module:events
 */

/**
 * @constant closeEvent
 *
 * @description
 * Fired when the connection to the game backend is closed
 *
 * @example
 * import {closeEvent} from '@sgorg/game-backend-client';
 *
 * closeEvent.on((code, reason) => {
 *   console.log('Connection closed ', code, reason);
 * });
 *
 * @type {Event.<CloseEventListener>}
 * @memberof module:events
 */
const closeEvent = exposeEvent(onClose);

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-09.
 * Copyright © Softgames 2019 - present
 */

/**
 * @typedef {function} ErrorEventListener
 * @param {Error} error
 *
 * @memberof module:events
 */

/**
 * @description
 * This event will be trigger when an error happens with the GB connection.
 *
 * @constant errorEvent
 * @example
 * import {errorEvent} from '@sgorg/game-backend-client';
 *
 * errorEvent.on(error => {
 *     console.log('The following error just happend', error);
 * });
 *
 * @type {Event.<ErrorEventListener>}
 * @memberof module:events
 */
const errorEvent = exposeEvent(onError);

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-09.
 * Copyright © Softgames 2019 - present
 */

/**
 * @description
 * This event will be trigger when the login to the game-backend finished successfully.
 *
 * @constant loginEvent
 * @example
 * import {loginEvent} from '@sgorg/game-backend-client';
 *
 * loginEvent.on(() => {
 *     console.log('A login to GB just happened.');
 * });
 *
 * @type {Event.<function>}
 * @memberof module:events
 */
const loginEvent = exposeEvent(onLogin);

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-09.
 * Copyright © Softgames 2019 - present
 */

/**
 * @typedef {function} MessageEventListener
 * @param {Message} message message object
 *
 * @memberof module:events
 */

/**
 * @description
 * Fired when a message is received from the GB.
 *
 * @constant messageEvent
 * @example
 * import {messageEvent} from '@sgorg/game-backend-client';
 *
 * messageEvent.on(message => {
 *   console.log('Message received', message);
 * });
 *
 * @type {Event.<MessageEventListener>}
 * @memberof module:events
 */
const messageEvent = exposeEvent(onMessage);

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-01.
 * Created by Daniel Pochert <daniel.pochert@softgames.de> on 2019-04-01.
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-04-01.
 * Created by Benhur Davies <benhur.davies@softgames.de> on 2019-04-01.
 * Copyright © Softgames 2019 - present
 */

var events = /*#__PURE__*/Object.freeze({
  __proto__: null,
  closeEvent: closeEvent,
  errorEvent: errorEvent,
  loginEvent: loginEvent,
  messageEvent: messageEvent
});

function checkLeaderboardName(leaderboardName) {
  if (!isNonEmptyString$3(leaderboardName)) {
    throw new GBCErrorValidationFailed(
      'Invalid "leaderboardName" parameter. "leaderboardName" should be not empty string',
      'main-leaderboard',
    );
  }
}

function checkLeaderboardInstanceId(instanceId) {
  if (!isNonEmptyString$3(instanceId)) {
    throw new GBCErrorValidationFailed(
      'Invalid "instanceId" parameter. "instanceId" should be not empty string',
      'default',
    );
  }
}

/**
 * @async
 * @function
 *
 * @description
 * Use this function to fetch the all entries of the specified leaderbaord instance for you and all of your friends.
 * PLEASE NOTE: This is based on the friends you synchronized via "syncFriendListByPlatformPlayerId".
 *
 * @example
 * import {getFriendsScores} from '@sgorg/game-backend-client';
 *
 * async function fetchAndLogScore() {
 *   const friendsEntries = await getFriendsScores({instanceId:'default',leaderboardName:'default'});
 *   console.log(friendScores);
 *   // [{rank: 1, score: 123, user: {id: 'abc', handle: 'Max', isCurrentUser: true, playerId: '1ea12'}}]
 *   return friendScores;
 * }
 *
 * // This data has to be decorated in most cases with an avatarUrl and a refreshed handle
 * // hear is an example for Facebook
 *
 * async function fetchAndLogScore() {
 *   const [friends, leaderboardEntries] = await Promise.all([
 *      FBInstant.player.getConnectedPlayersAsync(),
 *      getFriendsScores({instanceId:'default',leaderboardName:'default'}),
 *   ]);
 *   const usersByPlayerId = [
 *     castPlayerToUser(FBInstant.player),
 *     ...friends.map(castPlayerToUser),
 *   ].reduce((m, user) => {
 *       return {
 *           ...m,
 *           [user.id]: user,
 *       };
 *   }. {});
 *   const decoratedResults = leaderboardEntries.map(entry => {
 *       const {user: entryUser} = entry;
 *       const {playerId, handle} = entryUser;
 *       const {name, avatarUrl} = usersByPlayerId[playerId] || {};
 *       return {
 *           ...entry,
 *           user: {
 *               ...entryUser,
 *               handle: name || handle || 'N/A',
 *               avatarUrl,
 *           },
 *       };
 *   });
 *   console.log(decoratedResults);
 *   // [{
 *   //   rank: 1,
 *   //   score: 123,
 *   //   user: {
 *   //     id: 'abc',
 *   //     handle: 'Max',
 *   //     avatarUrl: 'http://www.fb.de/image.jpg',
 *   //     isCurrentUser: true,
 *   //     playerId: '1ea12',
 *   //   }
 *   // }]
 *   return decoratedResults;
 * }
 *
 * function castPlayerToUser(player) {
 *   return {
 *     id: player.getID(),
 *     name: player.getName(),
 *     avatarUrl: player.getPhoto(),
 *   };
 * }
 *
 * @param {Object} options
 * @param {String} options.instanceId Specify which instance of given leaderboard you want to get scores from.
 * @param {String} options.leaderboardName Specify the leaderboard
 * @returns {Promise<Array<LeaderboardEntry>>}
 * @memberof module:leaderboard
 *
 * @throws {GBCErrorNoConnection}
 * @throws {GBCErrorValidationFailed}
 */
const getFriendsScores$1 = async ({ instanceId, leaderboardName } = {}) => {
  checkLeaderboardName(leaderboardName);
  checkLeaderboardInstanceId(instanceId);

  const { data: { leaderboard: { friendsEntries } = {} } = {} } =
    await executeQuery({
      query: `
        query friendScores($leaderboardName: ID!, $instanceId: ID!){
          leaderboard(name: $leaderboardName) {
            friendsEntries(instanceId: $instanceId) {
              score
              rank
              user {
                id
                handle
                isCurrentUser
                playerId
              }
            }
          }
        }
      `,
      vars: {
        leaderboardName,
        instanceId,
      },
    });

  return friendsEntries;
};

/**
 * @typedef {Object} LeaderboardMetaData
 * @property {string} resetSchedule
 * @property {('BEST'|'SUM'|'SET')} aggregationMethod
 * @property {number} minScore
 * @property {number} maxScore
 * @property {boolean} ascending
 *
 * @memberof module:leaderboard
 */

/**
 * @async
 * @function
 *
 * @description
 * Use this function if you want to fetch the configuration of a specific leaderboard.
 *
 * @example
 * import { getLeaderboardMetaData } from '@sgorg/game-backend-client';
 * async fetchAndLogLeaderboard() {
 *   const leaderboardMeta = await getLeaderboardMetaData({leaderboardName:'default'});
 *   console.log(leaderboardMeta);
 *   // {
 *   //   resetSchedule: '0 0 1 * mon',
 *   //   aggregationMethod: 'BEST',
 *   //   minScore: 0,
 *   //   maxScore: 1000000,
 *   //   ascending: false
 *   // }
 *   return leaderboardMeta;
 * }
 *
 * @param {Object} options
 * @param {String} options.leaderboardName Specify the leaderboard
 * @returns {Promise<LeaderboardMetaData>} leader board object
 * @memberof module:leaderboard
 *
 * @throws {GBCErrorNoConnection}
 * @throws {GBCErrorValidationFailed}
 */
const getLeaderboardMetaData$1 = async ({ leaderboardName } = {}) => {
  checkLeaderboardName(leaderboardName);
  const { data: { leaderboard } = {} } = await executeQuery({
    query: `
      query friendScores($leaderboardName: ID!){
        leaderboard(name: $leaderboardName) {
          resetSchedule
          aggregationMethod
          minScore
          maxScore
          ascending
        }
      }
    `,
    vars: {
      leaderboardName,
    },
  });

  // TODO: fix after proper error from gb-game-server
  if (!leaderboard) throw new Error('No valid metadata result received');

  return leaderboard;
};

/**
 * @async
 * @function
 *
 * @description
 * Use this function to fetch the top scores of a leaderboard instance.
 *
 * @example
 * import { getTopScores } from '@sgorg/game-backend-client';
 * async function fetchAndLogTopScores() {
 *   const topScores = await getTopScores({leaderboardName:'default', instanceId:'default'});
 *   console.log(topScores);
 *   // [{rank: 1, score: 123, user : {id: 'abc', handle: 'Max', isCurrentUser: true, playerId: '1ea12'}}]
 *   return topScores;
 * }
 *
 * @param {Object} options
 * @param {String} options.leaderboardName  Specify the leaderboard.
 * @param {String} options.instanceId  Specify which instance of given leaderboard you want to get scores from.
 * @param {String} [options.limit] records limit
 * @param {String} [options.offset] offset
 * @returns {Promise<Array<LeaderboardEntry>>} top entries
 * @memberof module:leaderboard
 *
 * @throws {GBCErrorNoConnection}
 * @throws {GBCErrorValidationFailed}
 */
const getTopScores$1 = async ({
  leaderboardName,
  instanceId,
  limit = 100,
  offset = 0,
} = {}) => {
  checkLeaderboardName(leaderboardName);
  checkLeaderboardInstanceId(instanceId);

  const { data: { leaderboard: { topEntries } = {} } = {} } =
    await executeQuery({
      query: `
        query friendScores($leaderboardName: ID!, $instanceId: ID!, $limit: Int!, $offset: Int!){
          leaderboard(name: $leaderboardName) {
            topEntries(instanceId: $instanceId, limit: $limit, offset: $offset) {
              score
              rank
              user {
                id
                handle
                isCurrentUser
                playerId
              }
            }
          }
        }
      `,
      vars: {
        leaderboardName,
        limit,
        instanceId,
        offset,
      },
    });

  return topEntries;
};

function checkUserId(userId) {
  if (!isNonEmptyString$3(userId)) {
    throw new GBCErrorValidationFailed(
      'Invalid "userId" parameter. "userId" should be not empty string',
      '1687268',
    );
  }
}

/**
 * @async
 * @function
 *
 * @description
 * Use this function if you only want to get the current score of a specific user on a specific leaderboard instance
 *
 * @deprecated since version 4.3.0
 *
 * @example
 * import {getUsersEntry} from '@sgorg/game-backend-client';
 * async function fetchAndLogUsersEntry() {
 *   const user = await getUsersEntry({leaderboardName:'default',instanceId:'default',userId:'abcd123'});
 *   console.log(user);
 *   // { score: 17389, user: {id: 'abc', handle: 'Max', isCurrentUser: true, playerId: '1ea12'}}
 *   return user;
 * }
 *
 * @param {Object} options
 * @param {String} options.leaderboardName Specify the leaderboard.
 * @param {String} options.instanceId Specify which instance of given leaderboard you want to get scores from.
 * @param {String} options.userId user Id
 * @returns {Promise<UserWithScore>} users entry
 * @memberof module:leaderboard
 *
 * @throws {GBCErrorNoConnection}
 * @throws {GBCErrorValidationFailed}
 */
const getUsersEntry$1 = async ({ leaderboardName, instanceId, userId } = {}) => {
  checkLeaderboardName(leaderboardName);
  checkLeaderboardInstanceId(instanceId);
  checkUserId(userId);

  const { data: { leaderboard: { usersEntry } = {} } = {} } =
    await executeQuery({
      query: `
        query getUsersEntry($leaderboardName: ID!, $instanceId: ID!, $userId: ID!) {
          leaderboard(name: $leaderboardName) {
            usersEntry(
              userId: $userId
              instanceId: $instanceId
            ){
              score
              user {
                id
                handle
                isCurrentUser
              }
            }
          }
        }
      `,
      vars: {
        leaderboardName,
        instanceId,
        userId,
      },
    });

  return usersEntry;
};

function isValidArray(arr, validator) {
  if (!isFunction$1(validator)) {
    throw new Error('Provided validator must be a function');
  }
  if (!isArray$1(arr)) {
    return false;
  }
  return arr.findIndex((v) => !validator(v)) === -1;
}

/**
 * @async
 * @function
 *
 * @description
 * Use this function to fetch a leaderboard for a specific set of players from a specified leaderboard instance.
 * PLEASE NOTE: players is the list of playerIds not the GB user ids.
 *
 * @example
 * import {getUsersScores} from '@sgorg/game-backend-client';
 * async function fetchAndLogUsersScores() {
 *   const usersScore = await getUsersScores({leaderboardName:'default', instanceId: 'default', players: ['123kd2']});
 *   console.log(usersScore);
 *   // returns [{rank: 1, score: 123, user: {id: 'abc', handle: 'Max', isCurrentUser: true, playerId: '1ea12'}}]
 *   return usersScore;
 * }
 *
 * @param {Object} options
 * @param {String} options.leaderboardName Specify the leaderboard.
 * @param {String} options.instanceId Specify which instance of given leaderboard you want to get scores from.
 * @param {Array<String>} options.players players ids
 * @returns {Promise<Array<LeaderboardEntry>>}
 * @memberof module:leaderboard
 *
 * @throws {GBCErrorNoConnection}
 * @throws {GBCErrorValidationFailed}
 */
const getUsersScores$1 = async ({
  leaderboardName,
  instanceId,
  players,
} = {}) => {
  checkLeaderboardName(leaderboardName);
  checkLeaderboardInstanceId(instanceId);
  if (!isValidArray(players, isNonEmptyString$3)) {
    throw new GBCErrorValidationFailed(
      'Invalid "players" parameter. "players" should be array of users ids.',
      ['123456789', '987654321'],
    );
  }

  const { data: { leaderboard: { usersEntries } = {} } = {} } =
    await executeQuery({
      query: `
        query usersScores($leaderboardName: ID!, $instanceId: ID!, $players: [ID!]!){
          leaderboard(name: $leaderboardName) {
            usersEntries(instanceId: $instanceId, playerIds: $players) {
              score
              rank
              user {
                id
                handle
                isCurrentUser
                playerId
              }
            }
          }
        }
      `,
      vars: {
        leaderboardName,
        instanceId,
        players,
      },
    });

  // TODO: fix after proper error from gb-game-server
  if (!usersEntries) throw new Error('No valid getUsersScores result received');

  return usersEntries;
};

/**
 * @async
 * @function
 *
 * @description
 * Use this function if you only want to get the current score of a specific user on a specific leaderboard instance
 *
 * @example
 * import {getLeaderboardUserEntry} from '@sgorg/game-backend-client';
 * async function fetchAndLogUsersEntry() {
 *   const user = await getLeaderboardUserEntry({leaderboardName:'default',instanceId:'default',userId:'abcd123'});
 *   console.log(user);
 *   // { score: 17389, user: {id: 'abc', handle: 'Max', isCurrentUser: true, playerId: '1ea12'}}
 *   return user;
 * }
 *
 * @param {Object} options
 * @param {String} options.leaderboardName Specify the leaderboard.
 * @param {String} options.instanceId Specify which instance of given leaderboard you want to get scores from.
 * @param {String} options.userId user Id
 * @returns {Promise<UserWithScore>} users entry
 * @memberof module:leaderboard
 *
 * @throws {GBCErrorNoConnection}
 * @throws {GBCErrorValidationFailed}
 */
const getLeaderboardUserEntry$1 = async ({
  leaderboardName,
  instanceId,
  userId,
} = {}) => {
  checkLeaderboardName(leaderboardName);
  checkLeaderboardInstanceId(instanceId);
  checkUserId(userId);

  const { data: { leaderboard: { usersEntry } = {} } = {} } =
    await executeQuery({
      query: `
        query getLeaderboardUserEntry($leaderboardName: ID!, $instanceId: ID!, $userId: ID!) {
          leaderboard(name: $leaderboardName) {
            usersEntry(
              userId: $userId
              instanceId: $instanceId
            ){
              score
              user {
                id
                handle
                isCurrentUser
              }
            }
          }
        }
      `,
      vars: {
        leaderboardName,
        instanceId,
        userId,
      },
    });

  return usersEntry;
};

/**
 * @async
 * @function
 *
 * @description
 * Use this function to send the score of the current user to a specific leaderboard instance.
 *
 * @example
 * import {postScore} from '@sgorg/game-backend-client';
 * async function updateAndLogScore(){
 *   await postScore({leaderboardName: 'default', instanceId: 'default', score: 231});
 * }
 *
 * @param {Object} options
 * @param {String} options.leaderboardName Specify the leaderboard
 * @param {String} options.instanceId specify which instance of given leaderboard you want to get scores from.
 * @param {Number} options.score score to update
 * @return {Promise}
 * @memberof module:leaderboard
 *
 * @throws {GBCErrorNoConnection}
 * @throws {GBCErrorValidationFailed}
 */
const postScore$1 = async ({ leaderboardName, instanceId, score } = {}) => {
  checkLeaderboardName(leaderboardName);
  checkLeaderboardInstanceId(instanceId);
  if (!isNumber$1(score)) {
    throw new GBCErrorValidationFailed(
      'Invalid "score" parameter. "score" should be number',
      'default',
    );
  }
  await executeQuery({
    query: `
      mutation postScore($leaderboardName: ID!, $instanceId: ID!, $score: Float!){
        postScore(leaderboardName: $leaderboardName, instanceId: $instanceId, score: $score)
      }
    `,
    vars: {
      leaderboardName,
      instanceId,
      score,
    },
  });
};

/**
 * @async
 * @function
 *
 * @description
 * Use this function to remove score from all instances for a specific leaderboard.
 *
 * @example
 * import {removeLeaderboardScores} from '@sgorg/game-backend-client';
 * async function cleanupLeaderboardScores(){
 *   await removeLeaderboardScores({leaderboardName: 'default'});
 * }
 *
 * @param {Object} options
 * @param {String} options.leaderboardName Specify the leaderboard
 * @return {Promise}
 * @memberof module:leaderboard
 *
 * @throws {GBCErrorNoConnection}
 * @throws {GBCErrorValidationFailed}
 */
const removeLeaderboardScores$1 = async ({ leaderboardName } = {}) => {
  checkLeaderboardName(leaderboardName);

  await executeQuery({
    query: `
      mutation removeScore($leaderboardName: ID!){
        removeScore(leaderboardName: $leaderboardName)
      }
    `,
    vars: {
      leaderboardName,
    },
  });
};

/**
 * @async
 * @function
 *
 * @description
 * Use this function to remove score from a specific leaderboard instance.
 *
 * @example
 * import {removeLeaderboardInstanceScore} from '@sgorg/game-backend-client';
 * async function cleanupLeaderboardInstanceScore(){
 *   await removeLeaderboardInstanceScore({leaderboardName: 'default', instanceId: 'default'});
 * }
 *
 * @param {Object} options
 * @param {String} options.leaderboardName Specify the leaderboard
 * @param {String} options.instanceId Specify which instance of given leaderboard you want to remove score from.
 * @return {Promise}
 * @memberof module:leaderboard
 *
 * @throws {GBCErrorNoConnection}
 * @throws {GBCErrorValidationFailed}
 */
const removeLeaderboardInstanceScore$1 = async ({
  leaderboardName,
  instanceId,
} = {}) => {
  checkLeaderboardName(leaderboardName);
  checkLeaderboardInstanceId(instanceId);

  await executeQuery({
    query: `
      mutation removeScore($leaderboardName: ID!, $instanceId: ID){
        removeScore(leaderboardName: $leaderboardName, instanceId: $instanceId)
      }
    `,
    vars: {
      leaderboardName,
      instanceId,
    },
  });
};

/**
 * @async
 * @function
 *
 * @description
 * Use this function to subscribe or un-subscribe to leaderboards.
 * Per default all users are subscribed to leaderboards.
 * If a user un-subscribes to leaderboards all of his entries are deleted and to not show in the leaderboards anymore.
 * If a users re-subscribes he has to start over with new scores.
 *
 * @example
 * import { subscribeToLeaderboards } from '@sgorg/game-backend-client';
 *
 * async function unSubscribeToLeaderboards() {
 *   return subscribeToLeaderboards({subscribe: false});
 * }
 *
 * async function reSubscribeToLeaderboards() {
 *   return subscribeToLeaderboards({subscribe: true});
 * }
 *
 * @param {Object} options
 * @param {Boolean} options.subscribe Whether or not the user is subscribing (true) or un-subscribing (false)
 * @returns {Promise}
 * @memberof module:leaderboard
 *
 * @throws {GBCErrorNoConnection}
 * @throws {GBCErrorValidationFailed}
 */
const subscribeToLeaderboards$1 = async ({ subscribe } = {}) => {
  if (!isBoolean(subscribe)) {
    throw new GBCErrorValidationFailed(
      'Invalid "subscribe" parameter. "subscribe" must be a boolean',
      true,
    );
  }
  await executeQuery({
    query: `
      mutation subscribeToLeaderboards($subscribe: Boolean!){
        subscribeToLeaderboards(subscribe: $subscribe)
      }
    `,
    vars: {
      subscribe,
    },
  });
};

/**
 * Creates a unary function that invokes `func` with its argument transformed.
 *
 * @private
 * @param {Function} func The function to wrap.
 * @param {Function} transform The argument transform.
 * @returns {Function} Returns the new function.
 */

function overArg$1(func, transform) {
  return function(arg) {
    return func(transform(arg));
  };
}

var _overArg = overArg$1;

var overArg = _overArg;

/** Built-in value references. */
var getPrototype$1 = overArg(Object.getPrototypeOf, Object);

var _getPrototype = getPrototype$1;

var baseGetTag = _baseGetTag,
    getPrototype = _getPrototype,
    isObjectLike = isObjectLike_1;

/** `Object#toString` result references. */
var objectTag = '[object Object]';

/** Used for built-in method references. */
var funcProto = Function.prototype,
    objectProto = Object.prototype;

/** Used to resolve the decompiled source of functions. */
var funcToString = funcProto.toString;

/** Used to check objects for own properties. */
var hasOwnProperty = objectProto.hasOwnProperty;

/** Used to infer the `Object` constructor. */
var objectCtorString = funcToString.call(Object);

/**
 * Checks if `value` is a plain object, that is, an object created by the
 * `Object` constructor or one with a `[[Prototype]]` of `null`.
 *
 * @static
 * @memberOf _
 * @since 0.8.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a plain object, else `false`.
 * @example
 *
 * function Foo() {
 *   this.a = 1;
 * }
 *
 * _.isPlainObject(new Foo);
 * // => false
 *
 * _.isPlainObject([1, 2, 3]);
 * // => false
 *
 * _.isPlainObject({ 'x': 0, 'y': 0 });
 * // => true
 *
 * _.isPlainObject(Object.create(null));
 * // => true
 */
function isPlainObject$1(value) {
  if (!isObjectLike(value) || baseGetTag(value) != objectTag) {
    return false;
  }
  var proto = getPrototype(value);
  if (proto === null) {
    return true;
  }
  var Ctor = hasOwnProperty.call(proto, 'constructor') && proto.constructor;
  return typeof Ctor == 'function' && Ctor instanceof Ctor &&
    funcToString.call(Ctor) == objectCtorString;
}

var isPlainObject_1 = isPlainObject$1;

function isValidPlainObject(obj, validator) {
  if (!isFunction$1(validator)) {
    throw new Error('Provided validator must be a function');
  }
  if (!obj) {
    return false;
  }
  if (!isPlainObject_1(obj)) {
    return false;
  }
  return Object.values(obj).findIndex((v) => !validator(v)) === -1;
}

/**
 * @async
 * @function
 *
 * @description
 * Use this function to get the current user's friend list as it is in GB
 *
 * @example
 * import {getFriends} from '@sgorg/game-backend-client';
 *
 * async function getAndLogFriends() {
 *   const friends = await getFriends();
 *   console.log(friends);
 *   // [{
 *   //   id: '123',
 *   //   handle: 'Max Mustermann',
 *   //   locale: 'en-US',
 *   //   playerId: '123123312313'
 *   // }]
 *   return friends;
 * }
 *
 * @return {Promise<Array<Friend>>}
 * @memberof module:user
 *
 * @throws {GBCErrorNoConnection}
 */
const getFriends$1 = async () => {
  const { data: { me: { friendList = [] } = {} } = {} } = await executeQuery({
    query: `
      query getUserProfile {
        me {
          friendList {
            id
            handle
            locale
            playerId
          }
        }
      }
    `,
  });
  return friendList;
};

/**
 * @async
 * @function
 *
 * @description
 * This function fetches the amount of currency the current user has
 *
 * @example
 * import {getUserCurrencies} from '@sgorg/game-backend-client';
 *
 * async function fetchAndLogUserCurrencies() {
 *   const currencies = await getUserCurrencies();
 *   console.log(currencies);
 *   // [{
 *   //      id: '1',
 *   //      slug: 'currency1',
 *   //      displayName: 'Fancy Currency 1',
 *   //      balance: 112.23,
 *   // }]
 *   return currencies;
 * }
 *
 * @returns {Promise<Array<UserCurrency>>}
 * @memberof module:user
 *
 * @throws {GBCErrorNoConnection}
 */
const getUserCurrencies$1 = async () => {
  const { data: { me: { currencies = [] } = {} } = {} } = await executeQuery({
    query: `
      query userCurrencies {
        me {
          currencies {
            id
            slug
            displayName
            balance
          }
        }
      }
    `,
  });
  return currencies;
};

/**
 * @async
 * @function
 *
 * @description
 * This function fetches the values of the data of the current user for all keys.
 *
 * @example
 * import {getUserData} from '@sgorg/game-backend-client';
 * async function fetchAndLogUserData() {
 *   const userData = await getUserData();
 *   console.log(userData);
 *   // There is no log here because it is game specific
 *   return userData;
 * }
 *
 * @returns {Promise<Object>}
 * @memberof module:user
 *
 * @throws {GBCErrorNoConnection}
 */
const getUserData$1 = async () => {
  const { data: { me: { userData = {} } = {} } = {} } = await executeQuery({
    query: `
      query userData {
        me {
          userData
        }
      }
    `,
  });
  return userData;
};

/**
 * @async
 * @function
 *
 * @description
 * This function fetches the values of the data of the current user for provided keys.
 *
 * @example
 * import {getUserDataByKeys} from '@sgorg/game-backend-client';
 * async function fetchAndLogUserData() {
 *   const userData = await getUserDataByKeys(['key1', 'key2', 'key3', 'key4']);
 *   console.log(userData);
 *   // {
 *   //   key1: "String value of a key",
 *   //   key2: true,
 *   //   key3: 1890
 *   //   key4: {abc: 123, xyz: 987},
 *   // }
 *   return userData;
 * }
 *
 * @param {Object} options
 * @param {Object} options.keys keys to retrive
 * @returns {Promise<Object>}
 * @memberof module:user
 *
 * @throws {GBCBadResponseError}
 * @throws {GBCErrorNoConnection}
 */
const getUserDataByKeys$1 = async ({ keys }) => {
  const { data: { me: { userDataByKeys = {} } = {} } = {} } =
    await executeQuery({
      query: `
        query userDataByKeys($keys: [String!]!) {
          me {
            userDataByKeys(keys: $keys)
          }
        }
      `,
      vars: {
        keys,
      },
    });

  if (!isPlainObject_1(userDataByKeys)) {
    throw new GBCBadResponseError(
      `Response from GB is not a plain object. Provided type: ${typeof userDatabyKeys}`,
    );
  }
  return userDataByKeys;
};

/**
 * @async
 * @function
 *
 * @description
 * This function fetches the amount of items the current user has
 *
 * @example
 * import {getUserItems} from '@sgorg/game-backend-client';
 *
 * async function fetchAndLogUserItems() {
 *   const items = await getUserItems();
 *   console.log(items);
 *   // [{
 *   //      id: '1',
 *   //      slug: 'item1',
 *   //      displayName: 'Fancy Item 1',
 *   //      amount: 112,
 *   // }]
 *   return items;
 * }
 *
 * @returns {Promise<Array<UserItem>>}
 * @memberof module:user
 *
 * @throws {GBCErrorNoConnection}
 */
const getUserItems$1 = async () => {
  const { data: { me: { items = [] } = {} } = {} } = await executeQuery({
    query: `
      query userItems {
        me {
          items {
            id
            slug
            displayName
            amount
          }
        }
      }
    `,
  });
  return items;
};

/**
 * @async
 * @function
 *
 * @description
 * This function remove specified keys of the current user data.
 *
 * @example
 * import {removeUserDataByKeys} from '@sgorg/game-backend-client';
 * async function fetchAndLogUserData() {
 *   const result = await removeUserDataByKeys(['key1', 'key2']);
 *   console.log(userData);
 *   // {
 *   //   deleted: {
 *   //     key1: true,
 *   //     key2: true,
 *   //   }
 *   // }
 *   return result;
 * }
 *
 * @param {Object} options
 * @param {Object} options.keys keys to retrive
 * @returns {Promise<Object>}
 * @memberof module:user
 *
 * @throws {GBCBadResponseError}
 * @throws {GBCErrorNoConnection}
 */
const removeUserDataByKeys$1 = async (keys) => {
  const { data } = await executeQuery({
    query: `
      mutation removeUserData($keys: [String!]!) {
        removeUserData(keys: $keys)
      }
    `,
    vars: {
      keys,
    },
  });

  if (!isPlainObject_1(data)) {
    throw new GBCBadResponseError(
      `Response from GB is not a plain object. Provided type: ${typeof data}`,
    );
  }
  return data;
};

/**
 * @async
 * @function
 *
 * @description
 * With this function you can store data for the current user.
 * The given patch must be a plain object and will be shallow merged with the already existing data for the user in the GB.
 *
 * PLEASE NOTE:
 * a) Please use keys (not store the whole user state und one key)
 * b) Please check how often you save (there is no unlimited resources)
 * c) Maybe use timestamps and or versions for rollback scenarios
 *
 * @example
 * import {setUserData} from '@sgorg/game-backend-client';
 * async function setUserDataAndLog() {
 *   await setUserData({name:'Ben',id: 'sdfas2w123'})
 *     .then(()=>console.log('success'))
 *     .catch((e)=>console.log(e))
 * }
 *
 * @param {Object} options
 * @param {Object} options.patch patch object
 * @return {Promise}
 * @memberof module:user
 *
 * @throws {GBCErrorNoConnection}
 */
const setUserData$1 = async (patch) => {
  await executeQuery({
    query: `
      mutation setUserData($data: JSON!) {
        setUserData(patch: $data)
      }
    `,
    vars: {
      data: patch,
    },
  });
};

/**
 * @async
 * @function
 *
 * @description
 * Use this function to update user information in the GB.
 * Currently we store in the GB just the handle (Name) and the locale of the user.
 * It is recommended that you execute this on game start to keep the user profile on the servers up to date.
 *
 * @example
 * import {setUserProfile} from '@sgorg/game-backend-client';
 *
 * async function setUserProfileAndLog() {
 *   await setUserProfile({handle: 'max', locale: 'en'})
 *     .then(()=>console.log('success'))
 *     .catch((e)=>console.log('failed'))
 * }
 *
 * @param {Object} options
 * @param {String} options.handle username or nick name etc
 * @param {String} options.locale language code (EN)
 * @return {Promise}
 * @memberof module:user
 *
 * @throws {GBCErrorNoConnection}
 * @throws {GBCErrorValidationFailed}
 */
const setUserProfile$1 = async ({ handle, locale } = {}) => {
  if (!isString$3(handle)) {
    throw new GBCErrorValidationFailed(
      'Invalid "handle" parameter. "handle" should be string',
      'Mark Wolter',
    );
  }
  if (!isString$3(locale)) {
    throw new GBCErrorValidationFailed(
      'Invalid "locale" parameter. "locale" should be string',
      'de_DE',
    );
  }

  await executeQuery({
    query: `
      mutation setUserProfile($userProfile: UserProfileInput!) {
        setUserProfile(userProfile: $userProfile)
      }
    `,
    vars: {
      userProfile: {
        handle,
        locale,
      },
    },
  });
};

/**
 * @async
 * @function
 *
 *@description
 *Use this function to update the friends list of the current user on the GB.
 *This friends list is used to provide an easy and performant way
 *to retrieve friends scores of leaderboards.

 PLEASE NOTE:
 It is imperative that you evoke this function on game start,
 *if your game is using “getFriendsScores” or leaderboard related features.


 Why?

 The syncFriendListByPlatformPlayerId function sends an array of friend ids
 *received from Facebook or one of the other platforms to the gb-game-server.
 *There they get remapped to our internal platform agnostic ids and
 *then stored into a table, linking them to the current user’s id.
 *Now, when the getFriendsScores function is called, it can look up the
 *appropriate entries in this table (the ones that are linked to the current user’s id)
 *and then use the retrieved ids - which correspond to the current user’s
 *friends - to look up their scores.

 So if you want to use the getFriendsScores function, you have to sync the friend
 *list from your platform with the representation in the database beforehand and
 *that’s exactly what this function does. The best time to do this would be at
 *the start of the game. If you don’t do this, the server will NOT be able to fetch
 *the correct friend ids from the table and therefore the result of the
 *getFriendsScores/leaderboard function will be outdated or empty.


 * @example
 * import {syncFriendListByPlatformPlayerId} from '@sgorg/game-backend-client';
 * function syncFriendListByPlatformPlayerIdAndLog() {
 *   await syncFriendListByPlatformPlayerId({friendList:['f1','f2']});
 * }
 *
 * @param {Object} options
 * @param {Array<User>} options.friendList friend lists
 * @return {Promise}
 * @memberof module:user
 *
 * @throws {GBCErrorNoConnection}
 * @throws {GBCErrorValidationFailed}
 */
const syncFriendListByPlatformPlayerId$1 = async ({ friendList }) => {
  if (!isArray$1(friendList)) {
    throw new GBCErrorValidationFailed(
      'Invalid "friendList" parameter. "friendList" should be array of player ids',
      '["1312312", "36364563", "3180771]',
    );
  }
  await executeQuery({
    query: `
      mutation syncFriendListByPlatformPlayerId($friendList: [ID!]!) {
        syncFbInstantFriendList(friendList: $friendList)
      }
    `,
    vars: {
      friendList,
    },
  });
};

/**
 * @async
 * @function
 *
 * @description
 * Use this function to get the current user profile stored in GB
 *
 * @example
 * import {getUserProfile} from '@sgorg/game-backend-client';
 *
 * async function getUserAndLog() {
 *   const user = await getUserProfile();
 *   console.log(user);
 *   // {
 *   //   id: '123',
 *   //   handle: 'Max Mustermann',
 *   //   locale: 'en-US',
 *   //   subscribedToLeaderboards: true
 *   // }
 *   return user;
 * }
 *
 * @return {Promise<User>}
 * @memberof module:user
 *
 * @throws {GBCErrorNoConnection}
 */
const getUserProfile$1 = async () => {
  const {
    data: { me: { id, handle, locale, subscribedToLeaderboards } = {} } = {},
  } = await executeQuery({
    query: `
      query getUserProfile {
        me {
          id
          handle
          locale
          subscribedToLeaderboards
        }
      }
    `,
  });
  return {
    id,
    handle,
    locale,
    subscribedToLeaderboards,
  };
};

/**
 * @async
 * @function
 *
 * @description
 * This function fetches the publicly available user data for all given players and keys.
 *
 * In the Game Backends Game titleData must be configured which keys should be publicly available
 *
 * @example
 * import {getPublicUserData} from '@sgorg/game-backend-client';
 * async function fetchPublicUserData() {
 *   const dataByPlayerIds = await getPublicUserData({
 *     playerIds: ['123', '456'],
 *     keys: ['userDataA', 'blob.propertyA'],
 *   });
 *   console.log(dataByPlayerIds);
 *   // {
 *   //   '123': {
 *   //     'userDataA': 'someValue',
 *   //     'blob.propertyA': 69,
 *   //   },
 *   //   '456': {
 *   //     'userDataA': 'someOtherValue',
 *   //     'blob.propertyA': 96,
 *   //   },
 *   // }
 *   return userData;
 * }
 *
 * @param {Object} options
 * @param {Array<String>} options.playerIds List of playerIds you want to get the public data off
 * @param {Array<String>} options.keys List of user data keys. For data of plain objects paths are allowed key.propertyName.subPropertyName
 * @returns {Promise<Object>}
 * @memberof module:user
 *
 * @throws {GBCErrorNoConnection}
 */
const getPublicUserData$1 = async ({ playerIds, keys }) => {
  if (!isValidArray(playerIds, isNonEmptyString$3)) {
    throw new GBCErrorValidationFailed(
      'Invalid "playerIds" parameter. "playerIds" should be an array of non-empty strings',
      "['45544','21312']",
    );
  }
  if (!isValidArray(keys, isNonEmptyString$3)) {
    throw new GBCErrorValidationFailed(
      'Invalid "keys" parameter. "keys" should be an array of non-empty strings',
      "['dataKeyA','dataKeyB']",
    );
  }
  const { data: response } = await executeQuery({
    query: `
      query publicUserData($playerIds: [ID!]!, $keys: [String!]!) {
        publicUserData(playerIds: $playerIds, keys: $keys) {
          playerId
          data
        }
      }
    `,
    vars: { playerIds, keys },
  });
  const { publicUserData } = response || {};
  return (publicUserData || []).reduce(
    (m, { playerId, data }) => ({ ...m, [playerId]: data }),
    {},
  );
};

const getFriends = wrapMethod('getFriends', getFriends$1);
const getUserCurrencies = wrapMethod('getUserCurrencies', getUserCurrencies$1);
const getUserData = wrapMethod('getUserData', getUserData$1);
const getUserDataByKeys = wrapMethod('getUserDataByKeys', getUserDataByKeys$1);
const getUserItems = wrapMethod('getUserItems', getUserItems$1);
const removeUserDataByKeys = wrapMethod(
  'removeUserDataByKeys',
  removeUserDataByKeys$1,
);
const setUserData = wrapMethod('setUserData', setUserData$1);
const setUserProfile = wrapMethod('setUserProfile', setUserProfile$1);
const syncFriendListByPlatformPlayerId = wrapMethod(
  'syncFriendListByPlatformPlayerId',
  syncFriendListByPlatformPlayerId$1,
);
const getUserProfile = wrapMethod('getUserProfile', getUserProfile$1);
const getPublicUserData = wrapMethod('getPublicUserData', getPublicUserData$1);

var user = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getFriends: getFriends,
  getUserCurrencies: getUserCurrencies,
  getUserData: getUserData,
  getUserDataByKeys: getUserDataByKeys,
  getUserItems: getUserItems,
  removeUserDataByKeys: removeUserDataByKeys,
  setUserData: setUserData,
  setUserProfile: setUserProfile,
  syncFriendListByPlatformPlayerId: syncFriendListByPlatformPlayerId,
  getUserProfile: getUserProfile,
  getPublicUserData: getPublicUserData
});

/**
 * @async
 * @function
 *
 * @description
 * Use this function to create a post processor for the Leaderboard Client
 * to extend each leaderboard entry by data of public user data
 *
 * @example
 * import { getLeaderboardPostProcessorForUserData } from '@sgorg/game-backend-client';
 * import { createLeaderboardClient } from '@sgorg/leaderboard-client';
 *
 * // This example is based under following assumptions
 * // a) There is a public user data key called "user"
 * // b) It contains an object with the properties
 * // b1) name
 * // b2) avatarUrl
 * // c) It has the values for userId 1  { name: 'Max', avatarUrl: 'https://s3/1.jpg' }
 * // d) It has the values for userId 2  { name: 'Maxi', avatarUrl: 'https://s3/2.jpg' }
 *
 * const postProcessor = getLeaderboardPostProcessorForUserData({
 *   userDataMapping: {
 *     username: 'user.name'
 *     userAvatarUrl: 'user.avatarUrl'
 *   }
 * });
 * const ldbClient = createLeaderboardClient({
 *   gameId: 'gb-test-game',
 *   postProcessor,
 * });
 *
 * async function getLeaderboardEntriesByInstanceId({ currentPlayerId, leaderboardInstanceId }) {
 *   const entries = await ldbClient.getEntries({
 *     userId: currentPlayerId,
 *     leaderboardInstanceId,
 *   });
 *   console.log(entries);
 *   // [{
 *   //   rank: 1,
 *   //   score: 123,
 *   //   userId: '1',
 *   //   username: 'Max',
 *   //   userAvatarUrl: 'https://s3/1.jpg',
 *   // },{
 *   //   rank: 2,
 *   //   score: 122,
 *   //   userId: '2',
 *   //   username: 'Maxi',
 *   //   userAvatarUrl: 'https://s3/2.jpg',
 *   // }]
 *   return entries;
 * }
 *
 * @param {Object} options
 * @param {Object<string, string>} options.userDataMapping - Mapping key = key in every entry / value = key + path to data in public user data (see getPublicUserData)
 * @returns {function(Array<Object>): Promise<Array<Object>>}
 * @memberof module:leaderboard
 *
 * @throws {GBCErrorNoConnection}
 * @throws {GBCErrorValidationFailed}
 */
const getLeaderboardPostProcessorForUserData$1 = ({ userDataMapping }) => {
  if (!isValidPlainObject(userDataMapping, isNonEmptyString$3)) {
    throw new GBCErrorValidationFailed(
      'Invalid "userDataMapping" parameter. "userDataMapping" should be a plain-object where key and values must be a string.',
      `{username: 'user.name', avatar: 'user.avatarUrl'}`,
    );
  }
  const keys = Object.values(userDataMapping);
  const mappingEntries = Object.entries(userDataMapping);
  return wrapMethod('leaderboardPostProcessorForUserData', async (entries) => {
    try {
      const playerIds = entries.map(({ userId }) => userId);
      const data = await getPublicUserData({
        playerIds,
        keys,
      });
      return entries.map(({ userId, score, rank }) => {
        const userData = data[userId] || {};
        return {
          userId,
          score,
          rank,
          ...mappingEntries.reduce((m, [entryKey, dataKey]) => {
            m[entryKey] = userData[dataKey]; // eslint-disable-line no-param-reassign
            return m;
          }, {}),
        };
      });
    } catch (error) {
      throw new GBCUnknownError(
        'Error in GB client generated leaderboard post processor',
        error,
      );
    }
  });
};

const getFriendsScores = wrapMethod('getFriendsScores', getFriendsScores$1);
const getLeaderboardMetaData = wrapMethod(
  'getLeaderboardMetaData',
  getLeaderboardMetaData$1,
);
const getTopScores = wrapMethod('getTopScores', getTopScores$1);
const getUsersEntry = wrapMethod('getUsersEntry', getUsersEntry$1);
const getUsersScores = wrapMethod('getUsersScores', getUsersScores$1);
const getLeaderboardPostProcessorForUserData = wrapMethod(
  'getLeaderboardPostProcessorForUserData',
  getLeaderboardPostProcessorForUserData$1,
  true,
);
const getLeaderboardUserEntry = wrapMethod(
  'getLeaderboardUserEntry',
  getLeaderboardUserEntry$1,
);
const postScore = wrapMethod('postScore', postScore$1);
const removeLeaderboardScores = wrapMethod(
  'removeLeaderboardScores',
  removeLeaderboardScores$1,
);
const removeLeaderboardInstanceScore = wrapMethod(
  'removeLeaderboardInstanceScore',
  removeLeaderboardInstanceScore$1,
);
const subscribeToLeaderboards = wrapMethod(
  'subscribeToLeaderboards',
  subscribeToLeaderboards$1,
);

var leaderboard = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getFriendsScores: getFriendsScores,
  getLeaderboardMetaData: getLeaderboardMetaData,
  getTopScores: getTopScores,
  getUsersEntry: getUsersEntry,
  getUsersScores: getUsersScores,
  getLeaderboardUserEntry: getLeaderboardUserEntry,
  postScore: postScore,
  removeLeaderboardScores: removeLeaderboardScores,
  removeLeaderboardInstanceScore: removeLeaderboardInstanceScore,
  subscribeToLeaderboards: subscribeToLeaderboards,
  getLeaderboardPostProcessorForUserData: getLeaderboardPostProcessorForUserData
});

/**
 * @async
 * @function
 *
 * @description
 * Confirms that all messages are received and processed and can be deleted from the GB.
 *
 * @example
 * import {acknowledgeAllMessages} from '@sgorg/game-backend-client';
 * async function acknowledgeAllMessagesAndLogResult(){
 *   await acknowledgeAllMessages();
 * }
 *
 * @return {Promise}
 * @memberof module:messaging
 *
 * @throws {GBCErrorNoConnection}
 */
const acknowledgeAllMessages$1 = async () => {
  await executeQuery({
    query: `
      mutation acknowledgeAllMessages {
        acknowledgeAllPushMessages
      }
    `,
  });
};

/**
 * @async
 * @function
 *
 * @description
 * Conform that a specific message is received and processed and can be deleted from the GB.
 *
 * @example
 * import {acknowledgeMessage} from '@sgorg/game-backend-client';
 *
 * async function acknowledgeMessageAndLog() {
 *   await acknowledgeMessage({messageId:'someId2321'});
 * }
 *
 * @param {Object} options
 * @param {String} options.messageId message Id
 * @return {Promise}
 * @memberof module:messaging
 *
 * @throws {GBCErrorNoConnection}
 * @throws {GBCErrorValidationFailed}
 */
const acknowledgeMessage$1 = async ({ messageId } = {}) => {
  if (!isString$3(messageId) || !messageId.trim()) {
    throw new GBCErrorValidationFailed(
      'Invalid "messageId" parameter. "messageId" should be not empty string',
      '45544',
    );
  }
  await executeQuery({
    query: `
      mutation ackMessage($messageId: ID!){
        acknowledgePushMessage(messageId: $messageId)
      }
    `,
    vars: {
      messageId,
    },
  });
};

/**
 * @async
 * @function
 *
 * @description
 * Fetches all messages for the current user from the GB which are not confirmed yet.
 *
 * @example
 * import {fetchMessages} from '@sgorg/game-backend-client';
 * async function fetchAndLogMessages() {
 *   const messages = await fetchMessages();
 *   // [{id: '12abd32m', recipientUserId: '83jd83', senderUserId: '08sdj7d7', payload: {}}]
 *   return messages;
 * }
 *
 * @returns {Promise<Array<Message>>}
 * @memberof module:messaging
 *
 * @throws {GBCErrorNoConnection}
 */
const fetchMessages$1 = async () => {
  const { data: { me: { messages = [] } = {} } = {} } = await executeQuery({
    query: `
      query fetchMessages {
        me {
          messages {
            id
            recipientUserId
            senderUserId
            payload
          }
        }
      }
    `,
  });
  return messages;
};

/**
 * @async
 * @function
 *
 * @description
 * Sends a message to a specific user
 * The GB userId is required here, not the platform player id.
 * On success the message is stored in the user's mailbox until he acknowledges the message.
 * When in the response sendToBroadcast = true then the message was also send via our messaging system (based on AMQP)
 * directly to the user if the user was online at the time.
 * This is no indicator that the user received the message just that there was the chance if he was online.
 *
 * @example
 * import {sendMessage} from '@sgorg/game-backend-client';
 * async function sendMessageAndLog() {
 *   await sendMessage({userId: 'ca1x2d', payload: {}})
 *     .then(({sendToMailbox, sendToBroadcast})=> {
 *        if (sendToMailbox) {
 *            console.log(`Message was stored in the mailbox of the receiving user`);
 *        }
 *        if (sendToBroadcast) {
 *            console.log(`Message was broadcast, if the user was online at the time he got the message straight away`);
 *        }
 *     })
 *     .catch(e=>console.error('failed'));
 * }
 *
 * @param {Object} options
 * @param {Object} options.payload payload
 * @param {String} options.userId user Id
 * @return {Promise<{sendToMailbox: Boolean, sendToBroadcast: Boolean}>}
 * @memberof module:messaging
 *
 * @throws {GBCErrorNoConnection}
 * @throws {GBCErrorValidationFailed}
 */
const sendMessage$1 = async ({ userId, payload } = {}) => {
  checkUserId(userId);
  if (!isPlainObject_1(payload)) {
    throw new GBCErrorValidationFailed(
      'Invalid "payload" parameter. "payload" should be Object',
      '{"title": "Short title"}}',
    );
  }

  const {
    data: {
      sendPushMessage: { sendToMailbox = true, sendToBroadcast = true } = {},
    } = {},
  } = await executeQuery({
    query: `
      mutation sendMessage($userId: ID!, $payload: JSON!) {
        sendPushMessage(userId: $userId, payload: $payload)
      }
    `,
    vars: {
      userId,
      payload,
    },
  });
  return {
    sendToMailbox,
    sendToBroadcast,
  };
};

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-01.
 * Created by Daniel Pochert <daniel.pochert@softgames.de> on 2019-04-01.
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-04-01.
 * Created by Benhur Davies <benhur.davies@softgames.de> on 2019-04-01.
 * Copyright © Softgames 2019 - present
 */

const acknowledgeAllMessages = wrapMethod(
  'acknowledgeAllMessages',
  acknowledgeAllMessages$1,
);
const acknowledgeMessage = wrapMethod(
  'acknowledgeMessage',
  acknowledgeMessage$1,
);
const fetchMessages = wrapMethod('fetchMessages', fetchMessages$1);
const sendMessage = wrapMethod('sendMessage', sendMessage$1);

var messaging = /*#__PURE__*/Object.freeze({
  __proto__: null,
  acknowledgeAllMessages: acknowledgeAllMessages,
  acknowledgeMessage: acknowledgeMessage,
  fetchMessages: fetchMessages,
  sendMessage: sendMessage
});

/**
 * @async
 * @function
 *
 * @description
 * Fetches all notifications for the current user from the GB which are not acknowledged yet.
 *
 * @example
 * import { fetchNotifications } from '@sgorg/game-backend-client';
 * async function fetchAndLogNotifications() {
 *   const messages = await fetchNotifications();
 *   // [{id: '12abd32m', payload: {}}]
 *   return messages;
 * }
 *
 * @returns {Promise<Array<Notification>>}
 * @memberof module:notification
 *
 * @throws {GBCErrorNoConnection}
 */
const fetchNotifications$1 = async () => {
  const { data: { me: { notifications = [] } = {} } = {} } = await executeQuery(
    {
      query: `
        query fetchNotifications {
          me {
            notifications {
              id
              payload
            }
          }
        }
      `,
    },
  );
  return notifications;
};

/**
 * @async
 * @function
 *
 * @description
 * Acknowledge that a specific notification is received and processed.
 *
 * @example
 * import {acknowledgeNotifications} from '@sgorg/game-backend-client';
 *
 * async function acknowledgeNotificationAndLog() {
 *   await acknowledgeNotifications({notificationIds:['someId2321']});
 * }
 *
 * @param {Object} options
 * @param {Array<String>} options.notificationIds list of notification Id
 * @return {Promise}
 * @memberof module:notification
 *
 * @throws {GBCErrorNoConnection}
 * @throws {GBCErrorValidationFailed}
 */
const acknowledgeNotifications$1 = async ({ notificationIds } = {}) => {
  if (!isValidArray(notificationIds, isString$3)) {
    throw new GBCErrorValidationFailed(
      'Invalid "notificationIds" parameter. "notificationIds" should be an array of notificationId',
      "['45544','21312']",
    );
  }
  await executeQuery({
    query: `
      mutation acknowledgeNotifications($notificationIds: [ID!]){
        acknowledgeNotifications(notificationIds: $notificationIds)
      }
    `,
    vars: {
      notificationIds,
    },
  });
};

const fetchNotifications = wrapMethod(
  'fetchNotifications',
  fetchNotifications$1,
);
const acknowledgeNotifications = wrapMethod(
  'acknowledgeNotifications',
  acknowledgeNotifications$1,
);

var notification = /*#__PURE__*/Object.freeze({
  __proto__: null,
  fetchNotifications: fetchNotifications,
  acknowledgeNotifications: acknowledgeNotifications
});

async function handleCurrencyPurchase({
  currencySlug,
  amount,
  providerSpecificData,
}) {
  await executeQuery({
    query: `
      mutation purchaseCurrency($currencySlug: ID!, $amount: Float!, $providerSpecificData: JSON) {
          purchaseCurrency(currencySlug: $currencySlug, amount: $amount, providerSpecificData: $providerSpecificData)
      }
    `,
    vars: {
      currencySlug,
      amount,
      providerSpecificData,
    },
  });
}

/**
 * @async
 * @function
 *
 * @description
 * With this function you can execute a full currency purchase for a user.
 * The Game Backend will handle the purchase via the platform specific API and will add the amount of currency to the users wallet.
 * Since this is highly dependent on the platform, platform specific information and requirements follow. Only mentioned platforms are supported.
 *
 * #LINE
 * In LINE the purchase is using the users LINE COINS to proceed with the purchase.
 * For this the metaData of each in ADM for GB created currency must contain ```{line: {itemId: "string"}}``` where itemId is the id under which the currency is registered as item at LINE.
 * Furthermore you must provide the following data as providerSpecificData on each request
 * ```
 * {
 *     os: "string (IOS | AOS)",
      environment: "string (PRODUCTION | STAGING | SANDBOX) [check LINE API documentation for more information]",
      accessToken: "string (a valid accessToken as you already use for registerWithLineAccessToken)",
      title: "string (optional, just for tracking on LINE side)",
      memo: "string (optional, just for tracking on LINE side)"
 * }
 * ```
 *
 * @example
 * import {purchaseCurrency, ENVIRONMENT, OS} from '@sgorg/game-backend-client';
 *
 * // This example is based on LINE platform and must be adapted to your current platform
 * function getOs() {
 *     return isIOS() ? OS.IOS : OS.AOS;
 * }
 *
 * function getEnvironment() {
 *     if (isProduction()) {
 *         return ENVIRONMENT.PRODUCTION;
 *     }
 *     if (isStaging()) {
 *         return ENVIRONMENT.STAGING;
 *     }
 *     return ENVIRONMENT.SANDBOX;
 * }
 *
 * async function handleCurrencyPurchase({currencySlug, amount}) {
 *   const providerSpecificData = {
 *       os: getOs(),
 *       environment: getEnvironment(),
 *       accessToken: await getLineAccessTokenViaSomeWay(),
 *       title: "purchase-currency-${currencySlug}",
 *       memo: `Purchase of ${amount} times currency ${currencySlug}`
 *   };
 *   return purchaseCurrency({currencySlug, amount, providerSpecificData})
 * }
 *
 * @param {Object} options
 * @param {String} options.currencySlug Unique slug of the currency to purchase. Must have been created in GB via ADM
 * @param {Number} options.amount Amount of the specified currencySlug to purchase. Must be a positive number.
 * @param {Object} [options.providerSpecificData] Depending on your platform you have to provide specific addition information to execute a purchase
 * @return {Promise}
 * @memberof module:purchase
 *
 * @throws {GBCErrorNoConnection}
 * @throws {GBCErrorValidationFailed}
 */
const purchaseCurrency$1 = async ({
  currencySlug,
  amount,
  providerSpecificData,
} = {}) => {
  if (!isString$3(currencySlug) || !currencySlug.trim()) {
    throw new GBCErrorValidationFailed(
      'Invalid "currencySlug" parameter. "currencySlug" must be not empty string',
      'aCurrencyDisplayName',
    );
  }
  if (!amount || !isNumber$1(amount) || amount < 0) {
    throw new GBCErrorValidationFailed(
      'Invalid "amount" parameter. "amount" must be a number > 0',
      1,
    );
  }
  await handleCurrencyPurchase({
    currencySlug,
    amount,
    providerSpecificData,
  });
};

async function handleItemPurchase({
  itemSlug,
  amount,
  providerSpecificData,
}) {
  await executeQuery({
    query: `
      mutation purchaseItem($itemSlug: ID!, $amount: Float!, $providerSpecificData: JSON) {
          purchaseItem(itemSlug: $itemSlug, amount: $amount, providerSpecificData: $providerSpecificData)
      }
    `,
    vars: {
      itemSlug,
      amount,
      providerSpecificData,
    },
  });
}

/**
 * @async
 * @function
 *
 * @description
 * With this function you can execute a full item purchase for a user.
 * The Game Backend will handle the purchase via the platform specific API and will add the amount of the item to the users item pool.
 * Since this is highly dependent on the platform, platform specific information and requirements follow. Only mentioned platforms are supported.
 *
 * #LINE
 * In LINE the purchase is using the users LINE COINS to proceed with the purchase.
 * For this the metaData of each in ADM for GB created item must contain ```{line: {itemId: "string"}}``` where itemId is the id under which the item is registered at LINE.
 * Furthermore you must provide the following data as providerSpecificData on each request
 * ```
 * {
 *     os: "string (IOS | AOS)",
      environment: "string (PRODUCTION | STAGING | SANDBOX) [check LINE API documentation for more information]",
      accessToken: "string (a valid accessToken as you already use for registerWithLineAccessToken)",
      title: "string (optional, just for tracking on LINE side)",
      memo: "string (optional, just for tracking on LINE side)"
 * }
 * ```
 *
 * @example
 * import {purchaseItem, ENVIRONMENT} from '@sgorg/game-backend-client';
 *
 * // This example is based on LINE platform and must be adapted to your current platform
 * function getOs() {
 *     return isIOS() ? 'IOS' : 'AOS';
 * }
 *
 * function getEnvironment() {
 *     if (isProduction()) {
 *         return ENVIRONMENT.PRODUCTION;
 *     }
 *     if (isStaging()) {
 *         return ENVIRONMENT.STAGING;
 *     }
 *     return ENVIRONMENT.SANDBOX;
 * }
 *
 * async function handleItemPurchase({itemSlug, amount}) {
 *   const providerSpecificData = {
 *       os: getOs(),
 *       environment: getEnvironment(),
 *       accessToken: await getLineAccessTokenViaSomeWay(),
 *       title: "purchase-item-${itemSlug}",
 *       memo: `Purchase of ${amount} times item ${itemSlug}`
 *   };
 *   return purchaseItem({itemSlug, amount, providerSpecificData})
 * }
 *
 * @param {Object} options
 * @param {String} options.itemSlug Unique slug of the item to purchase. Must have been created in GB via ADM
 * @param {Number} options.amount Amount of the specified item to purchase. Must be a positive number.
 * @param {Object} [options.providerSpecificData] Depending on your platform you have to provide specific addition information to execute a purchase
 * @return {Promise}
 * @memberof module:purchase
 *
 * @throws {GBCErrorNoConnection}
 * @throws {GBCErrorValidationFailed}
 */
const purchaseItem$1 = async ({
  itemSlug,
  amount,
  providerSpecificData,
} = {}) => {
  if (!isString$3(itemSlug) || !itemSlug.trim()) {
    throw new GBCErrorValidationFailed(
      'Invalid "itemSlug" parameter. "itemSlug" must be not empty string',
      'aItemDisplayName',
    );
  }
  if (!amount || !isNumber$1(amount) || amount < 0) {
    throw new GBCErrorValidationFailed(
      'Invalid "amount" parameter. "amount" must be a number > 0',
      1,
    );
  }
  await handleItemPurchase({
    itemSlug,
    amount,
    providerSpecificData,
  });
};

/**
 * @async
 * @function
 *
 * @description
 * With this function you can store data regarding a purchase in the Game Backend for the current user
 *
 * @example
 * import {registerPurchase} from '@sgorg/game-backend-client';
 * async function trackPurchase(someDataToStore) {
 *   return registerPurchase({payload: someDataToStore})
 * }
 *
 * @param {Object} options
 * @param {Object} options.payload Payload to store about the purchase
 * @return {Promise}
 * @memberof module:purchase
 *
 * @throws {GBCErrorNoConnection}
 */
const registerPurchase$1 = async ({ payload }) => {
  const token = JSON.stringify(payload);
  await executeQuery({
    query: `
      mutation addIAPToken($token: String!) {
          addIAPToken(token: $token)
      }
    `,
    vars: {
      token,
    },
  });
};

/**
 * @async
 * @function
 *
 * @description
 * With this function you can open purchase transaction. To a transaction we can attach data regarding a purchase in the Game Backend for the current user
 *
 * @example
 * import {openPurchaseTransaction} from '@sgorg/game-backend-client';
 * async function openPurchase(someDataToStore) {
 *   const transactionToken = openPurchaseTransaction({payload: someDataToStore})
 * }
 *
 * @param {Object} options
 * @param {Object} options.payload Payload to store about the purchase
 * @return {Promise<string>} Transaction token
 * @memberof module:purchase
 *
 * @throws {GBCErrorNoConnection}
 * @throws {GBCErrorValidationFailed}
 */
const openPurchaseTransaction$1 = async ({ payload }) => {
  if (!isPlainObject_1(payload)) {
    throw new GBCErrorValidationFailed(
      'Invalid "payload" parameter. "payload" must be plain object',
      { purchaseData: 'fooBar' },
    );
  }
  const token = await executeQuery({
    query: `
      mutation openIAPTransaction($metaData: JSON!) {
          openIAPTransaction(metaData: $metaData)
      }
    `,
    vars: {
      metaData: payload,
    },
  });
  return token;
};

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-01.
 * Created by Daniel Pochert <daniel.pochert@softgames.de> on 2019-04-01.
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-04-01.
 * Created by Benhur Davies <benhur.davies@softgames.de> on 2019-04-01.
 * Copyright © Softgames 2019 - present
 */

const purchaseCurrency = wrapMethod('purchaseCurrency', purchaseCurrency$1);
const purchaseItem = wrapMethod('purchaseItem', purchaseItem$1);
const registerPurchase = wrapMethod('registerPurchase', registerPurchase$1);
const openPurchaseTransaction = wrapMethod(
  'openPurchaseTransaction',
  openPurchaseTransaction$1,
);

var purchase = /*#__PURE__*/Object.freeze({
  __proto__: null,
  purchaseCurrency: purchaseCurrency,
  purchaseItem: purchaseItem,
  registerPurchase: registerPurchase,
  openPurchaseTransaction: openPurchaseTransaction
});

var axios$3 = {exports: {}};

var axios$2 = {exports: {}};

var bind$2 = function bind(fn, thisArg) {
  return function wrap() {
    var args = new Array(arguments.length);
    for (var i = 0; i < args.length; i++) {
      args[i] = arguments[i];
    }
    return fn.apply(thisArg, args);
  };
};

var bind$1 = bind$2;

// utils is a library of generic helper functions non-specific to axios

var toString = Object.prototype.toString;

// eslint-disable-next-line func-names
var kindOf = (function(cache) {
  // eslint-disable-next-line func-names
  return function(thing) {
    var str = toString.call(thing);
    return cache[str] || (cache[str] = str.slice(8, -1).toLowerCase());
  };
})(Object.create(null));

function kindOfTest(type) {
  type = type.toLowerCase();
  return function isKindOf(thing) {
    return kindOf(thing) === type;
  };
}

/**
 * Determine if a value is an Array
 *
 * @param {Object} val The value to test
 * @returns {boolean} True if value is an Array, otherwise false
 */
function isArray(val) {
  return Array.isArray(val);
}

/**
 * Determine if a value is undefined
 *
 * @param {Object} val The value to test
 * @returns {boolean} True if the value is undefined, otherwise false
 */
function isUndefined(val) {
  return typeof val === 'undefined';
}

/**
 * Determine if a value is a Buffer
 *
 * @param {Object} val The value to test
 * @returns {boolean} True if value is a Buffer, otherwise false
 */
function isBuffer(val) {
  return val !== null && !isUndefined(val) && val.constructor !== null && !isUndefined(val.constructor)
    && typeof val.constructor.isBuffer === 'function' && val.constructor.isBuffer(val);
}

/**
 * Determine if a value is an ArrayBuffer
 *
 * @function
 * @param {Object} val The value to test
 * @returns {boolean} True if value is an ArrayBuffer, otherwise false
 */
var isArrayBuffer = kindOfTest('ArrayBuffer');


/**
 * Determine if a value is a view on an ArrayBuffer
 *
 * @param {Object} val The value to test
 * @returns {boolean} True if value is a view on an ArrayBuffer, otherwise false
 */
function isArrayBufferView(val) {
  var result;
  if ((typeof ArrayBuffer !== 'undefined') && (ArrayBuffer.isView)) {
    result = ArrayBuffer.isView(val);
  } else {
    result = (val) && (val.buffer) && (isArrayBuffer(val.buffer));
  }
  return result;
}

/**
 * Determine if a value is a String
 *
 * @param {Object} val The value to test
 * @returns {boolean} True if value is a String, otherwise false
 */
function isString(val) {
  return typeof val === 'string';
}

/**
 * Determine if a value is a Number
 *
 * @param {Object} val The value to test
 * @returns {boolean} True if value is a Number, otherwise false
 */
function isNumber(val) {
  return typeof val === 'number';
}

/**
 * Determine if a value is an Object
 *
 * @param {Object} val The value to test
 * @returns {boolean} True if value is an Object, otherwise false
 */
function isObject(val) {
  return val !== null && typeof val === 'object';
}

/**
 * Determine if a value is a plain Object
 *
 * @param {Object} val The value to test
 * @return {boolean} True if value is a plain Object, otherwise false
 */
function isPlainObject(val) {
  if (kindOf(val) !== 'object') {
    return false;
  }

  var prototype = Object.getPrototypeOf(val);
  return prototype === null || prototype === Object.prototype;
}

/**
 * Determine if a value is a Date
 *
 * @function
 * @param {Object} val The value to test
 * @returns {boolean} True if value is a Date, otherwise false
 */
var isDate = kindOfTest('Date');

/**
 * Determine if a value is a File
 *
 * @function
 * @param {Object} val The value to test
 * @returns {boolean} True if value is a File, otherwise false
 */
var isFile = kindOfTest('File');

/**
 * Determine if a value is a Blob
 *
 * @function
 * @param {Object} val The value to test
 * @returns {boolean} True if value is a Blob, otherwise false
 */
var isBlob = kindOfTest('Blob');

/**
 * Determine if a value is a FileList
 *
 * @function
 * @param {Object} val The value to test
 * @returns {boolean} True if value is a File, otherwise false
 */
var isFileList = kindOfTest('FileList');

/**
 * Determine if a value is a Function
 *
 * @param {Object} val The value to test
 * @returns {boolean} True if value is a Function, otherwise false
 */
function isFunction(val) {
  return toString.call(val) === '[object Function]';
}

/**
 * Determine if a value is a Stream
 *
 * @param {Object} val The value to test
 * @returns {boolean} True if value is a Stream, otherwise false
 */
function isStream(val) {
  return isObject(val) && isFunction(val.pipe);
}

/**
 * Determine if a value is a FormData
 *
 * @param {Object} thing The value to test
 * @returns {boolean} True if value is an FormData, otherwise false
 */
function isFormData(thing) {
  var pattern = '[object FormData]';
  return thing && (
    (typeof FormData === 'function' && thing instanceof FormData) ||
    toString.call(thing) === pattern ||
    (isFunction(thing.toString) && thing.toString() === pattern)
  );
}

/**
 * Determine if a value is a URLSearchParams object
 * @function
 * @param {Object} val The value to test
 * @returns {boolean} True if value is a URLSearchParams object, otherwise false
 */
var isURLSearchParams = kindOfTest('URLSearchParams');

/**
 * Trim excess whitespace off the beginning and end of a string
 *
 * @param {String} str The String to trim
 * @returns {String} The String freed of excess whitespace
 */
function trim(str) {
  return str.trim ? str.trim() : str.replace(/^\s+|\s+$/g, '');
}

/**
 * Determine if we're running in a standard browser environment
 *
 * This allows axios to run in a web worker, and react-native.
 * Both environments support XMLHttpRequest, but not fully standard globals.
 *
 * web workers:
 *  typeof window -> undefined
 *  typeof document -> undefined
 *
 * react-native:
 *  navigator.product -> 'ReactNative'
 * nativescript
 *  navigator.product -> 'NativeScript' or 'NS'
 */
function isStandardBrowserEnv() {
  if (typeof navigator !== 'undefined' && (navigator.product === 'ReactNative' ||
                                           navigator.product === 'NativeScript' ||
                                           navigator.product === 'NS')) {
    return false;
  }
  return (
    typeof window !== 'undefined' &&
    typeof document !== 'undefined'
  );
}

/**
 * Iterate over an Array or an Object invoking a function for each item.
 *
 * If `obj` is an Array callback will be called passing
 * the value, index, and complete array for each item.
 *
 * If 'obj' is an Object callback will be called passing
 * the value, key, and complete object for each property.
 *
 * @param {Object|Array} obj The object to iterate
 * @param {Function} fn The callback to invoke for each item
 */
function forEach(obj, fn) {
  // Don't bother if no value provided
  if (obj === null || typeof obj === 'undefined') {
    return;
  }

  // Force an array if not already something iterable
  if (typeof obj !== 'object') {
    /*eslint no-param-reassign:0*/
    obj = [obj];
  }

  if (isArray(obj)) {
    // Iterate over array values
    for (var i = 0, l = obj.length; i < l; i++) {
      fn.call(null, obj[i], i, obj);
    }
  } else {
    // Iterate over object keys
    for (var key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        fn.call(null, obj[key], key, obj);
      }
    }
  }
}

/**
 * Accepts varargs expecting each argument to be an object, then
 * immutably merges the properties of each object and returns result.
 *
 * When multiple objects contain the same key the later object in
 * the arguments list will take precedence.
 *
 * Example:
 *
 * ```js
 * var result = merge({foo: 123}, {foo: 456});
 * console.log(result.foo); // outputs 456
 * ```
 *
 * @param {Object} obj1 Object to merge
 * @returns {Object} Result of all merge properties
 */
function merge(/* obj1, obj2, obj3, ... */) {
  var result = {};
  function assignValue(val, key) {
    if (isPlainObject(result[key]) && isPlainObject(val)) {
      result[key] = merge(result[key], val);
    } else if (isPlainObject(val)) {
      result[key] = merge({}, val);
    } else if (isArray(val)) {
      result[key] = val.slice();
    } else {
      result[key] = val;
    }
  }

  for (var i = 0, l = arguments.length; i < l; i++) {
    forEach(arguments[i], assignValue);
  }
  return result;
}

/**
 * Extends object a by mutably adding to it the properties of object b.
 *
 * @param {Object} a The object to be extended
 * @param {Object} b The object to copy properties from
 * @param {Object} thisArg The object to bind function to
 * @return {Object} The resulting value of object a
 */
function extend(a, b, thisArg) {
  forEach(b, function assignValue(val, key) {
    if (thisArg && typeof val === 'function') {
      a[key] = bind$1(val, thisArg);
    } else {
      a[key] = val;
    }
  });
  return a;
}

/**
 * Remove byte order marker. This catches EF BB BF (the UTF-8 BOM)
 *
 * @param {string} content with BOM
 * @return {string} content value without BOM
 */
function stripBOM(content) {
  if (content.charCodeAt(0) === 0xFEFF) {
    content = content.slice(1);
  }
  return content;
}

/**
 * Inherit the prototype methods from one constructor into another
 * @param {function} constructor
 * @param {function} superConstructor
 * @param {object} [props]
 * @param {object} [descriptors]
 */

function inherits(constructor, superConstructor, props, descriptors) {
  constructor.prototype = Object.create(superConstructor.prototype, descriptors);
  constructor.prototype.constructor = constructor;
  props && Object.assign(constructor.prototype, props);
}

/**
 * Resolve object with deep prototype chain to a flat object
 * @param {Object} sourceObj source object
 * @param {Object} [destObj]
 * @param {Function} [filter]
 * @returns {Object}
 */

function toFlatObject(sourceObj, destObj, filter) {
  var props;
  var i;
  var prop;
  var merged = {};

  destObj = destObj || {};

  do {
    props = Object.getOwnPropertyNames(sourceObj);
    i = props.length;
    while (i-- > 0) {
      prop = props[i];
      if (!merged[prop]) {
        destObj[prop] = sourceObj[prop];
        merged[prop] = true;
      }
    }
    sourceObj = Object.getPrototypeOf(sourceObj);
  } while (sourceObj && (!filter || filter(sourceObj, destObj)) && sourceObj !== Object.prototype);

  return destObj;
}

/*
 * determines whether a string ends with the characters of a specified string
 * @param {String} str
 * @param {String} searchString
 * @param {Number} [position= 0]
 * @returns {boolean}
 */
function endsWith(str, searchString, position) {
  str = String(str);
  if (position === undefined || position > str.length) {
    position = str.length;
  }
  position -= searchString.length;
  var lastIndex = str.indexOf(searchString, position);
  return lastIndex !== -1 && lastIndex === position;
}


/**
 * Returns new array from array like object
 * @param {*} [thing]
 * @returns {Array}
 */
function toArray(thing) {
  if (!thing) return null;
  var i = thing.length;
  if (isUndefined(i)) return null;
  var arr = new Array(i);
  while (i-- > 0) {
    arr[i] = thing[i];
  }
  return arr;
}

// eslint-disable-next-line func-names
var isTypedArray = (function(TypedArray) {
  // eslint-disable-next-line func-names
  return function(thing) {
    return TypedArray && thing instanceof TypedArray;
  };
})(typeof Uint8Array !== 'undefined' && Object.getPrototypeOf(Uint8Array));

var utils$9 = {
  isArray: isArray,
  isArrayBuffer: isArrayBuffer,
  isBuffer: isBuffer,
  isFormData: isFormData,
  isArrayBufferView: isArrayBufferView,
  isString: isString,
  isNumber: isNumber,
  isObject: isObject,
  isPlainObject: isPlainObject,
  isUndefined: isUndefined,
  isDate: isDate,
  isFile: isFile,
  isBlob: isBlob,
  isFunction: isFunction,
  isStream: isStream,
  isURLSearchParams: isURLSearchParams,
  isStandardBrowserEnv: isStandardBrowserEnv,
  forEach: forEach,
  merge: merge,
  extend: extend,
  trim: trim,
  stripBOM: stripBOM,
  inherits: inherits,
  toFlatObject: toFlatObject,
  kindOf: kindOf,
  kindOfTest: kindOfTest,
  endsWith: endsWith,
  toArray: toArray,
  isTypedArray: isTypedArray,
  isFileList: isFileList
};

var utils$8 = utils$9;

function encode(val) {
  return encodeURIComponent(val).
    replace(/%3A/gi, ':').
    replace(/%24/g, '$').
    replace(/%2C/gi, ',').
    replace(/%20/g, '+').
    replace(/%5B/gi, '[').
    replace(/%5D/gi, ']');
}

/**
 * Build a URL by appending params to the end
 *
 * @param {string} url The base of the url (e.g., http://www.google.com)
 * @param {object} [params] The params to be appended
 * @returns {string} The formatted url
 */
var buildURL$1 = function buildURL(url, params, paramsSerializer) {
  /*eslint no-param-reassign:0*/
  if (!params) {
    return url;
  }

  var serializedParams;
  if (paramsSerializer) {
    serializedParams = paramsSerializer(params);
  } else if (utils$8.isURLSearchParams(params)) {
    serializedParams = params.toString();
  } else {
    var parts = [];

    utils$8.forEach(params, function serialize(val, key) {
      if (val === null || typeof val === 'undefined') {
        return;
      }

      if (utils$8.isArray(val)) {
        key = key + '[]';
      } else {
        val = [val];
      }

      utils$8.forEach(val, function parseValue(v) {
        if (utils$8.isDate(v)) {
          v = v.toISOString();
        } else if (utils$8.isObject(v)) {
          v = JSON.stringify(v);
        }
        parts.push(encode(key) + '=' + encode(v));
      });
    });

    serializedParams = parts.join('&');
  }

  if (serializedParams) {
    var hashmarkIndex = url.indexOf('#');
    if (hashmarkIndex !== -1) {
      url = url.slice(0, hashmarkIndex);
    }

    url += (url.indexOf('?') === -1 ? '?' : '&') + serializedParams;
  }

  return url;
};

var utils$7 = utils$9;

function InterceptorManager$1() {
  this.handlers = [];
}

/**
 * Add a new interceptor to the stack
 *
 * @param {Function} fulfilled The function to handle `then` for a `Promise`
 * @param {Function} rejected The function to handle `reject` for a `Promise`
 *
 * @return {Number} An ID used to remove interceptor later
 */
InterceptorManager$1.prototype.use = function use(fulfilled, rejected, options) {
  this.handlers.push({
    fulfilled: fulfilled,
    rejected: rejected,
    synchronous: options ? options.synchronous : false,
    runWhen: options ? options.runWhen : null
  });
  return this.handlers.length - 1;
};

/**
 * Remove an interceptor from the stack
 *
 * @param {Number} id The ID that was returned by `use`
 */
InterceptorManager$1.prototype.eject = function eject(id) {
  if (this.handlers[id]) {
    this.handlers[id] = null;
  }
};

/**
 * Iterate over all the registered interceptors
 *
 * This method is particularly useful for skipping over any
 * interceptors that may have become `null` calling `eject`.
 *
 * @param {Function} fn The function to call for each interceptor
 */
InterceptorManager$1.prototype.forEach = function forEach(fn) {
  utils$7.forEach(this.handlers, function forEachHandler(h) {
    if (h !== null) {
      fn(h);
    }
  });
};

var InterceptorManager_1 = InterceptorManager$1;

var utils$6 = utils$9;

var normalizeHeaderName$1 = function normalizeHeaderName(headers, normalizedName) {
  utils$6.forEach(headers, function processHeader(value, name) {
    if (name !== normalizedName && name.toUpperCase() === normalizedName.toUpperCase()) {
      headers[normalizedName] = value;
      delete headers[name];
    }
  });
};

var AxiosError_1;
var hasRequiredAxiosError;

function requireAxiosError () {
	if (hasRequiredAxiosError) return AxiosError_1;
	hasRequiredAxiosError = 1;

	var utils = utils$9;

	/**
	 * Create an Error with the specified message, config, error code, request and response.
	 *
	 * @param {string} message The error message.
	 * @param {string} [code] The error code (for example, 'ECONNABORTED').
	 * @param {Object} [config] The config.
	 * @param {Object} [request] The request.
	 * @param {Object} [response] The response.
	 * @returns {Error} The created error.
	 */
	function AxiosError(message, code, config, request, response) {
	  Error.call(this);
	  this.message = message;
	  this.name = 'AxiosError';
	  code && (this.code = code);
	  config && (this.config = config);
	  request && (this.request = request);
	  response && (this.response = response);
	}

	utils.inherits(AxiosError, Error, {
	  toJSON: function toJSON() {
	    return {
	      // Standard
	      message: this.message,
	      name: this.name,
	      // Microsoft
	      description: this.description,
	      number: this.number,
	      // Mozilla
	      fileName: this.fileName,
	      lineNumber: this.lineNumber,
	      columnNumber: this.columnNumber,
	      stack: this.stack,
	      // Axios
	      config: this.config,
	      code: this.code,
	      status: this.response && this.response.status ? this.response.status : null
	    };
	  }
	});

	var prototype = AxiosError.prototype;
	var descriptors = {};

	[
	  'ERR_BAD_OPTION_VALUE',
	  'ERR_BAD_OPTION',
	  'ECONNABORTED',
	  'ETIMEDOUT',
	  'ERR_NETWORK',
	  'ERR_FR_TOO_MANY_REDIRECTS',
	  'ERR_DEPRECATED',
	  'ERR_BAD_RESPONSE',
	  'ERR_BAD_REQUEST',
	  'ERR_CANCELED'
	// eslint-disable-next-line func-names
	].forEach(function(code) {
	  descriptors[code] = {value: code};
	});

	Object.defineProperties(AxiosError, descriptors);
	Object.defineProperty(prototype, 'isAxiosError', {value: true});

	// eslint-disable-next-line func-names
	AxiosError.from = function(error, code, config, request, response, customProps) {
	  var axiosError = Object.create(prototype);

	  utils.toFlatObject(error, axiosError, function filter(obj) {
	    return obj !== Error.prototype;
	  });

	  AxiosError.call(axiosError, error.message, code, config, request, response);

	  axiosError.name = error.name;

	  customProps && Object.assign(axiosError, customProps);

	  return axiosError;
	};

	AxiosError_1 = AxiosError;
	return AxiosError_1;
}

var transitional = {
  silentJSONParsing: true,
  forcedJSONParsing: true,
  clarifyTimeoutError: false
};

var toFormData_1;
var hasRequiredToFormData;

function requireToFormData () {
	if (hasRequiredToFormData) return toFormData_1;
	hasRequiredToFormData = 1;

	var utils = utils$9;

	/**
	 * Convert a data object to FormData
	 * @param {Object} obj
	 * @param {?Object} [formData]
	 * @returns {Object}
	 **/

	function toFormData(obj, formData) {
	  // eslint-disable-next-line no-param-reassign
	  formData = formData || new FormData();

	  var stack = [];

	  function convertValue(value) {
	    if (value === null) return '';

	    if (utils.isDate(value)) {
	      return value.toISOString();
	    }

	    if (utils.isArrayBuffer(value) || utils.isTypedArray(value)) {
	      return typeof Blob === 'function' ? new Blob([value]) : Buffer.from(value);
	    }

	    return value;
	  }

	  function build(data, parentKey) {
	    if (utils.isPlainObject(data) || utils.isArray(data)) {
	      if (stack.indexOf(data) !== -1) {
	        throw Error('Circular reference detected in ' + parentKey);
	      }

	      stack.push(data);

	      utils.forEach(data, function each(value, key) {
	        if (utils.isUndefined(value)) return;
	        var fullKey = parentKey ? parentKey + '.' + key : key;
	        var arr;

	        if (value && !parentKey && typeof value === 'object') {
	          if (utils.endsWith(key, '{}')) {
	            // eslint-disable-next-line no-param-reassign
	            value = JSON.stringify(value);
	          } else if (utils.endsWith(key, '[]') && (arr = utils.toArray(value))) {
	            // eslint-disable-next-line func-names
	            arr.forEach(function(el) {
	              !utils.isUndefined(el) && formData.append(fullKey, convertValue(el));
	            });
	            return;
	          }
	        }

	        build(value, fullKey);
	      });

	      stack.pop();
	    } else {
	      formData.append(parentKey, convertValue(data));
	    }
	  }

	  build(obj);

	  return formData;
	}

	toFormData_1 = toFormData;
	return toFormData_1;
}

var settle;
var hasRequiredSettle;

function requireSettle () {
	if (hasRequiredSettle) return settle;
	hasRequiredSettle = 1;

	var AxiosError = requireAxiosError();

	/**
	 * Resolve or reject a Promise based on response status.
	 *
	 * @param {Function} resolve A function that resolves the promise.
	 * @param {Function} reject A function that rejects the promise.
	 * @param {object} response The response.
	 */
	settle = function settle(resolve, reject, response) {
	  var validateStatus = response.config.validateStatus;
	  if (!response.status || !validateStatus || validateStatus(response.status)) {
	    resolve(response);
	  } else {
	    reject(new AxiosError(
	      'Request failed with status code ' + response.status,
	      [AxiosError.ERR_BAD_REQUEST, AxiosError.ERR_BAD_RESPONSE][Math.floor(response.status / 100) - 4],
	      response.config,
	      response.request,
	      response
	    ));
	  }
	};
	return settle;
}

var cookies;
var hasRequiredCookies;

function requireCookies () {
	if (hasRequiredCookies) return cookies;
	hasRequiredCookies = 1;

	var utils = utils$9;

	cookies = (
	  utils.isStandardBrowserEnv() ?

	  // Standard browser envs support document.cookie
	    (function standardBrowserEnv() {
	      return {
	        write: function write(name, value, expires, path, domain, secure) {
	          var cookie = [];
	          cookie.push(name + '=' + encodeURIComponent(value));

	          if (utils.isNumber(expires)) {
	            cookie.push('expires=' + new Date(expires).toGMTString());
	          }

	          if (utils.isString(path)) {
	            cookie.push('path=' + path);
	          }

	          if (utils.isString(domain)) {
	            cookie.push('domain=' + domain);
	          }

	          if (secure === true) {
	            cookie.push('secure');
	          }

	          document.cookie = cookie.join('; ');
	        },

	        read: function read(name) {
	          var match = document.cookie.match(new RegExp('(^|;\\s*)(' + name + ')=([^;]*)'));
	          return (match ? decodeURIComponent(match[3]) : null);
	        },

	        remove: function remove(name) {
	          this.write(name, '', Date.now() - 86400000);
	        }
	      };
	    })() :

	  // Non standard browser env (web workers, react-native) lack needed support.
	    (function nonStandardBrowserEnv() {
	      return {
	        write: function write() {},
	        read: function read() { return null; },
	        remove: function remove() {}
	      };
	    })()
	);
	return cookies;
}

/**
 * Determines whether the specified URL is absolute
 *
 * @param {string} url The URL to test
 * @returns {boolean} True if the specified URL is absolute, otherwise false
 */
var isAbsoluteURL$1 = function isAbsoluteURL(url) {
  // A URL is considered absolute if it begins with "<scheme>://" or "//" (protocol-relative URL).
  // RFC 3986 defines scheme name as a sequence of characters beginning with a letter and followed
  // by any combination of letters, digits, plus, period, or hyphen.
  return /^([a-z][a-z\d+\-.]*:)?\/\//i.test(url);
};

/**
 * Creates a new URL by combining the specified URLs
 *
 * @param {string} baseURL The base URL
 * @param {string} relativeURL The relative URL
 * @returns {string} The combined URL
 */
var combineURLs$1 = function combineURLs(baseURL, relativeURL) {
  return relativeURL
    ? baseURL.replace(/\/+$/, '') + '/' + relativeURL.replace(/^\/+/, '')
    : baseURL;
};

var isAbsoluteURL = isAbsoluteURL$1;
var combineURLs = combineURLs$1;

/**
 * Creates a new URL by combining the baseURL with the requestedURL,
 * only when the requestedURL is not already an absolute URL.
 * If the requestURL is absolute, this function returns the requestedURL untouched.
 *
 * @param {string} baseURL The base URL
 * @param {string} requestedURL Absolute or relative URL to combine
 * @returns {string} The combined full path
 */
var buildFullPath$1 = function buildFullPath(baseURL, requestedURL) {
  if (baseURL && !isAbsoluteURL(requestedURL)) {
    return combineURLs(baseURL, requestedURL);
  }
  return requestedURL;
};

var parseHeaders;
var hasRequiredParseHeaders;

function requireParseHeaders () {
	if (hasRequiredParseHeaders) return parseHeaders;
	hasRequiredParseHeaders = 1;

	var utils = utils$9;

	// Headers whose duplicates are ignored by node
	// c.f. https://nodejs.org/api/http.html#http_message_headers
	var ignoreDuplicateOf = [
	  'age', 'authorization', 'content-length', 'content-type', 'etag',
	  'expires', 'from', 'host', 'if-modified-since', 'if-unmodified-since',
	  'last-modified', 'location', 'max-forwards', 'proxy-authorization',
	  'referer', 'retry-after', 'user-agent'
	];

	/**
	 * Parse headers into an object
	 *
	 * ```
	 * Date: Wed, 27 Aug 2014 08:58:49 GMT
	 * Content-Type: application/json
	 * Connection: keep-alive
	 * Transfer-Encoding: chunked
	 * ```
	 *
	 * @param {String} headers Headers needing to be parsed
	 * @returns {Object} Headers parsed into an object
	 */
	parseHeaders = function parseHeaders(headers) {
	  var parsed = {};
	  var key;
	  var val;
	  var i;

	  if (!headers) { return parsed; }

	  utils.forEach(headers.split('\n'), function parser(line) {
	    i = line.indexOf(':');
	    key = utils.trim(line.substr(0, i)).toLowerCase();
	    val = utils.trim(line.substr(i + 1));

	    if (key) {
	      if (parsed[key] && ignoreDuplicateOf.indexOf(key) >= 0) {
	        return;
	      }
	      if (key === 'set-cookie') {
	        parsed[key] = (parsed[key] ? parsed[key] : []).concat([val]);
	      } else {
	        parsed[key] = parsed[key] ? parsed[key] + ', ' + val : val;
	      }
	    }
	  });

	  return parsed;
	};
	return parseHeaders;
}

var isURLSameOrigin;
var hasRequiredIsURLSameOrigin;

function requireIsURLSameOrigin () {
	if (hasRequiredIsURLSameOrigin) return isURLSameOrigin;
	hasRequiredIsURLSameOrigin = 1;

	var utils = utils$9;

	isURLSameOrigin = (
	  utils.isStandardBrowserEnv() ?

	  // Standard browser envs have full support of the APIs needed to test
	  // whether the request URL is of the same origin as current location.
	    (function standardBrowserEnv() {
	      var msie = /(msie|trident)/i.test(navigator.userAgent);
	      var urlParsingNode = document.createElement('a');
	      var originURL;

	      /**
	    * Parse a URL to discover it's components
	    *
	    * @param {String} url The URL to be parsed
	    * @returns {Object}
	    */
	      function resolveURL(url) {
	        var href = url;

	        if (msie) {
	        // IE needs attribute set twice to normalize properties
	          urlParsingNode.setAttribute('href', href);
	          href = urlParsingNode.href;
	        }

	        urlParsingNode.setAttribute('href', href);

	        // urlParsingNode provides the UrlUtils interface - http://url.spec.whatwg.org/#urlutils
	        return {
	          href: urlParsingNode.href,
	          protocol: urlParsingNode.protocol ? urlParsingNode.protocol.replace(/:$/, '') : '',
	          host: urlParsingNode.host,
	          search: urlParsingNode.search ? urlParsingNode.search.replace(/^\?/, '') : '',
	          hash: urlParsingNode.hash ? urlParsingNode.hash.replace(/^#/, '') : '',
	          hostname: urlParsingNode.hostname,
	          port: urlParsingNode.port,
	          pathname: (urlParsingNode.pathname.charAt(0) === '/') ?
	            urlParsingNode.pathname :
	            '/' + urlParsingNode.pathname
	        };
	      }

	      originURL = resolveURL(window.location.href);

	      /**
	    * Determine if a URL shares the same origin as the current location
	    *
	    * @param {String} requestURL The URL to test
	    * @returns {boolean} True if URL shares the same origin, otherwise false
	    */
	      return function isURLSameOrigin(requestURL) {
	        var parsed = (utils.isString(requestURL)) ? resolveURL(requestURL) : requestURL;
	        return (parsed.protocol === originURL.protocol &&
	            parsed.host === originURL.host);
	      };
	    })() :

	  // Non standard browser envs (web workers, react-native) lack needed support.
	    (function nonStandardBrowserEnv() {
	      return function isURLSameOrigin() {
	        return true;
	      };
	    })()
	);
	return isURLSameOrigin;
}

var CanceledError_1;
var hasRequiredCanceledError;

function requireCanceledError () {
	if (hasRequiredCanceledError) return CanceledError_1;
	hasRequiredCanceledError = 1;

	var AxiosError = requireAxiosError();
	var utils = utils$9;

	/**
	 * A `CanceledError` is an object that is thrown when an operation is canceled.
	 *
	 * @class
	 * @param {string=} message The message.
	 */
	function CanceledError(message) {
	  // eslint-disable-next-line no-eq-null,eqeqeq
	  AxiosError.call(this, message == null ? 'canceled' : message, AxiosError.ERR_CANCELED);
	  this.name = 'CanceledError';
	}

	utils.inherits(CanceledError, AxiosError, {
	  __CANCEL__: true
	});

	CanceledError_1 = CanceledError;
	return CanceledError_1;
}

var parseProtocol;
var hasRequiredParseProtocol;

function requireParseProtocol () {
	if (hasRequiredParseProtocol) return parseProtocol;
	hasRequiredParseProtocol = 1;

	parseProtocol = function parseProtocol(url) {
	  var match = /^([-+\w]{1,25})(:?\/\/|:)/.exec(url);
	  return match && match[1] || '';
	};
	return parseProtocol;
}

var xhr;
var hasRequiredXhr;

function requireXhr () {
	if (hasRequiredXhr) return xhr;
	hasRequiredXhr = 1;

	var utils = utils$9;
	var settle = requireSettle();
	var cookies = requireCookies();
	var buildURL = buildURL$1;
	var buildFullPath = buildFullPath$1;
	var parseHeaders = requireParseHeaders();
	var isURLSameOrigin = requireIsURLSameOrigin();
	var transitionalDefaults = transitional;
	var AxiosError = requireAxiosError();
	var CanceledError = requireCanceledError();
	var parseProtocol = requireParseProtocol();

	xhr = function xhrAdapter(config) {
	  return new Promise(function dispatchXhrRequest(resolve, reject) {
	    var requestData = config.data;
	    var requestHeaders = config.headers;
	    var responseType = config.responseType;
	    var onCanceled;
	    function done() {
	      if (config.cancelToken) {
	        config.cancelToken.unsubscribe(onCanceled);
	      }

	      if (config.signal) {
	        config.signal.removeEventListener('abort', onCanceled);
	      }
	    }

	    if (utils.isFormData(requestData) && utils.isStandardBrowserEnv()) {
	      delete requestHeaders['Content-Type']; // Let the browser set it
	    }

	    var request = new XMLHttpRequest();

	    // HTTP basic authentication
	    if (config.auth) {
	      var username = config.auth.username || '';
	      var password = config.auth.password ? unescape(encodeURIComponent(config.auth.password)) : '';
	      requestHeaders.Authorization = 'Basic ' + btoa(username + ':' + password);
	    }

	    var fullPath = buildFullPath(config.baseURL, config.url);

	    request.open(config.method.toUpperCase(), buildURL(fullPath, config.params, config.paramsSerializer), true);

	    // Set the request timeout in MS
	    request.timeout = config.timeout;

	    function onloadend() {
	      if (!request) {
	        return;
	      }
	      // Prepare the response
	      var responseHeaders = 'getAllResponseHeaders' in request ? parseHeaders(request.getAllResponseHeaders()) : null;
	      var responseData = !responseType || responseType === 'text' ||  responseType === 'json' ?
	        request.responseText : request.response;
	      var response = {
	        data: responseData,
	        status: request.status,
	        statusText: request.statusText,
	        headers: responseHeaders,
	        config: config,
	        request: request
	      };

	      settle(function _resolve(value) {
	        resolve(value);
	        done();
	      }, function _reject(err) {
	        reject(err);
	        done();
	      }, response);

	      // Clean up request
	      request = null;
	    }

	    if ('onloadend' in request) {
	      // Use onloadend if available
	      request.onloadend = onloadend;
	    } else {
	      // Listen for ready state to emulate onloadend
	      request.onreadystatechange = function handleLoad() {
	        if (!request || request.readyState !== 4) {
	          return;
	        }

	        // The request errored out and we didn't get a response, this will be
	        // handled by onerror instead
	        // With one exception: request that using file: protocol, most browsers
	        // will return status as 0 even though it's a successful request
	        if (request.status === 0 && !(request.responseURL && request.responseURL.indexOf('file:') === 0)) {
	          return;
	        }
	        // readystate handler is calling before onerror or ontimeout handlers,
	        // so we should call onloadend on the next 'tick'
	        setTimeout(onloadend);
	      };
	    }

	    // Handle browser request cancellation (as opposed to a manual cancellation)
	    request.onabort = function handleAbort() {
	      if (!request) {
	        return;
	      }

	      reject(new AxiosError('Request aborted', AxiosError.ECONNABORTED, config, request));

	      // Clean up request
	      request = null;
	    };

	    // Handle low level network errors
	    request.onerror = function handleError() {
	      // Real errors are hidden from us by the browser
	      // onerror should only fire if it's a network error
	      reject(new AxiosError('Network Error', AxiosError.ERR_NETWORK, config, request, request));

	      // Clean up request
	      request = null;
	    };

	    // Handle timeout
	    request.ontimeout = function handleTimeout() {
	      var timeoutErrorMessage = config.timeout ? 'timeout of ' + config.timeout + 'ms exceeded' : 'timeout exceeded';
	      var transitional = config.transitional || transitionalDefaults;
	      if (config.timeoutErrorMessage) {
	        timeoutErrorMessage = config.timeoutErrorMessage;
	      }
	      reject(new AxiosError(
	        timeoutErrorMessage,
	        transitional.clarifyTimeoutError ? AxiosError.ETIMEDOUT : AxiosError.ECONNABORTED,
	        config,
	        request));

	      // Clean up request
	      request = null;
	    };

	    // Add xsrf header
	    // This is only done if running in a standard browser environment.
	    // Specifically not if we're in a web worker, or react-native.
	    if (utils.isStandardBrowserEnv()) {
	      // Add xsrf header
	      var xsrfValue = (config.withCredentials || isURLSameOrigin(fullPath)) && config.xsrfCookieName ?
	        cookies.read(config.xsrfCookieName) :
	        undefined;

	      if (xsrfValue) {
	        requestHeaders[config.xsrfHeaderName] = xsrfValue;
	      }
	    }

	    // Add headers to the request
	    if ('setRequestHeader' in request) {
	      utils.forEach(requestHeaders, function setRequestHeader(val, key) {
	        if (typeof requestData === 'undefined' && key.toLowerCase() === 'content-type') {
	          // Remove Content-Type if data is undefined
	          delete requestHeaders[key];
	        } else {
	          // Otherwise add header to the request
	          request.setRequestHeader(key, val);
	        }
	      });
	    }

	    // Add withCredentials to request if needed
	    if (!utils.isUndefined(config.withCredentials)) {
	      request.withCredentials = !!config.withCredentials;
	    }

	    // Add responseType to request if needed
	    if (responseType && responseType !== 'json') {
	      request.responseType = config.responseType;
	    }

	    // Handle progress if needed
	    if (typeof config.onDownloadProgress === 'function') {
	      request.addEventListener('progress', config.onDownloadProgress);
	    }

	    // Not all browsers support upload events
	    if (typeof config.onUploadProgress === 'function' && request.upload) {
	      request.upload.addEventListener('progress', config.onUploadProgress);
	    }

	    if (config.cancelToken || config.signal) {
	      // Handle cancellation
	      // eslint-disable-next-line func-names
	      onCanceled = function(cancel) {
	        if (!request) {
	          return;
	        }
	        reject(!cancel || (cancel && cancel.type) ? new CanceledError() : cancel);
	        request.abort();
	        request = null;
	      };

	      config.cancelToken && config.cancelToken.subscribe(onCanceled);
	      if (config.signal) {
	        config.signal.aborted ? onCanceled() : config.signal.addEventListener('abort', onCanceled);
	      }
	    }

	    if (!requestData) {
	      requestData = null;
	    }

	    var protocol = parseProtocol(fullPath);

	    if (protocol && [ 'http', 'https', 'file' ].indexOf(protocol) === -1) {
	      reject(new AxiosError('Unsupported protocol ' + protocol + ':', AxiosError.ERR_BAD_REQUEST, config));
	      return;
	    }


	    // Send the request
	    request.send(requestData);
	  });
	};
	return xhr;
}

var _null;
var hasRequired_null;

function require_null () {
	if (hasRequired_null) return _null;
	hasRequired_null = 1;
	// eslint-disable-next-line strict
	_null = null;
	return _null;
}

var utils$5 = utils$9;
var normalizeHeaderName = normalizeHeaderName$1;
var AxiosError$1 = requireAxiosError();
var transitionalDefaults = transitional;
var toFormData = requireToFormData();

var DEFAULT_CONTENT_TYPE = {
  'Content-Type': 'application/x-www-form-urlencoded'
};

function setContentTypeIfUnset(headers, value) {
  if (!utils$5.isUndefined(headers) && utils$5.isUndefined(headers['Content-Type'])) {
    headers['Content-Type'] = value;
  }
}

function getDefaultAdapter() {
  var adapter;
  if (typeof XMLHttpRequest !== 'undefined') {
    // For browsers use XHR adapter
    adapter = requireXhr();
  } else if (typeof process !== 'undefined' && Object.prototype.toString.call(process) === '[object process]') {
    // For node use HTTP adapter
    adapter = requireXhr();
  }
  return adapter;
}

function stringifySafely(rawValue, parser, encoder) {
  if (utils$5.isString(rawValue)) {
    try {
      (parser || JSON.parse)(rawValue);
      return utils$5.trim(rawValue);
    } catch (e) {
      if (e.name !== 'SyntaxError') {
        throw e;
      }
    }
  }

  return (encoder || JSON.stringify)(rawValue);
}

var defaults$3 = {

  transitional: transitionalDefaults,

  adapter: getDefaultAdapter(),

  transformRequest: [function transformRequest(data, headers) {
    normalizeHeaderName(headers, 'Accept');
    normalizeHeaderName(headers, 'Content-Type');

    if (utils$5.isFormData(data) ||
      utils$5.isArrayBuffer(data) ||
      utils$5.isBuffer(data) ||
      utils$5.isStream(data) ||
      utils$5.isFile(data) ||
      utils$5.isBlob(data)
    ) {
      return data;
    }
    if (utils$5.isArrayBufferView(data)) {
      return data.buffer;
    }
    if (utils$5.isURLSearchParams(data)) {
      setContentTypeIfUnset(headers, 'application/x-www-form-urlencoded;charset=utf-8');
      return data.toString();
    }

    var isObjectPayload = utils$5.isObject(data);
    var contentType = headers && headers['Content-Type'];

    var isFileList;

    if ((isFileList = utils$5.isFileList(data)) || (isObjectPayload && contentType === 'multipart/form-data')) {
      var _FormData = this.env && this.env.FormData;
      return toFormData(isFileList ? {'files[]': data} : data, _FormData && new _FormData());
    } else if (isObjectPayload || contentType === 'application/json') {
      setContentTypeIfUnset(headers, 'application/json');
      return stringifySafely(data);
    }

    return data;
  }],

  transformResponse: [function transformResponse(data) {
    var transitional = this.transitional || defaults$3.transitional;
    var silentJSONParsing = transitional && transitional.silentJSONParsing;
    var forcedJSONParsing = transitional && transitional.forcedJSONParsing;
    var strictJSONParsing = !silentJSONParsing && this.responseType === 'json';

    if (strictJSONParsing || (forcedJSONParsing && utils$5.isString(data) && data.length)) {
      try {
        return JSON.parse(data);
      } catch (e) {
        if (strictJSONParsing) {
          if (e.name === 'SyntaxError') {
            throw AxiosError$1.from(e, AxiosError$1.ERR_BAD_RESPONSE, this, null, this.response);
          }
          throw e;
        }
      }
    }

    return data;
  }],

  /**
   * A timeout in milliseconds to abort a request. If set to 0 (default) a
   * timeout is not created.
   */
  timeout: 0,

  xsrfCookieName: 'XSRF-TOKEN',
  xsrfHeaderName: 'X-XSRF-TOKEN',

  maxContentLength: -1,
  maxBodyLength: -1,

  env: {
    FormData: require_null()
  },

  validateStatus: function validateStatus(status) {
    return status >= 200 && status < 300;
  },

  headers: {
    common: {
      'Accept': 'application/json, text/plain, */*'
    }
  }
};

utils$5.forEach(['delete', 'get', 'head'], function forEachMethodNoData(method) {
  defaults$3.headers[method] = {};
});

utils$5.forEach(['post', 'put', 'patch'], function forEachMethodWithData(method) {
  defaults$3.headers[method] = utils$5.merge(DEFAULT_CONTENT_TYPE);
});

var defaults_1 = defaults$3;

var utils$4 = utils$9;
var defaults$2 = defaults_1;

/**
 * Transform the data for a request or a response
 *
 * @param {Object|String} data The data to be transformed
 * @param {Array} headers The headers for the request or response
 * @param {Array|Function} fns A single function or Array of functions
 * @returns {*} The resulting transformed data
 */
var transformData$1 = function transformData(data, headers, fns) {
  var context = this || defaults$2;
  /*eslint no-param-reassign:0*/
  utils$4.forEach(fns, function transform(fn) {
    data = fn.call(context, data, headers);
  });

  return data;
};

var isCancel$1;
var hasRequiredIsCancel;

function requireIsCancel () {
	if (hasRequiredIsCancel) return isCancel$1;
	hasRequiredIsCancel = 1;

	isCancel$1 = function isCancel(value) {
	  return !!(value && value.__CANCEL__);
	};
	return isCancel$1;
}

var utils$3 = utils$9;
var transformData = transformData$1;
var isCancel = requireIsCancel();
var defaults$1 = defaults_1;
var CanceledError = requireCanceledError();

/**
 * Throws a `CanceledError` if cancellation has been requested.
 */
function throwIfCancellationRequested(config) {
  if (config.cancelToken) {
    config.cancelToken.throwIfRequested();
  }

  if (config.signal && config.signal.aborted) {
    throw new CanceledError();
  }
}

/**
 * Dispatch a request to the server using the configured adapter.
 *
 * @param {object} config The config that is to be used for the request
 * @returns {Promise} The Promise to be fulfilled
 */
var dispatchRequest$1 = function dispatchRequest(config) {
  throwIfCancellationRequested(config);

  // Ensure headers exist
  config.headers = config.headers || {};

  // Transform request data
  config.data = transformData.call(
    config,
    config.data,
    config.headers,
    config.transformRequest
  );

  // Flatten headers
  config.headers = utils$3.merge(
    config.headers.common || {},
    config.headers[config.method] || {},
    config.headers
  );

  utils$3.forEach(
    ['delete', 'get', 'head', 'post', 'put', 'patch', 'common'],
    function cleanHeaderConfig(method) {
      delete config.headers[method];
    }
  );

  var adapter = config.adapter || defaults$1.adapter;

  return adapter(config).then(function onAdapterResolution(response) {
    throwIfCancellationRequested(config);

    // Transform response data
    response.data = transformData.call(
      config,
      response.data,
      response.headers,
      config.transformResponse
    );

    return response;
  }, function onAdapterRejection(reason) {
    if (!isCancel(reason)) {
      throwIfCancellationRequested(config);

      // Transform response data
      if (reason && reason.response) {
        reason.response.data = transformData.call(
          config,
          reason.response.data,
          reason.response.headers,
          config.transformResponse
        );
      }
    }

    return Promise.reject(reason);
  });
};

var utils$2 = utils$9;

/**
 * Config-specific merge-function which creates a new config-object
 * by merging two configuration objects together.
 *
 * @param {Object} config1
 * @param {Object} config2
 * @returns {Object} New object resulting from merging config2 to config1
 */
var mergeConfig$2 = function mergeConfig(config1, config2) {
  // eslint-disable-next-line no-param-reassign
  config2 = config2 || {};
  var config = {};

  function getMergedValue(target, source) {
    if (utils$2.isPlainObject(target) && utils$2.isPlainObject(source)) {
      return utils$2.merge(target, source);
    } else if (utils$2.isPlainObject(source)) {
      return utils$2.merge({}, source);
    } else if (utils$2.isArray(source)) {
      return source.slice();
    }
    return source;
  }

  // eslint-disable-next-line consistent-return
  function mergeDeepProperties(prop) {
    if (!utils$2.isUndefined(config2[prop])) {
      return getMergedValue(config1[prop], config2[prop]);
    } else if (!utils$2.isUndefined(config1[prop])) {
      return getMergedValue(undefined, config1[prop]);
    }
  }

  // eslint-disable-next-line consistent-return
  function valueFromConfig2(prop) {
    if (!utils$2.isUndefined(config2[prop])) {
      return getMergedValue(undefined, config2[prop]);
    }
  }

  // eslint-disable-next-line consistent-return
  function defaultToConfig2(prop) {
    if (!utils$2.isUndefined(config2[prop])) {
      return getMergedValue(undefined, config2[prop]);
    } else if (!utils$2.isUndefined(config1[prop])) {
      return getMergedValue(undefined, config1[prop]);
    }
  }

  // eslint-disable-next-line consistent-return
  function mergeDirectKeys(prop) {
    if (prop in config2) {
      return getMergedValue(config1[prop], config2[prop]);
    } else if (prop in config1) {
      return getMergedValue(undefined, config1[prop]);
    }
  }

  var mergeMap = {
    'url': valueFromConfig2,
    'method': valueFromConfig2,
    'data': valueFromConfig2,
    'baseURL': defaultToConfig2,
    'transformRequest': defaultToConfig2,
    'transformResponse': defaultToConfig2,
    'paramsSerializer': defaultToConfig2,
    'timeout': defaultToConfig2,
    'timeoutMessage': defaultToConfig2,
    'withCredentials': defaultToConfig2,
    'adapter': defaultToConfig2,
    'responseType': defaultToConfig2,
    'xsrfCookieName': defaultToConfig2,
    'xsrfHeaderName': defaultToConfig2,
    'onUploadProgress': defaultToConfig2,
    'onDownloadProgress': defaultToConfig2,
    'decompress': defaultToConfig2,
    'maxContentLength': defaultToConfig2,
    'maxBodyLength': defaultToConfig2,
    'beforeRedirect': defaultToConfig2,
    'transport': defaultToConfig2,
    'httpAgent': defaultToConfig2,
    'httpsAgent': defaultToConfig2,
    'cancelToken': defaultToConfig2,
    'socketPath': defaultToConfig2,
    'responseEncoding': defaultToConfig2,
    'validateStatus': mergeDirectKeys
  };

  utils$2.forEach(Object.keys(config1).concat(Object.keys(config2)), function computeConfigValue(prop) {
    var merge = mergeMap[prop] || mergeDeepProperties;
    var configValue = merge(prop);
    (utils$2.isUndefined(configValue) && merge !== mergeDirectKeys) || (config[prop] = configValue);
  });

  return config;
};

var data;
var hasRequiredData;

function requireData () {
	if (hasRequiredData) return data;
	hasRequiredData = 1;
	data = {
	  "version": "0.27.2"
	};
	return data;
}

var VERSION = requireData().version;
var AxiosError = requireAxiosError();

var validators$1 = {};

// eslint-disable-next-line func-names
['object', 'boolean', 'number', 'function', 'string', 'symbol'].forEach(function(type, i) {
  validators$1[type] = function validator(thing) {
    return typeof thing === type || 'a' + (i < 1 ? 'n ' : ' ') + type;
  };
});

var deprecatedWarnings = {};

/**
 * Transitional option validator
 * @param {function|boolean?} validator - set to false if the transitional option has been removed
 * @param {string?} version - deprecated version / removed since version
 * @param {string?} message - some message with additional info
 * @returns {function}
 */
validators$1.transitional = function transitional(validator, version, message) {
  function formatMessage(opt, desc) {
    return '[Axios v' + VERSION + '] Transitional option \'' + opt + '\'' + desc + (message ? '. ' + message : '');
  }

  // eslint-disable-next-line func-names
  return function(value, opt, opts) {
    if (validator === false) {
      throw new AxiosError(
        formatMessage(opt, ' has been removed' + (version ? ' in ' + version : '')),
        AxiosError.ERR_DEPRECATED
      );
    }

    if (version && !deprecatedWarnings[opt]) {
      deprecatedWarnings[opt] = true;
      // eslint-disable-next-line no-console
      console.warn(
        formatMessage(
          opt,
          ' has been deprecated since v' + version + ' and will be removed in the near future'
        )
      );
    }

    return validator ? validator(value, opt, opts) : true;
  };
};

/**
 * Assert object's properties type
 * @param {object} options
 * @param {object} schema
 * @param {boolean?} allowUnknown
 */

function assertOptions(options, schema, allowUnknown) {
  if (typeof options !== 'object') {
    throw new AxiosError('options must be an object', AxiosError.ERR_BAD_OPTION_VALUE);
  }
  var keys = Object.keys(options);
  var i = keys.length;
  while (i-- > 0) {
    var opt = keys[i];
    var validator = schema[opt];
    if (validator) {
      var value = options[opt];
      var result = value === undefined || validator(value, opt, options);
      if (result !== true) {
        throw new AxiosError('option ' + opt + ' must be ' + result, AxiosError.ERR_BAD_OPTION_VALUE);
      }
      continue;
    }
    if (allowUnknown !== true) {
      throw new AxiosError('Unknown option ' + opt, AxiosError.ERR_BAD_OPTION);
    }
  }
}

var validator$1 = {
  assertOptions: assertOptions,
  validators: validators$1
};

var utils$1 = utils$9;
var buildURL = buildURL$1;
var InterceptorManager = InterceptorManager_1;
var dispatchRequest = dispatchRequest$1;
var mergeConfig$1 = mergeConfig$2;
var buildFullPath = buildFullPath$1;
var validator = validator$1;

var validators = validator.validators;
/**
 * Create a new instance of Axios
 *
 * @param {Object} instanceConfig The default config for the instance
 */
function Axios$1(instanceConfig) {
  this.defaults = instanceConfig;
  this.interceptors = {
    request: new InterceptorManager(),
    response: new InterceptorManager()
  };
}

/**
 * Dispatch a request
 *
 * @param {Object} config The config specific for this request (merged with this.defaults)
 */
Axios$1.prototype.request = function request(configOrUrl, config) {
  /*eslint no-param-reassign:0*/
  // Allow for axios('example/url'[, config]) a la fetch API
  if (typeof configOrUrl === 'string') {
    config = config || {};
    config.url = configOrUrl;
  } else {
    config = configOrUrl || {};
  }

  config = mergeConfig$1(this.defaults, config);

  // Set config.method
  if (config.method) {
    config.method = config.method.toLowerCase();
  } else if (this.defaults.method) {
    config.method = this.defaults.method.toLowerCase();
  } else {
    config.method = 'get';
  }

  var transitional = config.transitional;

  if (transitional !== undefined) {
    validator.assertOptions(transitional, {
      silentJSONParsing: validators.transitional(validators.boolean),
      forcedJSONParsing: validators.transitional(validators.boolean),
      clarifyTimeoutError: validators.transitional(validators.boolean)
    }, false);
  }

  // filter out skipped interceptors
  var requestInterceptorChain = [];
  var synchronousRequestInterceptors = true;
  this.interceptors.request.forEach(function unshiftRequestInterceptors(interceptor) {
    if (typeof interceptor.runWhen === 'function' && interceptor.runWhen(config) === false) {
      return;
    }

    synchronousRequestInterceptors = synchronousRequestInterceptors && interceptor.synchronous;

    requestInterceptorChain.unshift(interceptor.fulfilled, interceptor.rejected);
  });

  var responseInterceptorChain = [];
  this.interceptors.response.forEach(function pushResponseInterceptors(interceptor) {
    responseInterceptorChain.push(interceptor.fulfilled, interceptor.rejected);
  });

  var promise;

  if (!synchronousRequestInterceptors) {
    var chain = [dispatchRequest, undefined];

    Array.prototype.unshift.apply(chain, requestInterceptorChain);
    chain = chain.concat(responseInterceptorChain);

    promise = Promise.resolve(config);
    while (chain.length) {
      promise = promise.then(chain.shift(), chain.shift());
    }

    return promise;
  }


  var newConfig = config;
  while (requestInterceptorChain.length) {
    var onFulfilled = requestInterceptorChain.shift();
    var onRejected = requestInterceptorChain.shift();
    try {
      newConfig = onFulfilled(newConfig);
    } catch (error) {
      onRejected(error);
      break;
    }
  }

  try {
    promise = dispatchRequest(newConfig);
  } catch (error) {
    return Promise.reject(error);
  }

  while (responseInterceptorChain.length) {
    promise = promise.then(responseInterceptorChain.shift(), responseInterceptorChain.shift());
  }

  return promise;
};

Axios$1.prototype.getUri = function getUri(config) {
  config = mergeConfig$1(this.defaults, config);
  var fullPath = buildFullPath(config.baseURL, config.url);
  return buildURL(fullPath, config.params, config.paramsSerializer);
};

// Provide aliases for supported request methods
utils$1.forEach(['delete', 'get', 'head', 'options'], function forEachMethodNoData(method) {
  /*eslint func-names:0*/
  Axios$1.prototype[method] = function(url, config) {
    return this.request(mergeConfig$1(config || {}, {
      method: method,
      url: url,
      data: (config || {}).data
    }));
  };
});

utils$1.forEach(['post', 'put', 'patch'], function forEachMethodWithData(method) {
  /*eslint func-names:0*/

  function generateHTTPMethod(isForm) {
    return function httpMethod(url, data, config) {
      return this.request(mergeConfig$1(config || {}, {
        method: method,
        headers: isForm ? {
          'Content-Type': 'multipart/form-data'
        } : {},
        url: url,
        data: data
      }));
    };
  }

  Axios$1.prototype[method] = generateHTTPMethod();

  Axios$1.prototype[method + 'Form'] = generateHTTPMethod(true);
});

var Axios_1 = Axios$1;

var CancelToken_1;
var hasRequiredCancelToken;

function requireCancelToken () {
	if (hasRequiredCancelToken) return CancelToken_1;
	hasRequiredCancelToken = 1;

	var CanceledError = requireCanceledError();

	/**
	 * A `CancelToken` is an object that can be used to request cancellation of an operation.
	 *
	 * @class
	 * @param {Function} executor The executor function.
	 */
	function CancelToken(executor) {
	  if (typeof executor !== 'function') {
	    throw new TypeError('executor must be a function.');
	  }

	  var resolvePromise;

	  this.promise = new Promise(function promiseExecutor(resolve) {
	    resolvePromise = resolve;
	  });

	  var token = this;

	  // eslint-disable-next-line func-names
	  this.promise.then(function(cancel) {
	    if (!token._listeners) return;

	    var i;
	    var l = token._listeners.length;

	    for (i = 0; i < l; i++) {
	      token._listeners[i](cancel);
	    }
	    token._listeners = null;
	  });

	  // eslint-disable-next-line func-names
	  this.promise.then = function(onfulfilled) {
	    var _resolve;
	    // eslint-disable-next-line func-names
	    var promise = new Promise(function(resolve) {
	      token.subscribe(resolve);
	      _resolve = resolve;
	    }).then(onfulfilled);

	    promise.cancel = function reject() {
	      token.unsubscribe(_resolve);
	    };

	    return promise;
	  };

	  executor(function cancel(message) {
	    if (token.reason) {
	      // Cancellation has already been requested
	      return;
	    }

	    token.reason = new CanceledError(message);
	    resolvePromise(token.reason);
	  });
	}

	/**
	 * Throws a `CanceledError` if cancellation has been requested.
	 */
	CancelToken.prototype.throwIfRequested = function throwIfRequested() {
	  if (this.reason) {
	    throw this.reason;
	  }
	};

	/**
	 * Subscribe to the cancel signal
	 */

	CancelToken.prototype.subscribe = function subscribe(listener) {
	  if (this.reason) {
	    listener(this.reason);
	    return;
	  }

	  if (this._listeners) {
	    this._listeners.push(listener);
	  } else {
	    this._listeners = [listener];
	  }
	};

	/**
	 * Unsubscribe from the cancel signal
	 */

	CancelToken.prototype.unsubscribe = function unsubscribe(listener) {
	  if (!this._listeners) {
	    return;
	  }
	  var index = this._listeners.indexOf(listener);
	  if (index !== -1) {
	    this._listeners.splice(index, 1);
	  }
	};

	/**
	 * Returns an object that contains a new `CancelToken` and a function that, when called,
	 * cancels the `CancelToken`.
	 */
	CancelToken.source = function source() {
	  var cancel;
	  var token = new CancelToken(function executor(c) {
	    cancel = c;
	  });
	  return {
	    token: token,
	    cancel: cancel
	  };
	};

	CancelToken_1 = CancelToken;
	return CancelToken_1;
}

var spread;
var hasRequiredSpread;

function requireSpread () {
	if (hasRequiredSpread) return spread;
	hasRequiredSpread = 1;

	/**
	 * Syntactic sugar for invoking a function and expanding an array for arguments.
	 *
	 * Common use case would be to use `Function.prototype.apply`.
	 *
	 *  ```js
	 *  function f(x, y, z) {}
	 *  var args = [1, 2, 3];
	 *  f.apply(null, args);
	 *  ```
	 *
	 * With `spread` this example can be re-written.
	 *
	 *  ```js
	 *  spread(function(x, y, z) {})([1, 2, 3]);
	 *  ```
	 *
	 * @param {Function} callback
	 * @returns {Function}
	 */
	spread = function spread(callback) {
	  return function wrap(arr) {
	    return callback.apply(null, arr);
	  };
	};
	return spread;
}

var isAxiosError;
var hasRequiredIsAxiosError;

function requireIsAxiosError () {
	if (hasRequiredIsAxiosError) return isAxiosError;
	hasRequiredIsAxiosError = 1;

	var utils = utils$9;

	/**
	 * Determines whether the payload is an error thrown by Axios
	 *
	 * @param {*} payload The value to test
	 * @returns {boolean} True if the payload is an error thrown by Axios, otherwise false
	 */
	isAxiosError = function isAxiosError(payload) {
	  return utils.isObject(payload) && (payload.isAxiosError === true);
	};
	return isAxiosError;
}

var utils = utils$9;
var bind = bind$2;
var Axios = Axios_1;
var mergeConfig = mergeConfig$2;
var defaults = defaults_1;

/**
 * Create an instance of Axios
 *
 * @param {Object} defaultConfig The default config for the instance
 * @return {Axios} A new instance of Axios
 */
function createInstance(defaultConfig) {
  var context = new Axios(defaultConfig);
  var instance = bind(Axios.prototype.request, context);

  // Copy axios.prototype to instance
  utils.extend(instance, Axios.prototype, context);

  // Copy context to instance
  utils.extend(instance, context);

  // Factory for creating new instances
  instance.create = function create(instanceConfig) {
    return createInstance(mergeConfig(defaultConfig, instanceConfig));
  };

  return instance;
}

// Create the default instance to be exported
var axios$1 = createInstance(defaults);

// Expose Axios class to allow class inheritance
axios$1.Axios = Axios;

// Expose Cancel & CancelToken
axios$1.CanceledError = requireCanceledError();
axios$1.CancelToken = requireCancelToken();
axios$1.isCancel = requireIsCancel();
axios$1.VERSION = requireData().version;
axios$1.toFormData = requireToFormData();

// Expose AxiosError class
axios$1.AxiosError = requireAxiosError();

// alias for CanceledError for backward compatibility
axios$1.Cancel = axios$1.CanceledError;

// Expose all/spread
axios$1.all = function all(promises) {
  return Promise.all(promises);
};
axios$1.spread = requireSpread();

// Expose isAxiosError
axios$1.isAxiosError = requireIsAxiosError();

axios$2.exports = axios$1;

// Allow use of default import syntax in TypeScript
axios$2.exports.default = axios$1;

(function (module) {
	module.exports = axios$2.exports;
} (axios$3));

var axios = /*@__PURE__*/getDefaultExportFromCjs(axios$3.exports);

/**
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-07-08.
 * Copyright © Softgames 2019 - present
 */

const BOT_SERVICE_PROVIDER_MAPPING = {
  [PROVIDER.FACEBOOK_INSTANT_GAME]: 'fb-instant',
};

const BOT_SERVICE_SUPPORTED_PROVIDERS = Object.keys(
  BOT_SERVICE_PROVIDER_MAPPING,
);

/**
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-07-08.
 * Copyright © Softgames 2019 - present
 */

const ATTRIBUTES_FOR_ACTION = {
  get: {
    users: true,
    deleted: true,
    _id: true,
    messageId: true,
    environment: true,
    sender: true,
    scheduledFor: true,
    gameSlug: true,
    createDate: true,
    updates: false,
    locale: true,
    __v: false,
  },
  create: {
    users: true,
    _id: true,
    messageId: true,
    sender: true,
    environment: true,
    scheduledFor: true,
    gameSlug: true,
    createDate: true,
    locale: true,
    __v: false,
  },
  delete: {},
};

function validateResponse(response, action) {
  const missingRequiredAttributes = [];
  const missingNotRequiredAttributes = [];

  Object.keys(ATTRIBUTES_FOR_ACTION[action]).forEach((item) => {
    if (!Object.keys(response).includes(item)) {
      if (ATTRIBUTES_FOR_ACTION[action][item]) {
        missingRequiredAttributes.push(item);
      } else {
        missingNotRequiredAttributes.push(item);
      }
    }
  });

  const extraAttributes = Object.keys(response).filter((item) =>
    Object.keys(ATTRIBUTES_FOR_ACTION[action]).includes(item),
  );

  if (missingRequiredAttributes.length > 0) {
    return -1;
  }
  if (missingNotRequiredAttributes.length > 0 || extraAttributes.length > 0) {
    return 1;
  }
  return 0;
}

/**
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-07-08.
 * Copyright © Softgames 2019 - present
 */

const MAPPING = {
  id: {
    key: '_id',
    defaultValue: undefined,
  },
  provider: {
    key: 'environment',
    defaultValue: undefined,
    converter: (valueToConvert) =>
      Object.keys(BOT_SERVICE_PROVIDER_MAPPING).find(
        (key) => BOT_SERVICE_PROVIDER_MAPPING[key] === valueToConvert,
      ),
  },
  playerId: {
    key: 'sender',
    defaultValue: undefined,
  },
  recipientIds: {
    key: 'users',
    defaultValue: undefined,
  },
  messageId: {
    key: 'messageId',
    defaultValue: undefined,
  },
  locale: {
    key: 'locale',
    defaultValue: undefined,
  },
  gameId: {
    key: 'gameSlug',
    defaultValue: undefined,
  },
  createDate: {
    key: 'createDate',
    defaultValue: undefined,
  },
  scheduledFor: {
    key: 'scheduledFor',
    defaultValue: undefined,
  },
  deleted: {
    key: 'deleted',
    defaultValue: false,
  },
};

function convert(input) {
  return Object.keys(MAPPING).reduce((obj, key) => {
    const item = MAPPING[key];
    return {
      ...obj,
      [key]: fetchValue({ item, input }),
    };
  }, {});
}

function fetchValue({ item: { key, defaultValue, converter }, input }) {
  if (!Object.keys(input).includes(key)) {
    return defaultValue;
  }
  return converter ? converter(input[key]) : input[key];
}

function getBotMessageResponseError(
  error,
  requestOptions,
  specialResponseHandler,
) {
  const { message: errorMessage, response, request } = error;
  if (response) {
    const { status, data } = response;
    const { error: errorResponse, message: errorMessageResponse } = data || {};
    const message =
      errorResponse ||
      errorMessageResponse ||
      'No error message provided from bot service';
    if (isFunction$1(specialResponseHandler)) {
      const specialError = specialResponseHandler({ status, message, data });
      if (specialError) {
        return specialError;
      }
    }
    return new GBCBotServiceError(`Bot service response with: ${message}`);
  }
  if (request) {
    return new GBCBotServiceError(
      `No bot service server contact with request options:"\n${JSON.stringify(
        requestOptions,
        null,
        '  ',
      )}`,
    );
  }
  return new GBCBotServiceError(errorMessage);
}

/**
 * @async
 * @function
 *
 * @description
 * Use this function to create scheduled bot message. Before creating scheduled messages we have to configure message in Bot Service.
 * Use _id from response if you would like to delete scheduled message.
 *
 * @example
 * import {createScheduledBotMessage, PROVIDER} from '@sgorg/game-backend-client';
 * async function createScheduledMessage() {
 *   const playerInfo = await window.FBInstant.player.getSignedPlayerInfoAsync();
 *   const newScheduledMessage = await createScheduledBotMessage({
 *     playerId: playerInfo.getPlayerID(),
 *     provider: PROVIDER.FACEBOOK_INSTANT_GAME,
 *     inHours: 24,
 *     recipientIds: ["fb-friend-player-id-1", "fb-friend-player-id-2"],
 *     messageId: "test",
 *     messageData: {someCustomMessageData: "Hello"},
 *     locale: "en",
 *   });
 *   console.log(newScheduledMessage);
 *   // {
 *   //    id: "5b20cd9d2a54eb9aa962b650",
 *   //    provider: "fbig",
 *   //    playerId: "1409417839185403",
 *   //    recipientIds: ["1409417839185403", "6709417839186703"],
 *   //    messageId: "test",
 *   //    messageData: {someCustomMessageData: "Hello"},
 *   //    gameId: "fish-story",
 *   //    locale: "en",
 *   //    createDate: "2018-06-13T07:54:05.375Z",
 *   //    scheduledFor: "2018-06-13T08:54:05.367Z",
 *   //    deleted: false,
 *   // }
 *   return newScheduledMessage;
 * }
 *
 * @param {Object} options
 * @param {String} options.playerId Facebook player id
 * @param {PROVIDER} options.provider Valid values: fbig, ok, line
 * @param {Number} [options.inHours] Number of hours until the message will be sent
 * @param {Number} [options.inMinutes] Number of minutes until the message will be sent
 * @param {Number} [options.inSeconds] Number of seconds until the message will be sent
 * @param {Array} options.recipientIds An array of Facebook ids that should receive the message
 * @param {String} options.messageId The messageId property of the message that should be sent. messageId should be taken from Bot Service.
 * @param {Object} options.messageData The message data to replace massage placeholders. For message data structure refer to Bot Message API {@link https://confluence.softgames.de/display/DEV/Scheduled+Messages+API#ScheduledMessagesAPI-msgDataObj}
 * @param {String} options.locale A two character lowercase locale string
 * @returns {Promise<BotMessage>}
 * @memberof module:bots
 *
 * @throws {GBCError}
 * @throws {GBCErrorValidationFailed}
 * @throws {GBCBotServiceError}
 */

const createScheduledBotMessage$1 = async ({
  playerId,
  provider,
  inHours = 0,
  inMinutes = 0,
  inSeconds = 0,
  recipientIds,
  messageId,
  messageData,
  locale,
}) => {
  if (!isString$3(playerId) || !playerId.trim()) {
    throw new GBCErrorValidationFailed(
      'Invalid "playerId" parameter. "playerId" should be not empty string',
      '3751273517',
    );
  }

  if (!Object.values(BOT_SERVICE_SUPPORTED_PROVIDERS).includes(provider)) {
    throw new GBCErrorValidationFailed(
      `Invalid "provider" parameter. "provider" should be one from ${BOT_SERVICE_SUPPORTED_PROVIDERS}. Check more for PLATFORM.`,
      'fb-instant',
    );
  }

  if (!isInteger_1(inHours) || inHours < 0) {
    throw new GBCErrorValidationFailed(
      `Invalid "inHours" parameter. "inHours" should be a positive integer or 0 or undefined.`,
      1,
    );
  }

  if (!isInteger_1(inMinutes) || inMinutes < 0) {
    throw new GBCErrorValidationFailed(
      `Invalid "inMinutes" parameter. "inMinutes" should be a positive integer or 0 or undefined.`,
      2,
    );
  }

  if (!isInteger_1(inSeconds) || inSeconds < 0) {
    throw new GBCErrorValidationFailed(
      `Invalid "inSeconds" parameter. "inSeconds" should be a positive integer or 0 or undefined.`,
      3,
    );
  }

  if (!inHours && !inMinutes && !inSeconds) {
    throw new GBCErrorValidationFailed(
      `You have to provide a valid value for at least on of "inHours", "inMinutes", "inSeconds".`,
      3,
    );
  }

  if (!isArray$1(recipientIds)) {
    throw new GBCErrorValidationFailed(
      'Invalid "recipientIds" parameter. "recipientIds" should be array of player ids',
      ['1716312612', '2782168124', '7128367812'],
    );
  }

  if (!isString$3(messageId) || !messageId.trim()) {
    throw new GBCErrorValidationFailed(
      'Invalid "messageId" parameter. "messageId" should be not empty string',
      '3751273517',
    );
  }

  if (
    !isString$3(locale) ||
    !locale.trim() ||
    locale.length !== 2 ||
    locale !== locale.toLowerCase()
  ) {
    throw new GBCErrorValidationFailed(
      'Invalid "locale" parameter. "locale" should be a two character lowercase locale string',
      'en',
    );
  }

  if (messageData && !isPlainObject_1(messageData)) {
    throw new GBCErrorValidationFailed(
      'Invalid messageData parameter. "messageData" should be plain object',
      { customMessageData: 'Hello' },
    );
  }
  let requestOptions = null;
  try {
    requestOptions = {
      method: 'post',
      url: getBotServiceCreateScheduledMessageUrl({
        environment: BOT_SERVICE_PROVIDER_MAPPING[provider],
      }),
      auth: getBotServiceAuth(),
      data: {
        inHours,
        inMinutes,
        inSeconds,
        sender: playerId,
        userIds: recipientIds,
        messageId,
        messageData,
        locale,
      },
      responseType: 'json',
      responseEncoding: 'utf8',
      timeout: CREATE_SCHEDULED_BOT_MESSAGE_REQUEST_TIMEOUT,
    };

    const response = await axios(requestOptions);
    const { data: res } = response || {};
    if (validateResponse(res, 'create') < 0) {
      throw new GBCBotServiceError('Invalid response from bot service.');
    }
    return convert(res);
  } catch (error) {
    throw getBotMessageResponseError(
      error,
      requestOptions,
      ({ status, message }) => {
        if (
          status === 500 &&
          message.includes('Found no fb message with messageId')
        ) {
          throw new GBCError(
            ERROR_CODE.NOT_FOUND,
            `Facebook messageId ${messageId} not found. Check bot service configuration.`,
          );
        }
        return null;
      },
    );
  }
};

/**
 * @async
 * @function
 *
 * @description
 * Use this function to delete scheduled bot message. ID of the message to delete you can find in response from function getScheduledBotMessages.
 * When you try to delete not existing message function will throw error GBCError with status code ERROR_CODE.NOT_FOUND.
 *
 * @example
 * import {deleteScheduledBotMessage} from '@sgorg/game-backend-client';
 * async function deleteScheduledMessage() {
 *   await deleteScheduledBotMessage({scheduledMessageId: "1637815317311131"});
 *   console.log("Scheduled bot message has been deleted");
 * }
 *
 * @param {Object} options
 * @param {String} options.scheduledMessageId
 * @returns {Promise}
 * @memberof module:bots
 *
 * @throws {GBCError}
 * @throws {GBCErrorValidationFailed}
 * @throws {GBCBotServiceError}
 */

const deleteScheduledBotMessage$1 = async ({ scheduledMessageId }) => {
  if (!isString$3(scheduledMessageId) || !scheduledMessageId.trim()) {
    throw new GBCErrorValidationFailed(
      'Invalid "scheduledMessageId" parameter. "scheduledMessageId" should be not empty string',
      '3751273517',
    );
  }

  let requestOptions = null;
  try {
    requestOptions = {
      method: 'delete',
      url: getBotServiceDeleteScheduledMessageUrl({ scheduledMessageId }),
      auth: getBotServiceAuth(),
      responseType: 'json',
      responseEncoding: 'utf8',
      timeout: DELETE_SCHEDULED_BOT_MESSAGE_REQUEST_TIMEOUT,
    };

    await axios(requestOptions);
  } catch (error) {
    throw getBotMessageResponseError(error, requestOptions, ({ status }) => {
      if (status === 404) {
        throw new GBCError(
          ERROR_CODE.NOT_FOUND,
          `Scheduled message id ${scheduledMessageId} has not been found`,
        );
      }
      return null;
    });
  }
};

/**
 * @async
 * @function
 *
 * @description
 * Fetches all scheduled bot messages for current user from bot service. Use _id from response if you would like to delete scheduled message.
 *
 * @example
 * import {getScheduledBotMessages} from '@sgorg/game-backend-client';
 *
 * const playerInfo = await window.FBInstant.player.getSignedPlayerInfoAsync();
 * async function fetcScheduledBotMessages() {
 *   const messages = await getScheduledBotMessages({playerId: playerInfo.getID()});
 *   console.log(messages);
 *   // [{
 *   //    id: "5b20cd9d2a54eb9aa962b650",
 *   //    provider: "fbig",
 *   //    playerId: "1409417839185403",
 *   //    recipientIds: ["1409417839185403", "6709417839186703"],
 *   //    messageId: "test",
 *   //    messageData: {someCustomMessageData: "Hello"},
 *   //    gameId: "fish-story",
 *   //    locale: "en",
 *   //    createDate: "2018-06-13T07:54:05.375Z",
 *   //    scheduledFor: "2018-06-13T08:54:05.367Z",
 *   //    deleted: false,
 *   // }]
 *   return messages;
 * }
 *
 * @returns {Promise<Array<BotMessage>>}
 * @memberof module:bots
 *
 * @throws {GBCErrorValidationFailed}
 * @throws {GBCBotServiceError}
 */

const getScheduledBotMessages$1 = async ({ playerId }) => {
  if (!isString$3(playerId) || !playerId.trim()) {
    throw new GBCErrorValidationFailed(
      'Invalid "playerId" parameter. "playerId" should be not empty string',
      '3751273517',
    );
  }
  let requestOptions = null;
  try {
    requestOptions = {
      method: 'get',
      url: getBotServiceGetScheduledMessageUrl({ playerId }),
      auth: getBotServiceAuth(),
      responseType: 'json',
      responseEncoding: 'utf8',
      timeout: GET_SCHEDULED_BOT_MESSAGE_REQUEST_TIMEOUT,
    };

    const response = await axios(requestOptions);

    const { data: res = [] } = response || [];

    return res.map((item) => {
      if (validateResponse(item, 'get') < 0) {
        throw new GBCBotServiceError('Invalid response from bot service.', res);
      }
      return convert(item);
    });
  } catch (error) {
    throw getBotMessageResponseError(error, requestOptions);
  }
};

/**
 * @async
 * @function
 *
 * @description
 * Use this function to track conversion for bot messages. Parameter messageId should be taken from entry point data.
 *
 * @example
 * import {trackBotMessageConversion} from '@sgorg/game-backend-client';
 * async function trackBotConversion() {
 *   try{
 *     const entryPointData = FBInstant.getEntryPointData();
 *     await trackBotMessageConversion({messageId: entryPointData.messageId});
 *   } catch(error) {
 *     console.log('Error message', error.message);
 *     console.log('Error code', error.getCode());
 *   }
 * }
 *
 * @param {Object} options
 * @param {String} options.messageId bot message id from entry point data 'messageId'
 * @returns {Promise}
 * @memberof module:bots
 *
 * @throws {GBCBotServiceError}
 * @throws {GBCErrorValidationFailed}
 */

const trackBotMessageConversion$1 = async ({ messageId } = {}) => {
  if (!isString$3(messageId) || !messageId.trim()) {
    throw new GBCErrorValidationFailed(
      'Invalid "messageId" parameter. "messageId" should be not empty string',
      '3751273517',
    );
  }

  let requestOptions = null;
  try {
    requestOptions = {
      method: 'post',
      url: getBotServiceConversionUrl(),
      data: {
        messageId,
      },
      auth: getBotServiceAuth(),
      responseType: 'json',
      responseEncoding: 'utf8',
      timeout: TRACK_BOT_MESSAGE_CONVERSION_REQUEST_TIMEOUT,
    };

    const response = await axios(requestOptions);
    // ToDo: [RS] Check what the response is and add it to the docu or not return it
    // ToDo: [RS] Better yet move it to the GB-Game-Server and make a clean graphQL request here
    return response.data;
  } catch (error) {
    throw getBotMessageResponseError(error, requestOptions);
  }
};

/**
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-06-14.
 * Copyright © Softgames 2019 - present
 */

const createScheduledBotMessage = wrapMethod(
  'createScheduledBotMessage',
  createScheduledBotMessage$1,
);
const deleteScheduledBotMessage = wrapMethod(
  'deleteScheduledBotMessage',
  deleteScheduledBotMessage$1,
);
const getScheduledBotMessages = wrapMethod(
  'getScheduledBotMessages',
  getScheduledBotMessages$1,
);
const trackBotMessageConversion = wrapMethod(
  'trackBotMessageConversion',
  trackBotMessageConversion$1,
);

var bots = /*#__PURE__*/Object.freeze({
  __proto__: null,
  createScheduledBotMessage: createScheduledBotMessage,
  deleteScheduledBotMessage: deleteScheduledBotMessage,
  getScheduledBotMessages: getScheduledBotMessages,
  trackBotMessageConversion: trackBotMessageConversion
});

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-01.
 * Created by Daniel Pochert <daniel.pochert@softgames.de> on 2019-04-01.
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-04-01.
 * Created by Benhur Davies <benhur.davies@softgames.de> on 2019-04-01.
 * Copyright © Softgames 2019 - present
 */

var index = {
  ...base,
  ...economy,
  ...enums,
  ...errors,
  ...events,
  ...leaderboard,
  ...messaging,
  ...notification,
  ...purchase,
  ...bots,
  ...user,
};

export { ENVIRONMENT, ERROR_CODE, GBCBadResponseError, GBCBotServiceError, GBCClientTimeoutError, GBCConfigurationError, GBCDbTimeoutError, GBCEnvironmentError, GBCError, GBCErrorNoConnection, GBCErrorValidationFailed, GBCIdError, GBCInternalError, GBCInvalidCallError, GBCKnownGraphQLError, GBCNotAuthorizedError, GBCPaymentError, GBCRequestTimeoutError, GBCUnknownError, GBCUnknownGraphQLError, GBCWebsocketError, GBCWrongCredentialsError, OS, PROVIDER, SEVERITY$3 as SEVERITY, acknowledgeAllMessages, acknowledgeMessage, acknowledgeNotifications, addCurrencies, addItems, close, closeEvent, createScheduledBotMessage, index as default, deleteScheduledBotMessage, errorEvent, executeRuntimeScript, executeTest, fetchMessages, fetchNotifications, getCurrencyCatalog, getFriends, getFriendsScores, getGameConfig, getItemCatalog, getLeaderboardMetaData, getLeaderboardPostProcessorForUserData, getLeaderboardUserEntry, getPublicUserData, getScheduledBotMessages, getServerTimestamp, getTopScores, getUserCurrencies, getUserData, getUserDataByKeys, getUserItems, getUserProfile, getUsersEntry, getUsersScores, getVersion, isConnected, loginEvent, loginWithGameBackendCredentials, messageEvent, openPurchaseTransaction, postScore, purchaseCurrency, purchaseItem, registerPurchase, registerWithFacebookInstantGameSignature, removeLeaderboardInstanceScore, removeLeaderboardScores, removeUserDataByKeys, sendMessage, setGaTracking, setLogLevel, setUserData, setUserProfile, subscribeToLeaderboards, syncFriendListByPlatformPlayerId, trackBotMessageConversion, transaction };
